import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "@/App";
import { useSearchParams } from "react-router-dom";
import usePageTitle from "@/hooks/usePageTitle";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import {
  Upload,
  Download,
  FileSpreadsheet,
  Package,
  DollarSign,
  TrendingUp,
  BookOpen,
  Truck,
  CheckCircle,
  XCircle,
  Clock,
  History,
  AlertTriangle,
  FileText,
  Users,
  Building,
  Building2,
  UserCheck,
  RefreshCw,
  RotateCw,
  ChevronRight,
  ChevronDown,
  File as FileIcon,
  X,
  Info,
  Copy,
  Table,
  AlertCircle,
  CalendarDays,
  Edit3,
  ExternalLink,
  FileSearch,
  FileDown,
  Eye,
} from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { toast } from "sonner";
import * as XLSX from "xlsx";
import UploadSummaryDialog from "@/components/shipment-costing/UploadSummaryDialog";
import UploadAuditPanel from "@/components/shipment-costing/UploadAuditPanel";
import { formatDMY } from "@/lib/dmy";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Required columns configuration for each upload type
const REQUIRED_COLUMNS = {
  order_sheet: {
    required: ["tracking", "product"],
    optional: ["weight", "customer", "country", "service", "date", "qty"],
    aliases: {
      tracking: ["tracking", "tracking no", "tracking number", "track no", "awb"],
      product: ["product", "product name", "item", "medicine", "med"],
      weight: ["weight", "wt", "kg", "weight (kg)"],
      customer: ["customer", "client", "party", "customer name"],
      country: ["country", "destination", "dest"],
      service: ["service", "courier", "shipping"],
    }
  },
  combined_order_sheet: {
    required: ["tracking", "client_code", "product", "qty", "country", "service", "date", "weight"],
    optional: ["invoice", "customer"],
    aliases: {
      tracking: ["tracking", "tracking no", "tracking number", "awb"],
      client_code: ["client code", "client_code", "client"],
      product: ["2nd name", "2nd_name", "second name", "product", "medicine"],
      qty: ["qty", "quantity"],
      country: ["country", "destination"],
      service: ["service", "courier", "shipping"],
      date: ["ok parcel dt", "ok parcel date", "parcel dt", "parcel date"],
      weight: ["weight (gm)", "weight", "wt", "wt (gm)"],
      invoice: ["invoice no", "invoice", "inv no"],
      customer: ["customer name", "customer", "name"],
    }
  },
  packing_list: {
    required: [],
    optional: ["box", "tracking", "weight", "product", "qty"],
    aliases: {
      box: ["box", "box no", "box number", "carton"],
      tracking: ["tracking", "tracking no", "awb", "track no", "track number"],
      weight: ["weight", "wt", "kg", "wght"],
      product: ["product", "product name", "item", "medicine"],
    }
  },
  product_price_list: {
    required: ["product", "price"],
    optional: ["category", "unit", "company"],
    aliases: {
      product: ["product", "product name", "item", "medicine", "name"],
      price: ["price", "rate", "cost", "mrp", "unit price"],
      category: ["category", "cat", "type", "group"],
    }
  },
  price_history: {
    required: ["product", "price", "date"],
    optional: ["company", "notes"],
    aliases: {
      product: ["product", "product name", "item", "medicine"],
      price: ["price", "rate", "cost", "new price"],
      date: ["date", "effective date", "from date", "updated on"],
    }
  },
  sales_by_partner: {
    required: ["tracking", "sell"],
    optional: ["product", "customer", "date"],
    aliases: {
      tracking: ["tracking", "tracking no", "awb", "track"],
      sell: ["sell", "sell price", "selling price", "amount", "sale amount"],
    }
  },
  sales_by_agent: {
    required: ["tracking", "sell"],
    optional: ["product", "customer", "date", "agent"],
    aliases: {
      tracking: ["tracking", "tracking no", "awb"],
      sell: ["sell", "sell price", "amount"],
      agent: ["agent", "agent name", "sales person"],
    }
  },
  sales_by_client: {
    required: ["tracking", "sell"],
    optional: ["product", "customer", "date"],
    aliases: {
      tracking: ["tracking", "tracking no", "awb"],
      sell: ["sell", "sell price", "amount"],
    }
  },
  client_ledger: {
    required: ["client", "amount"],
    optional: ["date", "type", "reference", "notes"],
    aliases: {
      client: ["client", "client code", "party", "customer"],
      amount: ["amount", "amt", "value", "debit", "credit"],
    }
  },
  payment_records: {
    required: ["client", "amount", "date"],
    optional: ["reference", "mode", "notes"],
    aliases: {
      client: ["client", "client code", "party"],
      amount: ["amount", "amt", "payment"],
      date: ["date", "payment date", "received on"],
    }
  },
  client_price_list: {
    required: ["product_name", "sell_price"],
    optional: ["generic_name", "company", "unit", "branded"],
    aliases: {
      product_name: ["product", "product name", "product_name", "name", "item"],
      sell_price: ["sell price", "sell_price", "price", "rate", "selling price"],
      generic_name: ["generic", "generic name", "generic_name"],
      company: ["company", "brand", "manufacturer"],
      unit: ["unit", "pack", "packing"],
      branded: ["branded", "is_branded", "is branded"],
    }
  },
  shipping_rates: {
    required: ["country", "service", "weight", "rate"],
    optional: ["zone", "min_weight", "max_weight"],
    aliases: {
      country: ["country", "destination", "dest"],
      service: ["service", "courier", "carrier"],
      weight: ["weight", "wt", "kg", "slab"],
      rate: ["rate", "price", "cost", "charge"],
    }
  },
  partner_ledger_structured: {
    required: ["tracking", "total"],
    optional: ["date", "customer", "medicine", "qty", "shipping", "grand_total", "cost", "esr", "personal", "payment_mode"],
    aliases: {
      tracking: ["tracking", "tracking no", "tracking number", "track no", "awb"],
      total: ["total", "total amount", "amount", "sale", "sale amount"],
      date: ["date", "order date", "transaction date"],
      customer: ["customer", "client", "party", "customer name"],
      medicine: ["medicine", "product", "item", "product name"],
      qty: ["qty", "quantity", "units"],
      shipping: ["shipping", "shipping cost", "freight"],
      grand_total: ["grand total", "grand_total", "final amount"],
      cost: ["cost", "costing", "purchase cost"],
      esr: ["esr", "esr payment", "company payment"],
      personal: ["personal", "personal collection", "cash"],
      payment_mode: ["payment mode", "payment_mode", "mode", "payment method"],
    }
  },
};

// Upload category configurations
const UPLOAD_CATEGORIES = {
  shipment_costing: {
    id: "shipment_costing",
    title: "Shipment Costing",
    description: "Upload order sheets and packing lists",
    icon: Package,
    color: "blue",
    bgColor: "bg-blue-50",
    borderColor: "border-blue-200",
    iconColor: "text-blue-600",
    uploads: [
      {
        id: "order_sheet",
        name: "Order Sheet",
        endpoint: "/api/shipment-costing/process",
        templateEndpoint: "/api/shipment-costing/download-template",
        description: "Upload daily order sheets with tracking, products, weights",
        acceptedFormats: ".xlsx, .xls",
      },
      {
        id: "packing_list",
        name: "Packing List",
        endpoint: "/api/shipment-costing/upload-packing",
        templateEndpoint: "/api/shipment-costing/packing-template",
        description: "Upload packing lists with box details",
        acceptedFormats: ".xlsx, .xls",
      },
      {
        id: "combined_order_sheet",
        name: "Combined Database Sheet",
        endpoint: "/api/shipment-costing/process-combined-async",
        templateEndpoint: "/api/shipment-costing/combined-template",
        description:
          "Single-sheet combined format (Client Code, Tracking, 2nd Name, Qty, Customer, Country, Service, Parcel DT, Weight). Invoice No is ignored. Costing flows into the same Shipment Costing panel.",
        acceptedFormats: ".xlsx, .xls",
      },
    ],
  },
  price_list: {
    id: "price_list",
    title: "Price Management",
    description: "Upload product prices and price history",
    icon: DollarSign,
    color: "green",
    bgColor: "bg-green-50",
    borderColor: "border-green-200",
    iconColor: "text-green-600",
    uploads: [
      {
        id: "product_price_list",
        name: "Product Price List",
        endpoint: "/api/products/upload",
        templateEndpoint: "/api/products/download-template",
        description: "Upload or update product price list",
        acceptedFormats: ".xlsx, .xls",
      },
      {
        id: "price_history",
        name: "Price History",
        endpoint: "/api/products/branded-price-history/upload",
        templateEndpoint: "/api/products/branded-price-history/template",
        description: "Upload branded products price history with dates",
        acceptedFormats: ".xlsx, .xls",
      },
    ],
  },
  client_pricing: {
    id: "client_pricing",
    title: "DP CLIENTS Price Lists",
    description: "Upload custom price lists and price history for DP CLIENTS",
    icon: Users,
    color: "indigo",
    bgColor: "bg-indigo-50",
    borderColor: "border-indigo-200",
    iconColor: "text-indigo-600",
    uploads: [
      {
        id: "client_price_list",
        name: "DP CLIENT Price List",
        endpoint: "/api/ledgers/{client_code}/price-list/upload",
        templateEndpoint: "/api/client-price-list/template",
        description: "Upload custom price list for a specific DP CLIENT with markup options",
        acceptedFormats: ".xlsx, .xls",
        requiresClient: true,  // Special flag for client selection
        icon: DollarSign,
      },
      {
        id: "client_price_history",
        name: "DP CLIENT Price History",
        endpoint: "/api/ledgers/{client_code}/price-history/upload",
        templateEndpoint: "/api/client-price-history/template",
        description: "Upload historical prices with effective dates (for last 5 years data)",
        acceptedFormats: ".xlsx, .xls",
        requiresClient: true,
        icon: History,
      },
    ],
  },
  client_shipping: {
    id: "client_shipping",
    title: "DP CLIENTS Shipping Rates",
    description: "Upload custom shipping rates and rate history for DP CLIENTS",
    icon: Truck,
    color: "blue",
    bgColor: "bg-blue-50",
    borderColor: "border-blue-200",
    iconColor: "text-blue-600",
    uploads: [
      {
        id: "client_shipping_rates",
        name: "DP CLIENT Shipping Rates",
        endpoint: "/api/ledgers/{client_code}/shipping-rates/upload",
        templateEndpoint: "/api/client-shipping-rates/template",
        description: "Upload custom shipping rates (Country, Service, Weight Range, Price) for DP CLIENT",
        acceptedFormats: ".xlsx, .xls",
        requiresClient: true,  // Special flag for client selection
        icon: Truck,
      },
      {
        id: "client_shipping_history",
        name: "DP CLIENT Shipping Rate History",
        endpoint: "/api/ledgers/{client_code}/shipping-rate-history/upload",
        templateEndpoint: "/api/client-shipping-history/template",
        description: "Upload historical shipping rates with effective dates (for past rate changes)",
        acceptedFormats: ".xlsx, .xls",
        requiresClient: true,
        icon: History,
      },
    ],
  },
  sales_profit: {
    id: "sales_profit",
    title: "Sales & Profit",
    description: "Upload sales data by partner, agent, or client",
    icon: TrendingUp,
    color: "purple",
    bgColor: "bg-purple-50",
    borderColor: "border-purple-200",
    iconColor: "text-purple-600",
    uploads: [
      {
        id: "sales_by_partner",
        name: "Sales by Partner",
        endpoint: "/api/sales/upload",
        templateEndpoint: "/api/sales/partner-template",
        params: { type: "partner" },
        description: "Upload partner-wise sales data",
        acceptedFormats: ".xlsx, .xls",
        icon: Building,
      },
      {
        id: "sales_by_agent",
        name: "Sales by Agent",
        endpoint: "/api/sales/upload",
        templateEndpoint: "/api/sales/agent-template",
        params: { type: "agent" },
        description: "Upload agent-wise sales data",
        acceptedFormats: ".xlsx, .xls",
        icon: UserCheck,
      },
      {
        id: "sales_by_client",
        name: "Sales by Client",
        endpoint: "/api/sales/upload",
        templateEndpoint: "/api/sales/client-template",
        params: { type: "client" },
        description: "Upload client-wise sales data",
        acceptedFormats: ".xlsx, .xls",
        icon: Users,
      },
    ],
  },
  ledger: {
    id: "ledger",
    title: "Ledger",
    description: "Upload client ledger and payment records",
    icon: BookOpen,
    color: "amber",
    bgColor: "bg-amber-50",
    borderColor: "border-amber-200",
    iconColor: "text-amber-600",
    uploads: [
      {
        id: "partner_ledger_structured",
        name: "Partner Ledger - Structured Format",
        endpoint: "/api/partner-ledger/upload",
        templateEndpoint: "/api/partner-ledger/template",
        templateEndpoints: [
          { label: "Full Template (with samples)", endpoint: "/api/partner-ledger/template" },
          { label: "Partner Template (J-N empty)", endpoint: "/api/partner-ledger/template-partner" },
        ],
        description: "Upload partner sales sheet with structured columns (Tracking, Customer, Total, Shipping, Cost, ESR, etc.)",
        acceptedFormats: ".xlsx, .xls",
        requiresClient: true,
        icon: Table,
      },
      {
        id: "client_ledger",
        name: "Client Ledger",
        endpoint: "/api/ledger/upload",
        templateEndpoint: "/api/ledger/download-template",
        description: "Upload client ledger entries",
        acceptedFormats: ".xlsx, .xls",
      },
      {
        id: "payment_records",
        name: "Payment Records",
        endpoint: "/api/ledger/payments/upload",
        templateEndpoint: "/api/ledger/payments-template",
        description: "Upload payment records",
        acceptedFormats: ".xlsx, .xls",
      },
      {
        id: "raw_funds",
        name: "Raw Funds (Accounting)",
        endpoint: "/api/payment-verification/raw-funds/upload",
        templateEndpoint: "/api/payment-verification/raw-funds/template",
        description: "Internal payment-landed-in-bank sheet from the accounting team. Auto-verifies partner ledger payments when their structured sheet is uploaded (same client + INR ±₹50 + date ±7d).",
        acceptedFormats: ".xlsx, .xls",
        icon: Table,
      },
    ],
  },
  shipping: {
    id: "shipping",
    title: "Shipping Rates",
    description: "Upload shipping price lists by service and country",
    icon: Truck,
    color: "rose",
    bgColor: "bg-rose-50",
    borderColor: "border-rose-200",
    iconColor: "text-rose-600",
    uploads: [
      {
        id: "shipping_rates",
        name: "Shipping Rates",
        endpoint: "/api/shipping/upload",
        templateEndpoint: "/api/shipping/download-template",
        description: "Upload shipping rates by service, country, weight",
        acceptedFormats: ".xlsx, .xls",
      },
    ],
  },
};

const UploadCenterPage = () => {
  usePageTitle("Upload Center");
  const { user, token } = useAuth();
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedUpload, setSelectedUpload] = useState(null);
  const [uploadDialog, setUploadDialog] = useState(false);
  const [uploading, setUploading] = useState(false);
  // ---- Structured Data Conversion (raw partner sales → structured ledger)
  const [convFile, setConvFile] = useState(null);
  const [convPartner, setConvPartner] = useState("");
  const [convRunning, setConvRunning] = useState(false);
  const [convResult, setConvResult] = useState(null);
  const convFileInputRef = useRef(null);
  const handleConvFileSelect = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".xlsx")) {
      toast.error("Only .xlsx files are supported.");
      return;
    }
    setConvFile(f);
    // Auto-suggest partner code from filename prefix
    if (!convPartner) {
      const base = f.name.replace(/\.xlsx$/i, "").split(/[_\-\s]/)[0].toUpperCase();
      setConvPartner(base || "");
    }
    setConvResult(null);
  };
  const handleConvert = async () => {
    if (!convFile) { toast.error("Pick a raw sales file first"); return; }
    if (!convPartner.trim()) { toast.error("Enter a partner code (used as the output sheet name + filename)"); return; }
    setConvRunning(true);
    setConvResult(null);
    try {
      const token = localStorage.getItem("token");
      const fd = new FormData();
      fd.append("file", convFile);
      const url = `${BACKEND_URL}/api/upload-center/raw-to-structured-conversion?partner_code=${encodeURIComponent(convPartner.trim().toUpperCase())}`;
      const r = await fetch(url, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (!r.ok) {
        let detail = `HTTP ${r.status}`;
        try { const j = await r.clone().json(); detail = j.detail || detail; }
        catch { detail = (await r.clone().text()) || detail; }
        toast.error(detail);
        return;
      }
      // Capture conversion metadata from response headers
      const rows = r.headers.get("X-Conversion-Rows") || "?";
      const skipped = r.headers.get("X-Conversion-Skipped-Sheets") || "";
      const blob = await r.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = downloadUrl;
      a.download = `structured_ledger_${convPartner.trim().toUpperCase().replace(/[^A-Z0-9_-]+/g, "_")}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(downloadUrl);
      setConvResult({
        rows: parseInt(rows, 10) || 0,
        skipped: skipped ? skipped.split(",") : [],
        downloaded_at: new Date().toISOString(),
      });
      toast.success(`✅ Converted ${rows} transactions. File downloaded.`);
    } catch (e) {
      toast.error(`Conversion failed: ${e.message || e}`);
    } finally {
      setConvRunning(false);
    }
  };
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadResult, setUploadResult] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedFileData, setSelectedFileData] = useState(null); // Store ArrayBuffer to avoid "Body is disturbed" error
  // Combined Database Sheet — optional corrections ArrayBuffer. When set, the
  // final upload sends this patched workbook instead of the original bytes.
  const [patchedFileData, setPatchedFileData] = useState(null);
  const [correctionsSummary, setCorrectionsSummary] = useState(null);
  const [uploadHistory, setUploadHistory] = useState([]);
  // Upload History — original-file preview drawer + download state
  const [historyViewOpen, setHistoryViewOpen] = useState(false);
  const [historyView, setHistoryView] = useState(null);
  const [historyViewLoading, setHistoryViewLoading] = useState(false);
  const [historyViewError, setHistoryViewError] = useState(null);
  const [historyViewSheetIdx, setHistoryViewSheetIdx] = useState(0);
  const [downloadingStoreId, setDownloadingStoreId] = useState(null);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [activeTab, setActiveTab] = useState("upload");
  const [searchParams] = useSearchParams();
  
  // Read tab from URL query param on mount
  useEffect(() => {
    const tabParam = searchParams.get("tab");
    if (tabParam && ["upload", "convert", "year-summary", "pending", "history", "audit"].includes(tabParam)) {
      setActiveTab(tabParam);
    }
  }, [searchParams]);

  // Capture uncaught frontend errors during upload/validation so the dialog
  // can surface them (earlier bugs like ReferenceError: pickSample were
  // silently swallowed in console.warn — the strip below makes them visible).
  useEffect(() => {
    const onError = (ev) => {
      const msg = ev?.error?.message || ev?.message || String(ev?.reason || ev);
      if (!msg) return;
      setFrontendErrors((prev) => {
        if (prev.some((e) => e.msg === msg)) return prev;
        return [...prev, { msg: String(msg).slice(0, 400), at: new Date().toISOString() }];
      });
    };
    const onReject = (ev) => onError({ message: ev?.reason?.message || String(ev?.reason) });
    window.addEventListener("error", onError);
    window.addEventListener("unhandledrejection", onReject);
    return () => {
      window.removeEventListener("error", onError);
      window.removeEventListener("unhandledrejection", onReject);
    };
  }, []);

  const fileInputRef = useRef(null);
  const [validationResult, setValidationResult] = useState(null);
  const [validating, setValidating] = useState(false);
  const [filePreview, setFilePreview] = useState(null);
  // Upload-only frontend error capture. Any JS error thrown by the validation
  // scan or the upload pipeline lands here so users can see exactly why a
  // panel is missing — instead of silently swallowing it in console.warn.
  const [frontendErrors, setFrontendErrors] = useState([]);
  
  // Background task state (for large file processing)
  const [backgroundTaskId, setBackgroundTaskId] = useState(null);
  const [progressMessage, setProgressMessage] = useState("");
  
  // Packing weight source dialog state
  const [showWeightDialog, setShowWeightDialog] = useState(false);
  const [weightSource, setWeightSource] = useState("price_list");
  const [applyingPacking, setApplyingPacking] = useState(false);
  const [uploadSummaryResult, setUploadSummaryResult] = useState(null);
  
  // Client selection for client-based uploads
  const [clients, setClients] = useState([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientSearch, setClientSearch] = useState("");
  const [replaceExisting, setReplaceExisting] = useState(false);
  
  // Duplicate handling for Shipment Costing
  const [showDuplicateDialog, setShowDuplicateDialog] = useState(false);
  const [duplicateInfo, setDuplicateInfo] = useState({ 
    count: 0, 
    trackings: [],
    // Smart breakdown
    unassigned_count: 0,
    error_count: 0,
    clean_count: 0,
    problem_count: 0
  });
  const [pendingOrders, setPendingOrders] = useState(null);
  const [pendingStagingId, setPendingStagingId] = useState(null);
  const [duplicateAction, setDuplicateAction] = useState("skip"); // "skip", "replace", or "update_problems"

  // Role-based permissions state
  const [hiddenSections, setHiddenSections] = useState([]);
  const [loadingPermissions, setLoadingPermissions] = useState(true);

  // Year Summary tab state
  // Manual Parcels — editable per-month parcel count entered by upload team.
  // Map of "year-month" → { parcels, updated_by, updated_at }.
  const [manualParcels, setManualParcels] = useState({});
  const [editingManualKey, setEditingManualKey] = useState(null);
  const [editingManualValue, setEditingManualValue] = useState("");
  const [yearSummaryData, setYearSummaryData] = useState({ months: [], totals: {} });
  const [yearSummaryYear, setYearSummaryYear] = useState(new Date().getFullYear());
  const [loadingYearSummary, setLoadingYearSummary] = useState(false);
  const [editYearSummaryDialog, setEditYearSummaryDialog] = useState(false);
  // Category + name filter for Year Summary
  const [yearSummaryCategory, setYearSummaryCategory] = useState("all");
  const [yearSummaryClientCode, setYearSummaryClientCode] = useState("");
  const [yearSummaryFilterOptions, setYearSummaryFilterOptions] = useState({ dp_clients: [], partners: [], agents: [] });

  // Balance Raw drill-down modal
  const [balanceRawOpen, setBalanceRawOpen] = useState(false);
  const [balanceRawLoading, setBalanceRawLoading] = useState(false);
  const [balanceRawData, setBalanceRawData] = useState(null);
  const [balanceRawContext, setBalanceRawContext] = useState({ year: null, month: null });
  // On-screen preview opens in a new browser tab via openYearSummaryPreview.
  // Remember last-viewed dataset per (year+month) so finance can reopen the
  // same slice with a single click while iterating on one partner.
  const [lastViewedDataset, setLastViewedDataset] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("eximflow_year_summary_last_view") || "{}");
    } catch {
      return {};
    }
  });
  const DATASET_LABELS = {
    parcels: "Parcels",
    costing: "Costing DP",
    costing_partners: "Costing Partners",
    partners_ledger: "Partners Ledger",
    sales_pending_dp: "Sales Pending DP",
    sales_pending_partners: "Sales Pending Partners",
    errors: "Other Errors",
    payment: "Payment",
  };
  // Per-day expansion state
  const [expandedMonth, setExpandedMonth] = useState(null); // month number 1-12
  const [monthDaysData, setMonthDaysData] = useState({});   // { "<year>-<month>": [days] }
  const [monthDaysLoading, setMonthDaysLoading] = useState(false);
  // Bulk-day selection
  const [selectedDays, setSelectedDays] = useState({}); // { "<year>-<month>-<day>": true }
  const [bulkDataset, setBulkDataset] = useState("parcels");
  const [bulkDownloading, setBulkDownloading] = useState(false);
  const [editingYearMonth, setEditingYearMonth] = useState(null);
  const [editYearVerified, setEditYearVerified] = useState(false);
  const [editYearNarration, setEditYearNarration] = useState("");

  // Reconcile dialog state
  const [reconcileDialog, setReconcileDialog] = useState(false);
  const [reconcileData, setReconcileData] = useState(null);
  const [loadingReconcile, setLoadingReconcile] = useState(false);
  const [reconcileShowAll, setReconcileShowAll] = useState(false);
  const [reconcileDetailDialog, setReconcileDetailDialog] = useState(false);
  const [reconcileDetailData, setReconcileDetailData] = useState(null);
  const [reconcileDetailMeta, setReconcileDetailMeta] = useState(null); // { month_name, dataset, delta }
  const [loadingReconcileDetail, setLoadingReconcileDetail] = useState(false);

  const fetchReconcile = async () => {
    setLoadingReconcile(true);
    setReconcileDialog(true);
    try {
      const res = await fetch(
        `${BACKEND_URL}/api/upload-center/year-summary/reconcile?year=${yearSummaryYear}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setReconcileData(data);
    } catch (err) {
      toast.error(`Reconcile failed: ${err.message}`);
      setReconcileDialog(false);
    } finally {
      setLoadingReconcile(false);
    }
  };

  const fetchReconcileDetail = async (month, monthName, dataset, cell) => {
    setReconcileDetailMeta({ month, month_name: monthName, dataset, ...cell });
    setReconcileDetailDialog(true);
    setLoadingReconcileDetail(true);
    setReconcileDetailData(null);
    try {
      const res = await fetch(
        `${BACKEND_URL}/api/upload-center/year-summary/reconcile-detail?year=${yearSummaryYear}&month=${month}&dataset=${dataset}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setReconcileDetailData(data);
    } catch (err) {
      toast.error(`Drill-down failed: ${err.message}`);
      setReconcileDetailDialog(false);
    } finally {
      setLoadingReconcileDetail(false);
    }
  };

  // ── Reconcile actions: mark resolved / open in costing / send to partner ──
  const markReconcileResolved = async (tracking) => {
    try {
      const res = await fetch(
        `${BACKEND_URL}/api/upload-center/year-summary/reconcile-resolve`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
          body: JSON.stringify({ tracking }),
        }
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      toast.success(`${tracking} marked resolved`);
      // Optimistically remove from the open dialog
      setReconcileDetailData((prev) =>
        prev ? { ...prev, rows: prev.rows.filter((r) => r.tracking !== tracking) } : prev
      );
      // Refresh the parent reconcile counts
      fetchReconcile();
    } catch (err) {
      toast.error(`Resolve failed: ${err.message}`);
    }
  };

  const openInCosting = (tracking) => {
    window.open(`/shipment-costing?tracking=${encodeURIComponent(tracking)}`, "_blank", "noopener");
  };

  // Download a per-client Close Pack for the partner whose reconcile row was
  // clicked — gives finance everything they need to confront that partner
  // directly with one click, without leaving the Reconcile cockpit.
  const downloadClientClosePack = async (clientCode) => {
    const cc = (clientCode || "").trim().toUpperCase();
    if (!cc || !reconcileDetailMeta?.month) {
      toast.error("Missing client code or month");
      return;
    }
    const month = reconcileDetailMeta.month;
    const url = `${BACKEND_URL}/api/upload-center/year-summary/download?year=${yearSummaryYear}&month=${month}&dataset=all&client_code=${encodeURIComponent(cc)}`;
    try {
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      const monthName = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"][month - 1];
      a.download = `EximFlow_ClosePack_${cc}_${monthName}${yearSummaryYear}.xlsx`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success(`Downloaded Close Pack for ${cc}`);
    } catch (err) {
      toast.error(`Download failed: ${err.message}`);
    }
  };

  // Open the on-screen preview in a NEW TAB.
  // Saves the choice as the "last viewed" dataset for the (year+month) so
  // a one-click 🕒 chip can reopen the same slice later.
  const openYearSummaryPreview = (month, dataset, day = null) => {
    if (!dataset) return;
    const yr = yearSummaryData?.year || yearSummaryYear;
    const cf = (yearSummaryClientCode || "").trim().toUpperCase();
    // Persist last-viewed slice (per year+month).
    const key = `${yr}-${month}`;
    setLastViewedDataset((prev) => {
      const next = { ...prev, [key]: dataset };
      try { localStorage.setItem("eximflow_year_summary_last_view", JSON.stringify(next)); } catch {}
      return next;
    });
    // Build the preview page URL (relative, opens in new tab).
    const qs = new URLSearchParams({
      year: String(yr),
      month: String(month),
      dataset,
    });
    if (day) qs.set("day", String(day));
    if (cf) qs.set("client_code", cf);
    window.open(`/year-summary-preview?${qs.toString()}`, "_blank", "noopener");
  };

  const sendToPartner = async (row) => {
    try {
      const res = await fetch(
        `${BACKEND_URL}/api/upload-center/partner-contact/${encodeURIComponent(row.client_code)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        throw new Error(e.detail || `HTTP ${res.status}`);
      }
      const c = await res.json();
      const phone = (c.phone || "").replace(/[^\d+]/g, "");
      const lines = [
        `Hi ${c.name || c.client_code},`,
        ``,
        `We need help reconciling a parcel that has both costing and sales gaps:`,
        `• Tracking: ${row.tracking}`,
        `• Customer: ${row.customer_name || "—"}`,
        `• Order date: ${formatDMY(row.order_date)}`,
        `• Qty: ${row.qty ?? "—"} | Gross Wt: ${row.gross_weight ?? "—"}`,
        ``,
        `Could you please share the sale details and confirm the cost?`,
        `Thanks!`,
      ];
      const message = encodeURIComponent(lines.join("\n"));
      if (phone) {
        const cleaned = phone.startsWith("+") ? phone.slice(1) : phone;
        window.open(`https://wa.me/${cleaned}?text=${message}`, "_blank", "noopener");
      } else if (c.email) {
        const subject = encodeURIComponent(`Reconcile request — ${row.tracking}`);
        window.open(`mailto:${c.email}?subject=${subject}&body=${message}`, "_blank", "noopener");
      } else {
        // No contact on file — copy the message to clipboard so user can paste anywhere
        try {
          await navigator.clipboard.writeText(decodeURIComponent(message));
          toast.success(`No phone/email on file for ${c.client_code}. Message copied to clipboard.`);
        } catch {
          toast.error(`No phone/email on file for ${c.client_code}.`);
        }
      }
    } catch (err) {
      toast.error(`Send to partner failed: ${err.message}`);
    }
  };

  // Fetch year summary data
  const fetchYearSummary = async () => {
    setLoadingYearSummary(true);
    try {
      const params = new URLSearchParams({ year: String(yearSummaryYear) });
      if (yearSummaryCategory && yearSummaryCategory !== "all") {
        params.append("category", yearSummaryCategory);
      }
      if (yearSummaryClientCode && yearSummaryClientCode !== "__category__") {
        params.append("client_code", yearSummaryClientCode);
      }
      const response = await fetch(
        `${BACKEND_URL}/api/upload-center/year-summary?${params}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setYearSummaryData(data);
      }
    } catch (error) {
      console.error("Error fetching year summary:", error);
    } finally {
      setLoadingYearSummary(false);
    }
  };

  // Load the category/name dropdown options once on first year-summary view
  const fetchYearSummaryFilterOptions = async () => {
    try {
      const r = await fetch(`${BACKEND_URL}/api/upload-center/year-summary/filter-options`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (r.ok) setYearSummaryFilterOptions(await r.json());
    } catch (e) {
      // Silent — dropdown will just be sparse
    }
  };

  const openBalanceRawDrilldown = async (year, month) => {
    setBalanceRawContext({ year, month });
    setBalanceRawOpen(true);
    setBalanceRawLoading(true);
    setBalanceRawData(null);
    try {
      const params = new URLSearchParams({ year: String(year), month: String(month) });
      if (yearSummaryCategory && yearSummaryCategory !== "all") params.append("category", yearSummaryCategory);
      if (yearSummaryClientCode) params.append("client_code", yearSummaryClientCode);
      const r = await fetch(`${BACKEND_URL}/api/upload-center/year-summary/balance-raw-drilldown?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) { toast.error("Drill-down failed"); return; }
      setBalanceRawData(await r.json());
    } catch (e) {
      toast.error("Drill-down failed: " + e.message);
    } finally {
      setBalanceRawLoading(false);
    }
  };

  // Fetch per-day breakdown for a given month
  const fetchMonthDays = async (month) => {
    const cacheKey = `${yearSummaryYear}-${month}`;
    setMonthDaysLoading(true);
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/upload-center/year-summary/days?year=${yearSummaryYear}&month=${month}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setMonthDaysData((prev) => ({ ...prev, [cacheKey]: data.days || [] }));
      }
    } catch (error) {
      console.error("Error fetching month days:", error);
    } finally {
      setMonthDaysLoading(false);
    }
  };

  // Toggle expansion of a month row
  const toggleMonthExpansion = async (month) => {
    if (expandedMonth === month) {
      setExpandedMonth(null);
      return;
    }
    setExpandedMonth(month);
    const cacheKey = `${yearSummaryYear}-${month}`;
    if (!monthDaysData[cacheKey]) {
      await fetchMonthDays(month);
    }
  };

  // Update year summary verification
  const handleUpdateYearVerification = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/upload-center/update-verification`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          year: yearSummaryYear,
          month: editingYearMonth?.month,
          verified: editYearVerified,
          narration: editYearNarration,
        }),
      });
      if (response.ok) {
        toast.success("Verification updated");
        setEditYearSummaryDialog(false);
        fetchYearSummary();
      }
    } catch (error) {
      toast.error("Failed to update verification");
    }
  };

  // Handle double-click on year summary row
  const handleYearSummaryDoubleClick = (monthData) => {
    setEditingYearMonth(monthData);
    setEditYearVerified(monthData.verified || false);
    setEditYearNarration(monthData.narration || "");
    setEditYearSummaryDialog(true);
  };

  // Partner Ledger Duplicate Handling State
  const [duplicateDialog, setDuplicateDialog] = useState(false);
  const [duplicateEntries, setDuplicateEntries] = useState([]);
  const [ledgerErrorEntries, setLedgerErrorEntries] = useState([]);
  const [pendingLedgerResult, setPendingLedgerResult] = useState(null);
  const [ledgerDuplicateAction, setLedgerDuplicateAction] = useState("skip"); // skip, keep_both, replace

  // Handle duplicate resolution for partner ledger
  const handleDuplicateResolution = async (action) => {
    if (!pendingLedgerResult) {
      // This used to silently abort, leaving the buttons in the dialog
      // looking unresponsive. Surface the issue so users can re-upload
      // instead of being stuck.
      toast.error("Upload session expired. Please close this dialog and re-upload the file.");
      return;
    }
    
    setDuplicateDialog(false);
    setUploading(true);
    setProgressMessage("Processing with duplicate resolution...");
    
    try {
      // Call backend to process with the chosen action
      const response = await fetch(`${BACKEND_URL}/api/partner-ledger/process/${pendingLedgerResult.upload_id}`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          duplicate_action: action, // skip, keep_both, replace
        }),
      });
      
      if (!response.ok) {
        throw new Error("Failed to process ledger");
      }
      
      const processResult = await response.json();
      
      // Show success
      const message = `✓ Processed ${processResult.processed || 0} entries (${action === 'skip' ? 'duplicates skipped' : action === 'replace' ? 'duplicates replaced' : 'all kept'})`;
      
      setUploadResult({
        success: true,
        message: message,
        details: processResult,
        isPartnerLedger: true
      });
      toast.success(message);
      
    } catch (error) {
      toast.error("Failed to process: " + error.message);
    } finally {
      setUploading(false);
      setPendingLedgerResult(null);
      setDuplicateEntries([]);
      setLedgerErrorEntries([]);
    }
  };

  // Fetch year summary when tab is active
  useEffect(() => {
    if (activeTab === "year-summary") {
      // Also load manual-parcels for the selected year (silent — handler shows toast on save).
      (async () => {
        try {
          const r = await fetch(`${BACKEND_URL}/api/payment-verification/manual-parcels?year=${yearSummaryYear}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (!r.ok) return;
          const d = await r.json();
          const map = {};
          for (const m of d.months || []) {
            if (m.parcels != null) map[`${yearSummaryYear}-${m.month}`] = m;
          }
          setManualParcels(map);
        } catch {}
      })();
      fetchYearSummary();
      if (!yearSummaryFilterOptions.dp_clients.length && !yearSummaryFilterOptions.partners.length) {
        fetchYearSummaryFilterOptions();
      }
    }
  }, [activeTab, yearSummaryYear, yearSummaryCategory, yearSummaryClientCode]);

  // Fetch role permissions on mount
  useEffect(() => {
    const fetchPermissions = async () => {
      if (!user?.role || user?.role === 'super_admin') {
        setLoadingPermissions(false);
        return; // Admin sees everything
      }
      
      try {
        const response = await fetch(`${BACKEND_URL}/api/role-permissions/${user.role}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setHiddenSections(data.upload_center?.hidden_sections || []);
        }
      } catch (error) {
        console.error("Failed to fetch permissions:", error);
      } finally {
        setLoadingPermissions(false);
      }
    };
    
    fetchPermissions();
  }, [user?.role, token]);

  // Filter categories based on permissions
  const visibleCategories = Object.values(UPLOAD_CATEGORIES).filter(
    category => !hiddenSections.includes(category.id)
  );

  // Fetch clients list when needed
  const fetchClients = async () => {
    if (clients.length > 0) return; // Already fetched
    setLoadingClients(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/ledgers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        // API returns {ledgers: [...], total: N}
        setClients(data.ledgers || data || []);
      }
    } catch (error) {
      console.error("Failed to fetch clients:", error);
      toast.error("Failed to load clients list");
    } finally {
      setLoadingClients(false);
    }
  };

  // Filter clients based on search
  const filteredClients = clients.filter(client => 
    client.client_code?.toLowerCase().includes(clientSearch.toLowerCase()) ||
    client.client_name?.toLowerCase().includes(clientSearch.toLowerCase())
  );

  // Validate file columns against required columns
  const validateFileColumns = async (file, uploadId) => {
    setValidating(true);
    setValidationResult(null);
    setFilePreview(null);
    setSelectedFileData(null); // Clear previous file data
    setPatchedFileData(null);
    setCorrectionsSummary(null);
    setFrontendErrors([]); // Clear old errors; only keep ones from this attempt
    
    try {
      // Use slice() to create a copy - more reliable across browsers (especially mobile)
      const blob = file.slice(0, file.size, file.type);
      
      // Check if file is .xlsb - skip validation as XLSX.js has limited xlsb support
      if (file.name.endsWith('.xlsb')) {
        // For .xlsb files, read and store the data for later upload
        const xlsbData = await blob.arrayBuffer();
        setSelectedFileData(xlsbData);
        
        setValidationResult({
          valid: true,
          warning: "Binary Excel file (.xlsb) - column validation skipped. File will be processed by server."
        });
        setFilePreview({
          headers: ['Binary Excel format - preview not available'],
          rowCount: 'Unknown',
          sampleData: []
        });
        return true;
      }
      
      // Read and store the file data - IMPORTANT: This prevents "Body is disturbed" error
      const data = await blob.arrayBuffer();
      setSelectedFileData(data); // Store for later use in handleUpload
      
      const workbook = XLSX.read(data, { type: "array" });
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
      
      if (jsonData.length === 0) {
        setValidationResult({
          valid: false,
          error: "File is empty",
          details: "The uploaded file contains no data."
        });
        return false;
      }
      
      // Get headers from first row
      const headers = jsonData[0].map(h => String(h || "").toLowerCase().trim());
      const rowCount = jsonData.length - 1; // Exclude header row
      
      // Get required columns config
      const config = REQUIRED_COLUMNS[uploadId];
      if (!config) {
        // No validation config, allow upload
        setFilePreview({ headers, rowCount, sampleData: jsonData.slice(1, 4) });
        setValidationResult({ valid: true, warning: "No column validation available for this upload type." });
        return true;
      }
      
      // Check for required columns (with alias support)
      const missingColumns = [];
      const foundColumns = {};
      
      for (const reqCol of config.required) {
        const aliases = config.aliases?.[reqCol] || [reqCol];
        const found = aliases.some(alias => 
          headers.some(h => h.includes(alias.toLowerCase()))
        );
        if (!found) {
          missingColumns.push(reqCol);
        } else {
          foundColumns[reqCol] = headers.find(h => 
            aliases.some(alias => h.includes(alias.toLowerCase()))
          );
        }
      }
      
      // Check for optional columns
      const optionalFound = [];
      for (const optCol of (config.optional || [])) {
        const aliases = config.aliases?.[optCol] || [optCol];
        const found = aliases.some(alias => 
          headers.some(h => h.includes(alias.toLowerCase()))
        );
        if (found) {
          optionalFound.push(optCol);
        }
      }
      
      // Set preview data
      setFilePreview({
        headers,
        rowCount,
        sampleData: jsonData.slice(1, 4),
        foundColumns,
        optionalFound
      });

      // ====================================================================
      // ORDER SHEET DEEP PREVIEW: parse all sheets, count parcels & issues
      // ====================================================================
      // For the Order Sheet upload, scan every worksheet, find the TRACKING
      // column (skipping "old tracking"), tally unique tracking numbers and
      // common issues (rows missing a tracking/client) so users can spot
      // obvious problems before hitting "Upload File".
      let orderSheetStats = null;
      if (uploadId === 'order_sheet' || uploadId === 'combined_order_sheet') {
        try {
          const isValidTracking = (s) => {
            if (!s) return false;
            const t = String(s).trim();
            if (!t || t.length < 6) return false;
            if (!/\d/.test(t)) return false;
            const low = t.toLowerCase();
            const bad = ['adv.', 'adv ', 'advance', 'pending', 'tbd', 'n/a', 'nil', 'awaiting', 'later'];
            return !bad.some((b) => low.includes(b));
          };
          let totalRowsAcrossSheets = 0;
          const uniqueTrackings = new Set();
          let invalidTrackingRows = 0;
          let missingClientRows = 0;
          const perSheet = [];
          for (const name of workbook.SheetNames) {
            const ws = workbook.Sheets[name];
            if (!ws) continue;
            const grid = XLSX.utils.sheet_to_json(ws, { header: 1, blankrows: false, defval: '' });
            if (!grid.length) continue;
            // find header row containing TRACKING
            let hdrRow = -1;
            for (let i = 0; i < Math.min(5, grid.length); i++) {
              const up = (grid[i] || []).map((v) => String(v || '').trim().toUpperCase());
              if (up.includes('TRACKING')) { hdrRow = i; break; }
            }
            if (hdrRow === -1) continue;
            const hdr = (grid[hdrRow] || []).map((v) => String(v || '').trim().toUpperCase());
            // TRACKING column that is NOT "OLD TRACKING"
            const trackIdx = hdr.findIndex((h) => h.includes('TRACKING') && !h.includes('OLD'));
            const clientIdx = hdr.findIndex((h) => h === 'CLIENT CODE' || h === 'CLIENT' || h === 'CODE');
            if (trackIdx === -1) continue;
            const sheetTrackings = new Set();
            let sheetRows = 0;
            let sheetInvalid = 0;
            let sheetMissingClient = 0;
            for (let r = hdrRow + 1; r < grid.length; r++) {
              const row = grid[r] || [];
              const tRaw = row[trackIdx];
              const t = tRaw == null ? '' : String(tRaw).trim();
              if (!t) continue;
              sheetRows += 1;
              if (!isValidTracking(t)) {
                sheetInvalid += 1;
                continue;
              }
              const tUp = t.toUpperCase();
              sheetTrackings.add(tUp);
              uniqueTrackings.add(tUp);
              if (clientIdx !== -1) {
                const c = row[clientIdx];
                if (c == null || String(c).trim() === '') sheetMissingClient += 1;
              }
            }
            totalRowsAcrossSheets += sheetRows;
            invalidTrackingRows += sheetInvalid;
            missingClientRows += sheetMissingClient;
            perSheet.push({
              name,
              rows: sheetRows,
              parcels: sheetTrackings.size,
              invalid: sheetInvalid,
            });
          }
          orderSheetStats = {
            totalRows: totalRowsAcrossSheets,
            uniqueParcels: uniqueTrackings.size,
            invalidTrackingRows,
            missingClientRows,
            perSheet,
          };

          // ================================================================
          // DATA QUALITY CHECK (Combined Database Sheet only)
          // Flag rows with suspicious Country values or unknown Service codes
          // BEFORE upload so users can fix the source sheet in one pass.
          // Checks:
          //   • Country: empty, >30 chars, contains digits/commas (address
          //     slipped into the country column), or short/garbage tokens.
          //   • Service: empty, or not in the known-alias list (currently
          //     ITPS/EMS/DHL/CMS/FEDEX/UPS family and common aliases).
          //   • Missing Qty / Weight / Tracking.
          // Also accumulates a `flaggedRows` array so users can download a
          // CSV of just the problematic rows.
          // ================================================================
          if (uploadId === 'combined_order_sheet' || uploadId === 'order_sheet') {
            const KNOWN_SERVICES = [
              'ITPS', 'EMS', 'SPEED POST', 'SPEEDPOST', 'EMS-SERVICE', 'EMS SERVICE',
              'DHL', 'FEDEX', 'UPS', 'CMS',
            ];
            const isKnownService = (s) => {
              if (!s) return false;
              const up = String(s).trim().toUpperCase();
              return KNOWN_SERVICES.some((k) => up === k || up.startsWith(k) || up.includes(k));
            };
            const suspiciousCountry = (c) => {
              if (!c) return 'missing';
              const s = String(c).trim();
              if (!s) return 'missing';
              if (s.length > 30) return 'too-long';
              if (/[0-9,]/.test(s)) return 'contains-digits-or-commas';
              if (/[–—]/.test(s)) return 'contains-dash-separator';
              return null;
            };

            const samples = {
              missing_country: [],
              bad_country: [],
              unknown_service: [],
              missing_qty: [],
              missing_weight: [],
              missing_tracking: [],
              missing_client_code: [],
              missing_parcel_dt: [],
              missing_product: [],
              duplicate_tracking: [],
            };
            const counts = {
              missing_country: 0,
              bad_country: 0,
              unknown_service: 0,
              missing_qty: 0,
              missing_weight: 0,
              missing_tracking: 0,
              missing_client_code: 0,
              missing_parcel_dt: 0,
              missing_product: 0,
              duplicate_tracking: 0,
            };
            let totalFlaggedRows = 0;
            // Full CSV-exportable list of flagged rows (capped for memory safety)
            const flaggedRows = [];
            const FLAGGED_CAP = 5000;
            // Tracking-collision scan: map tracking -> [{sheet,row,customer,country,client}, ...]
            // Only flag as duplicate when the SAME tracking appears with
            // DIFFERENT customers or countries (genuine data error).
            const trackingIndex = new Map();
            // Helper hoisted to the outer scope so it's reachable from both
            // the per-sheet loop AND the post-loop duplicate-tracking phase.
            const pickSample = (kind, value) => {
              if (!samples[kind]) samples[kind] = [];
              if (samples[kind].length < 5 && !samples[kind].includes(value)) {
                samples[kind].push(value);
              }
            };

            for (const name of workbook.SheetNames) {
              const ws = workbook.Sheets[name];
              if (!ws) continue;
              const grid = XLSX.utils.sheet_to_json(ws, { header: 1, blankrows: false, defval: '' });
              if (!grid.length) continue;
              // Find header
              let hdrRow = -1;
              for (let i = 0; i < Math.min(5, grid.length); i++) {
                const up = (grid[i] || []).map((v) => String(v || '').trim().toUpperCase());
                if (up.includes('TRACKING')) { hdrRow = i; break; }
              }
              if (hdrRow === -1) continue;
              const hdr = (grid[hdrRow] || []).map((v) => String(v || '').trim().toUpperCase());
              const colIdx = (names) => {
                for (const n of names) {
                  const i = hdr.indexOf(n);
                  if (i !== -1) return i;
                }
                return -1;
              };
              const trackIdx = hdr.findIndex((h) => h.includes('TRACKING') && !h.includes('OLD'));
              const countryIdx = hdr.indexOf('COUNTRY');
              const serviceIdx = hdr.indexOf('SERVICE');
              const qtyIdx = colIdx(['QTY', 'QUANTITY']);
              const weightIdx = colIdx(['WEIGHT (GM)', 'WEIGHT', 'WT (GM)', 'WT']);
              const invoiceIdx = colIdx(['INVOICE NO', 'INVOICE', 'INV NO']);
              const clientIdxLocal = colIdx(['CLIENT CODE', 'CLIENT']);
              const secondIdx = colIdx(['2ND NAME', 'SECOND NAME', 'PRODUCT', 'MEDICINE']);
              const customerIdx = colIdx(['CUSTOMER NAME', 'CUSTOMER', 'NAME']);
              const dtIdx = colIdx(['OK PARCEL DT', 'OK PARCEL DATE', 'PARCEL DT', 'PARCEL DATE']);
              // Order Sheet specific: its primary product column is "PRODUCT"
              // (distinct from "2ND NAME"). For Combined Sheet this column is
              // absent — product info lives in the 2ND NAME column instead.
              const productIdx = hdr.findIndex((h) => h === 'PRODUCT');
              if (trackIdx === -1) continue;
              for (let r = hdrRow + 1; r < grid.length; r++) {
                const row = grid[r] || [];
                const tRaw = row[trackIdx];
                const t = tRaw == null ? '' : String(tRaw).trim();
                if (!t) continue; // blank row

                // Track every real tracking value we see for duplicate detection
                if (t && t.length >= 6) {
                  const customerVal = customerIdx !== -1 ? String(row[customerIdx] ?? '').trim() : '';
                  const countryVal = countryIdx !== -1 ? String(row[countryIdx] ?? '').trim() : '';
                  const clientVal = clientIdxLocal !== -1 ? String(row[clientIdxLocal] ?? '').trim() : '';
                  const entry = {
                    sheet: name,
                    row: r + 1,
                    customer: customerVal,
                    country: countryVal,
                    client_code: clientVal,
                  };
                  if (!trackingIndex.has(t)) trackingIndex.set(t, []);
                  trackingIndex.get(t).push(entry);
                }

                const rowIssues = [];
                if (!t || t.length < 6) { counts.missing_tracking += 1; pickSample('missing_tracking', `row ${r + 1}`); rowIssues.push('missing_tracking'); }

                // Order Sheet only: first-row-of-parcel sanity checks.
                // Combined Sheet doesn't need these (every row already carries
                // all columns by construction).
                if (uploadId === 'order_sheet') {
                  if (clientIdxLocal !== -1) {
                    const cc = row[clientIdxLocal];
                    const ccs = cc == null ? '' : String(cc).trim();
                    if (!ccs) { counts.missing_client_code += 1; pickSample('missing_client_code', `row ${r + 1}`); rowIssues.push('missing_client_code'); }
                  }
                  if (dtIdx !== -1) {
                    const dv = row[dtIdx];
                    const dvs = dv == null ? '' : String(dv).trim();
                    if (!dvs) { counts.missing_parcel_dt += 1; pickSample('missing_parcel_dt', `row ${r + 1}`); rowIssues.push('missing_parcel_dt'); }
                  }
                  if (productIdx !== -1) {
                    const pv = row[productIdx];
                    const pvs = pv == null ? '' : String(pv).trim();
                    if (!pvs) { counts.missing_product += 1; pickSample('missing_product', `row ${r + 1}`); rowIssues.push('missing_product'); }
                  }
                }
                if (countryIdx !== -1) {
                  const c = row[countryIdx];
                  const issue = suspiciousCountry(c);
                  if (issue === 'missing') { counts.missing_country += 1; pickSample('missing_country', `row ${r + 1}`); rowIssues.push('missing_country'); }
                  else if (issue) { counts.bad_country += 1; pickSample('bad_country', String(c).trim()); rowIssues.push(`bad_country (${issue})`); }
                }
                if (serviceIdx !== -1) {
                  const s = row[serviceIdx];
                  if (!isKnownService(s)) { counts.unknown_service += 1; pickSample('unknown_service', String(s || '(empty)').trim()); rowIssues.push('unknown_service'); }
                }
                if (qtyIdx !== -1) {
                  const q = row[qtyIdx];
                  const qs = q == null ? '' : String(q).trim();
                  if (qs === '' || Number(qs) <= 0 || Number.isNaN(Number(qs))) {
                    counts.missing_qty += 1; pickSample('missing_qty', `row ${r + 1}`); rowIssues.push('missing_qty');
                  }
                }
                if (weightIdx !== -1) {
                  const w = row[weightIdx];
                  const ws2 = w == null ? '' : String(w).trim();
                  if (ws2 === '' || Number(ws2) <= 0 || Number.isNaN(Number(ws2))) {
                    counts.missing_weight += 1; pickSample('missing_weight', `row ${r + 1}`); rowIssues.push('missing_weight');
                  }
                }
                if (rowIssues.length > 0) {
                  totalFlaggedRows += 1;
                  if (flaggedRows.length < FLAGGED_CAP) {
                    flaggedRows.push({
                      sheet: name,
                      row_number: r + 1,
                      issues: rowIssues,
                      invoice_no: invoiceIdx !== -1 ? String(row[invoiceIdx] ?? '').trim() : '',
                      client_code: clientIdxLocal !== -1 ? String(row[clientIdxLocal] ?? '').trim() : '',
                      tracking: t,
                      second_name: secondIdx !== -1 ? String(row[secondIdx] ?? '').trim() : '',
                      qty: qtyIdx !== -1 ? String(row[qtyIdx] ?? '').trim() : '',
                      customer: customerIdx !== -1 ? String(row[customerIdx] ?? '').trim() : '',
                      country: countryIdx !== -1 ? String(row[countryIdx] ?? '').trim() : '',
                      service: serviceIdx !== -1 ? String(row[serviceIdx] ?? '').trim() : '',
                      ok_parcel_dt: dtIdx !== -1 ? String(row[dtIdx] ?? '').trim() : '',
                      weight_gm: weightIdx !== -1 ? String(row[weightIdx] ?? '').trim() : '',
                    });
                  }
                }
              }
            }
            orderSheetStats.dataQuality = { counts, samples, totalFlaggedRows, flaggedRows, flaggedCap: FLAGGED_CAP };

            // ---- Duplicate-tracking scan (cross-sheet) ----
            // A tracking is only flagged as "duplicate" if the SAME tracking
            // appears more than once AND at least two of its occurrences have
            // a different customer or country (real collision, not just a
            // multi-product parcel from the raw packing list).
            const duplicateGroups = [];
            for (const [trk, entries] of trackingIndex.entries()) {
              if (entries.length < 2) continue;
              const signatures = new Set(
                entries.map((e) => `${(e.customer || '').toUpperCase()}|${(e.country || '').toUpperCase()}`)
              );
              if (signatures.size <= 1) continue; // multi-product parcel, not a collision
              duplicateGroups.push({ tracking: trk, occurrences: entries });
            }
            if (duplicateGroups.length > 0) {
              // Total rows touched across all collisions
              let dupRows = 0;
              duplicateGroups.forEach((g) => { dupRows += g.occurrences.length; });
              counts.duplicate_tracking = dupRows;
              // Add one sample per distinct colliding tracking
              for (const g of duplicateGroups.slice(0, 5)) {
                pickSample('duplicate_tracking', g.tracking);
              }
              totalFlaggedRows += dupRows;
              // Append a summary row per collision to the flagged CSV
              for (const g of duplicateGroups) {
                for (const occ of g.occurrences) {
                  if (flaggedRows.length >= FLAGGED_CAP) break;
                  flaggedRows.push({
                    sheet: occ.sheet,
                    row_number: occ.row,
                    issues: ['duplicate_tracking'],
                    invoice_no: '',
                    client_code: occ.client_code,
                    tracking: g.tracking,
                    second_name: '',
                    qty: '',
                    customer: occ.customer,
                    country: occ.country,
                    service: '',
                    ok_parcel_dt: '',
                    weight_gm: '',
                  });
                }
              }
              // Attach the raw collision list so the UI can render a detail
              // dialog without re-scanning the file.
              orderSheetStats.dataQuality.duplicateGroups = duplicateGroups.slice(0, 200);
              orderSheetStats.dataQuality.totalFlaggedRows = totalFlaggedRows;
            }
          }

          setFilePreview((prev) => ({ ...(prev || {}), headers, rowCount, sampleData: jsonData.slice(1, 4), foundColumns, optionalFound, orderSheetStats }));

          // ================================================================
          // MONTH-OVER-MONTH COMPARISON
          // Detect the target month/year from the filename first, then fall
          // back to scanning the first few `ok parcel dt` values in the
          // sheets. Then ask the backend how many parcels the previous month
          // had and render a comparison chip on the preview.
          // ================================================================
          const MONTHS = {
            JAN: 1, JANUARY: 1, FEB: 2, FEBRUARY: 2, MAR: 3, MARCH: 3,
            APR: 4, APRIL: 4, MAY: 5, JUN: 6, JUNE: 6, JUL: 7, JULY: 7,
            AUG: 8, AUGUST: 8, SEP: 9, SEPT: 9, SEPTEMBER: 9,
            OCT: 10, OCTOBER: 10, NOV: 11, NOVEMBER: 11, DEC: 12, DECEMBER: 12,
          };
          let detectedMonth = null;
          let detectedYear = null;
          const fnameUp = file.name.toUpperCase();
          // Accept any non-letter separator (or none) between month name and year.
          // Matches: "MARCH 2026", "march-2026", "March_2026", "march2026", "MAR26" etc.
          const fnameMatch = fnameUp.match(
            /(JANUARY|FEBRUARY|MARCH|APRIL|MAY|JUNE|JULY|AUGUST|SEPTEMBER|SEPT|OCTOBER|NOVEMBER|DECEMBER|JAN|FEB|MAR|APR|JUN|JUL|AUG|SEP|OCT|NOV|DEC)[^A-Z]{0,6}(20\d{2}|\d{2})(?!\d)/
          );
          if (fnameMatch) {
            detectedMonth = MONTHS[fnameMatch[1]] || null;
            const yrRaw = fnameMatch[2];
            detectedYear = yrRaw.length === 4 ? parseInt(yrRaw, 10) : 2000 + parseInt(yrRaw, 10);
          }
          // Fallback: scan parcel_dt values like "21-MAR-26-A004118"
          if (!detectedMonth || !detectedYear) {
            outer: for (const name of workbook.SheetNames) {
              const ws = workbook.Sheets[name];
              if (!ws) continue;
              const grid = XLSX.utils.sheet_to_json(ws, { header: 1, blankrows: false, defval: '' });
              let hdrRow = -1;
              for (let i = 0; i < Math.min(5, grid.length); i++) {
                const up = (grid[i] || []).map((v) => String(v || '').trim().toUpperCase());
                if (up.includes('TRACKING')) { hdrRow = i; break; }
              }
              if (hdrRow === -1) continue;
              const hdr = (grid[hdrRow] || []).map((v) => String(v || '').trim().toUpperCase());
              const dtIdx = hdr.findIndex((h) => h.includes('PARCEL') && (h.includes('DT') || h.includes('DATE')));
              if (dtIdx === -1) continue;
              for (let r = hdrRow + 1; r < Math.min(hdrRow + 80, grid.length); r++) {
                const v = grid[r]?.[dtIdx];
                if (!v) continue;
                const m = String(v).toUpperCase().match(/\b(\d{1,2})-([A-Z]{3,9})-(\d{2})/);
                if (m) {
                  detectedMonth = MONTHS[m[2]] || null;
                  detectedYear = 2000 + parseInt(m[3], 10);
                  if (detectedMonth) break outer;
                }
              }
            }
          }

          if (detectedMonth && detectedYear) {
            // Previous month
            const prevMonth = detectedMonth === 1 ? 12 : detectedMonth - 1;
            const prevYear = detectedMonth === 1 ? detectedYear - 1 : detectedYear;
            try {
              const tok = localStorage.getItem('token');
              const authHeaders = { Authorization: `Bearer ${tok}` };
              const [prevResp, currResp] = await Promise.all([
                fetch(
                  `${BACKEND_URL}/api/shipment-costing/parcel-count-by-month?month=${prevMonth}&year=${prevYear}`,
                  { headers: authHeaders }
                ),
                fetch(
                  `${BACKEND_URL}/api/shipment-costing/parcel-count-by-month?month=${detectedMonth}&year=${detectedYear}`,
                  { headers: authHeaders }
                ),
              ]);
              const prevBody = prevResp.ok ? await prevResp.json() : null;
              const currBody = currResp.ok ? await currResp.json() : null;
              const prevParcels = prevBody?.parcels || 0;
              const current = orderSheetStats.uniqueParcels;
              let pct = null;
              if (prevParcels > 0) {
                pct = ((current - prevParcels) / prevParcels) * 100;
              }
              const comparison = {
                targetMonth: detectedMonth,
                targetYear: detectedYear,
                prevMonth,
                prevYear,
                prevParcels,
                prevErrors: prevBody?.errors || 0,
                currentParcels: current,
                pct,
                existingInTargetMonth: currBody?.parcels || 0,
                existingErrorsInTargetMonth: currBody?.errors || 0,
              };
              setFilePreview((prev) => ({
                ...(prev || {}),
                orderSheetStats: { ...(prev?.orderSheetStats || orderSheetStats), comparison },
              }));
            } catch (compErr) {
              console.warn('Month comparison fetch failed:', compErr);
            }
          }
        } catch (e) {
          // Non-fatal - upload still proceeds with the basic preview, but
          // surface the reason so users know why parcel/tracking stats and the
          // Data-Quality panel are missing (most common cause: Excel parser
          // choking on a malformed sheet — not something we should hide).
          const msg = (e && (e.message || String(e))) || 'unknown error';
          console.warn('Order-sheet preview failed:', e);
          setFilePreview((prev) => ({
            ...(prev || {}),
            orderSheetStatsError: msg,
          }));
          setFrontendErrors((prev) => {
            const entry = { msg: `Validation scan: ${msg}`.slice(0, 400), at: new Date().toISOString() };
            if (prev.some((x) => x.msg === entry.msg)) return prev;
            return [...prev, entry];
          });
        }
      }

      if (missingColumns.length > 0) {
        setValidationResult({
          valid: false,
          error: "Missing required columns",
          missingColumns,
          foundColumns: Object.keys(foundColumns),
          details: `Your file is missing: ${missingColumns.join(", ")}. Please check the template for correct column names.`
        });
        return false;
      }
      
      setValidationResult({
        valid: true,
        foundColumns: Object.keys(foundColumns),
        optionalFound,
        rowCount
      });
      return true;
      
    } catch (error) {
      console.error("Validation error:", error);
      setValidationResult({
        valid: false,
        error: "Error checking file",
        details: error.message || "The file could not be parsed. Please ensure it's a valid Excel file."
      });
      return false;
    } finally {
      setValidating(false);
    }
  };

  // Record upload activity in database for history tracking.
  // When `fileObj` (the original File) is provided, the raw bytes are stored
  // server-side too — so operators can View/Download the original sheet from
  // Upload History for 30 days (after which the bytes auto-purge).
  const recordUploadActivity = async (fileName, uploadType, module, status, details = {}, fileObj = null) => {
    try {
      if (fileObj instanceof Blob) {
        const fd = new FormData();
        fd.append('file', fileObj, fileName || fileObj.name || 'upload.xlsx');
        fd.append('upload_type', uploadType);
        fd.append('module', module);
        fd.append('status', status);
        let detailsJson = '{}';
        try { detailsJson = JSON.stringify(details || {}); } catch (e) { /* circular refs — skip */ }
        fd.append('details', detailsJson);
        await fetch(`${BACKEND_URL}/api/upload-history/store-file`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: fd,
        });
        return;
      }
      await fetch(`${BACKEND_URL}/api/activity-logs/record-upload`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          file_name: fileName,
          upload_type: uploadType,
          module: module,
          status: status,
          details: details
        })
      });
    } catch (error) {
      console.error("Failed to record upload activity:", error);
      // Don't show error to user - this is just logging
    }
  };

  // Open the side-drawer with the parsed contents of a stored upload
  const openHistoryView = async (storeId) => {
    setHistoryViewOpen(true);
    setHistoryViewLoading(true);
    setHistoryView(null);
    setHistoryViewError(null);
    setHistoryViewSheetIdx(0);
    try {
      const r = await fetch(`${BACKEND_URL}/api/upload-history/${storeId}/view`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || 'Failed to load preview');
      setHistoryView(data);
    } catch (e) {
      setHistoryViewError(e.message);
    } finally {
      setHistoryViewLoading(false);
    }
  };

  // Download the original uploaded file (raw bytes)
  const downloadHistoryFile = async (storeId, fileName) => {
    setDownloadingStoreId(storeId);
    try {
      const r = await fetch(`${BACKEND_URL}/api/upload-history/${storeId}/download`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) {
        const data = await r.json().catch(() => ({}));
        throw new Error(data.detail || `Download failed (${r.status})`);
      }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName || 'upload.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setDownloadingStoreId(null);
    }
  };

  // Re-process an old upload through the latest parser (mainly to backfill
  // correct order-dates on rows that landed on today's date before the
  // 2026-07-04 date-fallback fix).
  const [reprocessingId, setReprocessingId] = useState(null);
  const [bulkReprocessOpen, setBulkReprocessOpen] = useState(false);
  const [bulkReprocessing, setBulkReprocessing] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const [bulkMonth, setBulkMonth] = useState(new Date().getMonth() + 1);
  const [bulkYear, setBulkYear] = useState(new Date().getFullYear());
  const [backfillingLedgers, setBackfillingLedgers] = useState(false);

  const handleBackfillLedgers = async () => {
    if (!window.confirm(
      "Scan all existing orders/sales and auto-create ledgers for any missing client codes?\n\n" +
      "New ledgers get default role=Partner, ledger_type=debtor, everything else blank — editable later."
    )) return;
    setBackfillingLedgers(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/backfill-ledgers`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.detail || `Backfill failed (${r.status})`);
      if (data.created_count > 0) {
        toast.success(`Created ${data.created_count} new ledger(s): ${(data.created || []).slice(0, 6).join(", ")}${data.created.length > 6 ? "…" : ""}`);
      } else {
        toast.info(`Scanned ${data.scanned_codes} codes — all already have ledgers.`);
      }
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBackfillingLedgers(false);
    }
  };
  const reprocessHistoryUpload = async (storeId, fileName) => {
    if (!window.confirm(`Re-run "${fileName}" through the latest parser?\n\nThis creates a NEW upload with the up-to-date parse (original stays intact).`)) return;
    setReprocessingId(storeId);
    try {
      const r = await fetch(`${BACKEND_URL}/api/upload-history/${storeId}/reprocess`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.detail || `Re-process failed (${r.status})`);
      toast.success(`Re-processed as new upload ${(data.new_upload_id || "").slice(0, 8)}…`);
      fetchUploadHistory();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setReprocessingId(null);
    }
  };

  const runBulkReprocess = async () => {
    setBulkReprocessing(true);
    setBulkResult(null);
    try {
      const r = await fetch(`${BACKEND_URL}/api/upload-history/reprocess-bulk`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ module: "partner_ledger", month: bulkMonth, year: bulkYear }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.detail || `Bulk re-process failed (${r.status})`);
      setBulkResult(data);
      if (data.processed > 0) {
        toast.success(`Re-processed ${data.processed}/${data.total_matched || 0} uploads`);
        fetchUploadHistory();
      } else {
        toast.info(data.message || "No uploads found in that range");
      }
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBulkReprocessing(false);
    }
  };

  // Apply packing list data to existing orders (fetch from staging + recalculate)
  const handleApplyPackingData = async () => {
    setApplyingPacking(true);
    try {
      // 1. Fetch packing data from staging
      const stagingRes = await fetch(`${BACKEND_URL}/api/shipment-costing/get-packing-list`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!stagingRes.ok) throw new Error("Failed to fetch packing data");
      const stagingData = await stagingRes.json();
      
      if (!stagingData.has_packing_list || !stagingData.packing_data || Object.keys(stagingData.packing_data).length === 0) {
        toast.error("No packing data found in staging");
        setShowWeightDialog(false);
        return;
      }

      toast.info(`Applying packing data (${Object.keys(stagingData.packing_data).length} parcels) with ${weightSource === 'packing_list' ? 'actual' : 'price list'} weight...`);

      // 2. Recalculate in BACKGROUND (prod-scale sheets take minutes — a
      // blocking request hits HTTP timeouts). Poll job progress instead.
      const jobId = (crypto.randomUUID && crypto.randomUUID()) || `pk-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      const recalcRes = await fetch(`${BACKEND_URL}/api/shipment-costing/recalculate-with-packing`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ packing_data: stagingData.packing_data, weight_source: weightSource, job_id: jobId })
      });
      const recalcData = await recalcRes.json();
      if (!recalcRes.ok || recalcData.status !== "started") {
        toast.error(recalcData.detail || "Failed to start recalculation");
        return;
      }

      setShowWeightDialog(false);
      toast.loading("Recalculating parcels in background...", { id: "packing-recalc" });

      const started = Date.now();
      const poll = async () => {
        if (Date.now() - started > 20 * 60 * 1000) {
          toast.warning("Recalculation is taking unusually long — check back in a few minutes.", { id: "packing-recalc" });
          return;
        }
        try {
          const pr = await fetch(`${BACKEND_URL}/api/shipment-costing/recalculate-all/progress/${jobId}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          const pd = await pr.json();
          if (pd && pd.done) {
            if (pd.phase === "error") {
              toast.error(`Recalculation failed: ${pd.error || "unknown error"}`, { id: "packing-recalc" });
            } else {
              let msg = pd.message || "Packing data applied";
              if (pd.weight_updates > 0) msg += ` | Weight updated: ${pd.weight_updates}`;
              if (pd.product_updates > 0) msg += ` | Products synced: ${pd.product_updates}`;
              toast.success(msg, { id: "packing-recalc", duration: 8000 });
              // Cleanup errors after successful recalc
              fetch(`${BACKEND_URL}/api/shipment-costing/cleanup-errors`, {
                method: "POST",
                headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` }
              }).catch(() => {});
            }
            return;
          }
          if (pd && pd.total > 0) {
            toast.loading(`Recalculating... ${pd.processed || 0}/${pd.total} parcels`, { id: "packing-recalc" });
          }
        } catch {
          // transient poll error — keep polling
        }
        setTimeout(poll, 2500);
      };
      setTimeout(poll, 2500);
    } catch (e) {
      toast.error("Failed to apply packing data: " + e.message);
    } finally {
      setApplyingPacking(false);
      setShowWeightDialog(false);
    }
  };

  // Fetch upload history
  const fetchUploadHistory = async () => {
    try {
      setLoadingHistory(true);
      const response = await fetch(`${BACKEND_URL}/api/activity-logs/file-uploads`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setUploadHistory(data.uploads || []);
      }
    } catch (error) {
      console.error("Error fetching upload history:", error);
    } finally {
      setLoadingHistory(false);
    }
  };

  useEffect(() => {
    if (activeTab === "history") {
      fetchUploadHistory();
    }
  }, [activeTab]);

  // Handle file selection
  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setUploadResult(null);
      // Validate file columns
      if (selectedUpload) {
        await validateFileColumns(file, selectedUpload.id);
      }
    }
  };

  // Handle drag and drop
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    const file = e.dataTransfer.files?.[0];
    if (file && (file.name.endsWith('.xlsx') || file.name.endsWith('.xls'))) {
      setSelectedFile(file);
      setUploadResult(null);
      // Validate file columns
      if (selectedUpload) {
        await validateFileColumns(file, selectedUpload.id);
      }
    } else {
      toast.error("Please upload an Excel file (.xlsx or .xls)");
    }
  };

  // Download template
  // Apply a corrections CSV (previously downloaded from the Data Quality
  // panel and edited offline) to the currently-selected Combined Database
  // Sheet. Matches rows by (tracking) — falls back to (sheet + row_number)
  // when tracking is missing. Builds a patched in-memory XLSX so the real
  // upload sends the fixes. Does NOT mutate the original file on disk.
  const applyCsvCorrections = async (csvFile) => {
    try {
      if (!selectedFile || !selectedFileData) {
        toast.error("Select the Combined Database Sheet first");
        return;
      }
      const csvText = await csvFile.text();
      // Minimal CSV parser supporting quoted fields + double-quote escapes.
      const parseCsv = (txt) => {
        const rows = [];
        let cur = [];
        let cell = '';
        let inQuotes = false;
        for (let i = 0; i < txt.length; i++) {
          const ch = txt[i];
          if (inQuotes) {
            if (ch === '"' && txt[i + 1] === '"') { cell += '"'; i += 1; }
            else if (ch === '"') { inQuotes = false; }
            else { cell += ch; }
          } else {
            if (ch === '"') { inQuotes = true; }
            else if (ch === ',') { cur.push(cell); cell = ''; }
            else if (ch === '\n') { cur.push(cell); rows.push(cur); cur = []; cell = ''; }
            else if (ch === '\r') { /* skip */ }
            else { cell += ch; }
          }
        }
        if (cell !== '' || cur.length) { cur.push(cell); rows.push(cur); }
        return rows;
      };
      const rows = parseCsv(csvText.replace(/^\ufeff/, ''));
      if (rows.length < 2) {
        toast.error("CSV has no data rows");
        return;
      }
      const header = rows[0].map((h) => String(h || '').trim().toLowerCase());
      const col = (name) => header.indexOf(name);
      const idx = {
        row_number: col('row_number'),
        sheet: col('sheet'),
        tracking: col('tracking'),
        client_code: col('client_code'),
        second_name: col('2nd_name'),
        qty: col('qty'),
        customer: col('customer_name'),
        country: col('country'),
        service: col('service'),
        ok_parcel_dt: col('ok_parcel_dt'),
        weight_gm: col('weight_gm'),
      };
      if (idx.tracking === -1 && (idx.sheet === -1 || idx.row_number === -1)) {
        toast.error("CSV must contain a 'tracking' column (or 'sheet' + 'row_number')");
        return;
      }

      // Build correction map keyed by tracking (primary) and sheet::row (secondary)
      const byTracking = new Map();
      const byPosition = new Map();
      for (let i = 1; i < rows.length; i++) {
        const r = rows[i];
        if (!r || r.every((v) => (v || '') === '')) continue;
        const patch = {};
        if (idx.client_code !== -1) patch.client_code = r[idx.client_code];
        if (idx.second_name !== -1) patch.second_name = r[idx.second_name];
        if (idx.qty !== -1) patch.qty = r[idx.qty];
        if (idx.customer !== -1) patch.customer = r[idx.customer];
        if (idx.country !== -1) patch.country = r[idx.country];
        if (idx.service !== -1) patch.service = r[idx.service];
        if (idx.ok_parcel_dt !== -1) patch.ok_parcel_dt = r[idx.ok_parcel_dt];
        if (idx.weight_gm !== -1) patch.weight_gm = r[idx.weight_gm];
        const t = idx.tracking !== -1 ? String(r[idx.tracking] || '').trim().toUpperCase() : '';
        if (t) byTracking.set(t, patch);
        const sh = idx.sheet !== -1 ? String(r[idx.sheet] || '').trim() : '';
        const rn = idx.row_number !== -1 ? String(r[idx.row_number] || '').trim() : '';
        if (sh && rn) byPosition.set(`${sh}::${rn}`, patch);
      }

      // Re-parse the original workbook and apply patches directly on each sheet.
      const wb = XLSX.read(new Uint8Array(selectedFileData), { type: 'array' });
      let patched = 0;
      const unmatchedTrackings = new Set(byTracking.keys());
      for (const sheetName of wb.SheetNames) {
        const ws = wb.Sheets[sheetName];
        if (!ws) continue;
        const grid = XLSX.utils.sheet_to_json(ws, { header: 1, blankrows: false, defval: '' });
        if (!grid.length) continue;
        // Locate header
        let hdrRow = -1;
        for (let i = 0; i < Math.min(5, grid.length); i++) {
          const up = (grid[i] || []).map((v) => String(v || '').trim().toUpperCase());
          if (up.includes('TRACKING')) { hdrRow = i; break; }
        }
        if (hdrRow === -1) continue;
        const hdr = (grid[hdrRow] || []).map((v) => String(v || '').trim().toUpperCase());
        const findCol = (names) => {
          for (const n of names) {
            const i = hdr.indexOf(n);
            if (i !== -1) return i;
          }
          return -1;
        };
        const cols = {
          tracking: hdr.findIndex((h) => h.includes('TRACKING') && !h.includes('OLD')),
          client_code: findCol(['CLIENT CODE', 'CLIENT']),
          second_name: findCol(['2ND NAME', 'SECOND NAME', 'PRODUCT', 'MEDICINE']),
          qty: findCol(['QTY', 'QUANTITY']),
          customer: findCol(['CUSTOMER NAME', 'CUSTOMER', 'NAME']),
          country: findCol(['COUNTRY']),
          service: findCol(['SERVICE']),
          ok_parcel_dt: findCol(['OK PARCEL DT', 'OK PARCEL DATE', 'PARCEL DT', 'PARCEL DATE']),
          weight_gm: findCol(['WEIGHT (GM)', 'WEIGHT', 'WT (GM)', 'WT']),
        };
        for (let r = hdrRow + 1; r < grid.length; r++) {
          const row = grid[r] || [];
          const tUp = cols.tracking !== -1
            ? String(row[cols.tracking] || '').trim().toUpperCase()
            : '';
          let patch = null;
          if (tUp && byTracking.has(tUp)) {
            patch = byTracking.get(tUp);
            unmatchedTrackings.delete(tUp);
          } else {
            const posKey = `${sheetName}::${r + 1}`;
            if (byPosition.has(posKey)) patch = byPosition.get(posKey);
          }
          if (!patch) continue;
          // Apply patches directly to worksheet cells (source of truth for XLSX.write)
          const applyCell = (colIdx, value) => {
            if (colIdx === -1 || value === undefined) return;
            const addr = XLSX.utils.encode_cell({ r, c: colIdx });
            ws[addr] = { t: 's', v: value == null ? '' : String(value) };
          };
          applyCell(cols.client_code, patch.client_code);
          applyCell(cols.second_name, patch.second_name);
          applyCell(cols.qty, patch.qty);
          applyCell(cols.customer, patch.customer);
          applyCell(cols.country, patch.country);
          applyCell(cols.service, patch.service);
          applyCell(cols.ok_parcel_dt, patch.ok_parcel_dt);
          applyCell(cols.weight_gm, patch.weight_gm);
          patched += 1;
        }
      }

      // Serialize the patched workbook
      const wbout = XLSX.write(wb, { type: 'array', bookType: 'xlsx' });
      setPatchedFileData(wbout);

      // Re-run the Data Quality scan on the patched workbook so the counts refresh
      const patchedFile = new File([wbout], selectedFile.name, { type: selectedFile.type });
      try {
        await validateFileColumns(patchedFile, selectedUpload?.id || 'combined_order_sheet');
        // Keep the patched bytes after re-validation (validateFileColumns reset them earlier)
        setPatchedFileData(wbout);
      } catch (_e) {
        // non-fatal — keep the patched bytes even if rescan failed
      }

      setCorrectionsSummary({
        total_corrections: byTracking.size + byPosition.size,
        patched,
        unmatched: unmatchedTrackings.size,
      });
      toast.success(`Applied ${patched.toLocaleString()} corrections${unmatchedTrackings.size ? ` · ${unmatchedTrackings.size} unmatched` : ''}`);
    } catch (e) {
      console.error('Apply corrections failed:', e);
      toast.error('Failed to apply corrections: ' + (e?.message || 'unknown error'));
    }
  };

  // Download flagged rows (from client-side data-quality scan) as CSV so
  // the uploader can fix them offline and re-upload.
  const downloadFlaggedRowsCsv = () => {
    const dq = filePreview?.orderSheetStats?.dataQuality;
    const rows = dq?.flaggedRows || [];
    if (!rows.length) {
      toast.info("No flagged rows to download");
      return;
    }
    const headers = [
      "row_number", "sheet", "issues",
      "invoice_no", "client_code", "tracking", "2nd_name", "qty",
      "customer_name", "country", "service", "ok_parcel_dt", "weight_gm",
    ];
    const escape = (v) => {
      const s = v == null ? "" : String(v);
      if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
      return s;
    };
    const lines = [headers.join(",")];
    for (const r of rows) {
      lines.push([
        r.row_number, r.sheet, (r.issues || []).join(";"),
        r.invoice_no, r.client_code, r.tracking, r.second_name, r.qty,
        r.customer, r.country, r.service, r.ok_parcel_dt, r.weight_gm,
      ].map(escape).join(","));
    }
    const csv = lines.join("\n");
    // Prepend BOM so Excel opens UTF-8 correctly
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    const base = (selectedFile?.name || "combined_sheet").replace(/\.[^.]+$/, "");
    link.href = url;
    link.download = `${base}_flagged_rows.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success(`Downloaded ${rows.length.toLocaleString()} flagged rows`);
  };


  const handleDownloadTemplate = async (upload, endpointOverride = null) => {
    try {
      const ep = endpointOverride || upload.templateEndpoint;
      const response = await fetch(`${BACKEND_URL}${ep}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${upload.id}_template.xlsx`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        toast.success("Template downloaded");
      } else {
        toast.error("Failed to download template");
      }
    } catch (error) {
      console.error("Download error:", error);
      toast.error("Failed to download template");
    }
  };

  // Upload file
  // Poll background task status for Upload Center
  const pollUploadCenterTask = async (taskId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/background-tasks/${taskId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!response.ok) {
        throw new Error("Failed to check task status");
      }
      
      const taskData = await response.json();
      
      setUploadProgress(taskData.progress || 0);
      setProgressMessage(taskData.progress_message || "Processing...");
      
      if (taskData.status === "completed") {
        setBackgroundTaskId(null);
        setUploadProgress(100);
        
        const result = taskData.result || {};
        
        // If this is an order sheet (regular or combined) — check for duplicate resolution first
        if ((selectedUpload?.id === 'order_sheet' || selectedUpload?.id === 'combined_order_sheet') && result.orders && result.orders.length > 0) {
          
          // CHECK FOR DUPLICATE RESOLUTION REQUIRED
          if (result.requires_duplicate_resolution && result.duplicate_info) {
            const dupInfo = result.duplicate_info;
            const smartBreakdown = dupInfo.smart_breakdown || {};
            
            // Show duplicate dialog with smart breakdown
            setDuplicateInfo({
              count: dupInfo.count || 0,
              trackings: dupInfo.trackings || [],
              unassigned_count: smartBreakdown.unassigned_count || 0,
              error_count: smartBreakdown.error_count || 0,
              clean_count: smartBreakdown.clean_count || 0,
              problem_count: smartBreakdown.problem_count || 0,
              problem_trackings: smartBreakdown.problem_trackings || []
            });
            setPendingOrders(result.orders);
            setPendingStagingId(result.staging_id || null);
            setShowDuplicateDialog(true);
            setUploading(false);
            
            toast.info(`Found ${dupInfo.count} duplicate tracking numbers. Please choose how to proceed.`);
            return;
          }
          
          // No duplicates - auto-saved
          if (result.auto_saved) {
            setUploadResult({ success: true, message: result.message, details: result });
            setUploadSummaryResult(result);
            setUploading(false);
            
            // Show success with details
            if (result.skipped_duplicates > 0) {
              toast.success(`${result.saved_count} orders saved (${result.skipped_duplicates} duplicates skipped)`);
            } else {
              toast.success(`${result.saved_count || result.orders?.length || 0} orders saved`);
            }
            return;
          }
          
          // Fallback: old behavior if auto_saved not present
          setProgressMessage("Saving orders to database...");
          try {
            // Use staging_id if available (for large files), otherwise send orders directly
            const saveBody = result.staging_id
              ? { staging_id: result.staging_id, save_mode: 'skip_duplicates' }
              : { orders: result.orders, save_mode: 'skip_duplicates' };
            const saveResponse = await fetch(`${BACKEND_URL}/api/shipment-costing/save`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
              body: JSON.stringify(saveBody)
            });
            const saveData = await saveResponse.json();
            if (saveResponse.ok) {
              toast.success(`${saveData.orders_saved} orders saved`);
              setUploadResult({ success: true, message: `${saveData.orders_saved} orders saved`, details: result });
            } else {
              toast.warning(saveData.detail || 'Save had issues');
              setUploadResult({ success: true, message: result.message, details: result });
            }
            await recordUploadActivity(selectedFile?.name || '', 'order_sheet', 'shipment_costing', 'success', { orders: result.orders.length }, selectedFile);
          } catch (saveError) {
            console.error("Save error:", saveError);
            toast.error("Orders processed but failed to save: " + saveError.message);
            setUploadResult({ success: false, message: saveError.message, details: result });
          }
          setUploading(false);
          return;
        }
        
        // Default completion handler (non-order-sheet)
        setUploadResult({
          success: true,
          message: result.message || 'Upload successful',
          details: result,
        });
        toast.success(result.message || 'Upload successful');
        
        // Record upload activity for background task completion
        if (selectedFile && selectedUpload) {
          await recordUploadActivity(
            selectedFile.name,
            selectedUpload.id,
            selectedCategory?.id || 'upload_center',
            'success',
            result,
            selectedFile
          );
        }
        setUploading(false);
        return;
      } else if (taskData.status === "failed") {
        setBackgroundTaskId(null);
        setUploading(false);
        
        setUploadResult({
          success: false,
          message: taskData.error || 'Processing failed',
        });
        toast.error(taskData.error || 'Processing failed');
        return;
      }
      
      // Still processing - continue polling
      setTimeout(() => pollUploadCenterTask(taskId), 3000);
      
    } catch (error) {
      console.error("Error polling task:", error);
      setBackgroundTaskId(null);
      setUploading(false);
      toast.error("Error checking upload status");
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !selectedUpload) return;
    
    // Check if client selection is required but not selected
    if (selectedUpload.requiresClient && !selectedClient) {
      toast.error("Please select a client first");
      return;
    }
    
    setUploading(true);
    setUploadProgress(0);
    setUploadResult(null);
    setProgressMessage("Uploading file...");
    
    // ============ SPECIAL HANDLING: Packing List - Server-side async processing ============
    if (selectedUpload.id === 'packing_list') {
      try {
        setProgressMessage("Uploading packing list to server...");
        
        // Create FormData for file upload
        const formData = new FormData();
        
        // Use stored file data to create a fresh File object
        let fileToUpload;
        if (selectedFileData) {
          fileToUpload = new File([selectedFileData], selectedFile.name, {
            type: selectedFile.type || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          });
        } else {
          fileToUpload = selectedFile;
        }
        formData.append('file', fileToUpload);
        
        // Use async endpoint for background processing (handles large files)
        const response = await fetch(`${BACKEND_URL}/api/shipment-costing/upload-packing-async`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`
          },
          body: formData
        });
        
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.detail || `Upload failed with status ${response.status}`);
        }
        
        const data = await response.json();
        
        // If we got a task_id, poll for completion
        if (data.task_id) {
          setProgressMessage("Processing packing list in background...");
          setUploadProgress(20);
          
          // Poll for task completion
          const pollInterval = setInterval(async () => {
            try {
              const statusResponse = await fetch(`${BACKEND_URL}/api/background-tasks/${data.task_id}`, {
                headers: { Authorization: `Bearer ${token}` }
              });
              
              if (!statusResponse.ok) {
                console.warn("Status check failed, will retry...");
                return;
              }
              
              const statusData = await statusResponse.json();
              
              if (statusData.progress) {
                setUploadProgress(statusData.progress);
              }
              if (statusData.progress_message) {
                setProgressMessage(statusData.progress_message);
              }
              
              if (statusData.status === 'completed') {
                clearInterval(pollInterval);
                const result = statusData.result || {};
                
                setUploadProgress(100);
                setUploadResult({
                  success: result.success !== false,
                  message: result.message || `Packing list processed: ${result.total_records || 0} parcels`,
                  details: result.details || { total: result.total_records || 0 },
                  isPackingList: true
                });
                
                if (result.success !== false) {
                  toast.success(`Packing list saved: ${result.total_records || 0} parcels. Choose weight source to apply.`);
                  
                  // Show weight source dialog to apply packing data to orders
                  setShowWeightDialog(true);
                  
                  // Record upload activity
                  await recordUploadActivity(
                    selectedFile.name,
                    'packing_list',
                    'shipment_costing',
                    'success',
                    { parcels: result.total_records, sheets_processed: result.sheets_processed },
                    selectedFile
                  );
                } else {
                  toast.warning(result.message || "Packing list processing completed with warnings");
                }
                
                setUploading(false);
              } else if (statusData.status === 'failed') {
                clearInterval(pollInterval);
                setUploadResult({
                  success: false,
                  message: statusData.error || 'Packing list processing failed'
                });
                toast.error("Packing list processing failed: " + (statusData.error || 'Unknown error'));
                setUploading(false);
              }
            } catch (pollError) {
              console.warn("Poll error:", pollError);
              // Don't stop polling on transient errors
            }
          }, 2000);  // Poll every 2 seconds
          
          // Timeout after 5 minutes
          setTimeout(() => {
            clearInterval(pollInterval);
            if (uploading) {
              setUploadResult({
                success: false,
                message: 'Processing timed out. Please check the status later.'
              });
              toast.error("Packing list processing timed out");
              setUploading(false);
            }
          }, 5 * 60 * 1000);
          
          return; // Exit - polling will handle the rest
        }
        
        // Direct response (no task_id) - shouldn't happen with async endpoint
        setUploadProgress(100);
        setUploadResult({
          success: data.success !== false,
          message: data.message || `Packing list processed`,
          details: data.details || {},
          isPackingList: true
        });
        
        if (data.success !== false) {
          toast.success(data.message || "Packing list saved successfully");
        }
        
      } catch (error) {
        console.error("Packing list upload error:", error);
        setUploadResult({
          success: false,
          message: error.message || 'Failed to upload packing list'
        });
        toast.error("Failed to upload packing list: " + error.message);
      } finally {
        if (!uploading) return; // Already handled by polling
        setUploading(false);
      }
      return;
    }
    // ============ END SPECIAL HANDLING: Packing List ============
    
    // ============ OLD CLIENT-SIDE PACKING LIST PARSING (REMOVED - now using server-side) ============
    /*
    if (selectedUpload.id === 'packing_list_OLD_CLIENT_SIDE') {
      try {
        setProgressMessage("Parsing packing list...");
        
        const arrayBuffer = await selectedFile.arrayBuffer();
        const workbook = XLSX.read(arrayBuffer, { type: 'array' });
        
        const packingData = {};
        let totalRecords = 0;
        let totalSheets = 0;
        
        // Process each sheet (service-wise)
        workbook.SheetNames.forEach(sheetName => {
          const worksheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: '' });
          
          // Find header row (look for TRACK NO. or similar)
          let headerRow = -1;
          let colMapping = {};
          
          for (let i = 0; i < Math.min(20, jsonData.length); i++) {
            const row = jsonData[i];
            if (!row) continue;
            
            for (let j = 0; j < row.length; j++) {
              const cellValue = String(row[j] || '').toLowerCase().trim();
              if (cellValue.includes('track') && (cellValue.includes('no') || cellValue.includes('number'))) {
                colMapping.tracking = j;
                headerRow = i;
              }
              if (cellValue === 'product' || cellValue.includes('product name')) {
                colMapping.product = j;
              }
              if ((cellValue === 'weight' || cellValue.includes('wt') || cellValue === 'wght') && 
                  !cellValue.includes('amt') && !cellValue.includes('amount')) {
                colMapping.weight = j;
              }
              if (cellValue === 'qty' || cellValue === 'quantity') {
                colMapping.qty = j;
              }
              if (cellValue.includes('customer') || (cellValue.includes('name') && !cellValue.includes('product') && !cellValue.includes('generic'))) {
                if (!colMapping.customer) colMapping.customer = j;
              }
            }
            if (headerRow >= 0) break;
          }
          
          if (headerRow < 0 || colMapping.tracking === undefined) {
            console.log(`Skipping sheet ${sheetName}: No tracking column found`);
            return;
          }
          
          totalSheets++;
          
          // Determine service from sheet name
          let service = 'UNKNOWN';
          const sheetNameUpper = sheetName.toUpperCase();
          if (sheetNameUpper.includes('EMS')) service = 'EMS';
          else if (sheetNameUpper.includes('ITPS')) service = 'ITPS';
          else if (sheetNameUpper.includes('DHL')) service = 'DHL';
          else if (sheetNameUpper.includes('RMS')) service = 'RMS';
          else if (sheetNameUpper.includes('UPS')) service = 'UPS';
          else if (sheetNameUpper.includes('FEDEX')) service = 'FEDEX';
          
          // Process data rows
          let currentTracking = null;
          let currentRecord = null;
          
          for (let i = headerRow + 1; i < jsonData.length; i++) {
            const row = jsonData[i];
            if (!row || row.every(cell => !cell)) continue;
            
            const trackingValue = String(row[colMapping.tracking] || '').trim().toUpperCase();
            const productValue = String(row[colMapping.product] || '').trim();
            const weightValue = String(row[colMapping.weight !== undefined ? colMapping.weight : -1] || '').trim();
            const qtyValue = String(row[colMapping.qty !== undefined ? colMapping.qty : -1] || '').trim();
            const customerValue = String(row[colMapping.customer !== undefined ? colMapping.customer : -1] || '').trim();
            
            // Check if this is a new tracking number (alphanumeric pattern like EE123456789IN)
            const isNewTracking = trackingValue && /^[A-Z]{2}\d+[A-Z]{2}$/.test(trackingValue);
            
            if (isNewTracking) {
              // Save previous record if exists
              if (currentTracking && currentRecord) {
                packingData[currentTracking] = currentRecord;
                totalRecords++;
              }
              
              // Parse weight
              let weightGrams = 0;
              if (weightValue) {
                const cleanedWeight = weightValue.replace(/,/g, '');
                const weightMatch = cleanedWeight.match(/([\d.]+)\s*(gm|g|gram|grams|kg)?/i);
                if (weightMatch) {
                  weightGrams = parseFloat(weightMatch[1]);
                  if (weightMatch[2] && weightMatch[2].toLowerCase() === 'kg') {
                    weightGrams *= 1000;
                  }
                }
              }
              
              // If weight not found in weight column, scan all columns for weight pattern
              if (weightGrams === 0) {
                for (let scanCol = 0; scanCol < row.length; scanCol++) {
                  const cellVal = String(row[scanCol] || '').trim();
                  const cleanedCell = cellVal.replace(/,/g, '');
                  const weightPattern = cleanedCell.match(/^(\d{2,6})\s*(gm|g|gram|grams)?$/i);
                  if (weightPattern && parseFloat(weightPattern[1]) >= 10) {
                    weightGrams = parseFloat(weightPattern[1]);
                    break;
                  }
                }
              }
              
              currentTracking = trackingValue;
              currentRecord = {
                tracking: trackingValue,
                products: productValue ? [productValue] : [],
                weight: weightGrams,
                qty: parseInt(qtyValue) || 1,
                customer: customerValue,
                service: service,
                sheet: sheetName
              };
            } else if (currentRecord && productValue) {
              // Continuation row - add product
              const productLower = productValue.toLowerCase();
              const isValidProduct = productValue.length > 2 && 
                !productLower.includes('product') && 
                !productLower.includes('generic') && 
                !productLower.includes('quantity') &&
                !productLower.includes('weight') &&
                !/^\d+$/.test(productValue);
              
              if (isValidProduct) {
                currentRecord.products.push(productValue);
              }
            }
          }
          
          // Don't forget the last record
          if (currentTracking && currentRecord) {
            packingData[currentTracking] = currentRecord;
            totalRecords++;
          }
        });
        
        setUploadProgress(80);
        setProgressMessage("Saving packing list to server...");
        
        // Save packing list to backend for merging with order sheet
        try {
          const saveResponse = await fetch(`${BACKEND_URL}/api/shipment-costing/save-packing-list`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`
            },
            body: JSON.stringify({
              packing_data: packingData,
              file_name: selectedFile.name
            })
          });
          
          if (!saveResponse.ok) {
            throw new Error('Failed to save packing list to server');
          }
          
          const saveResult = await saveResponse.json();
          
          setUploadProgress(100);
          setUploadResult({
            success: true,
            message: `Packing list saved: ${totalRecords} parcels from ${totalSheets} sheets. Ready to merge with Order Sheet.`,
            details: {
              total: totalRecords,
              sheets_processed: totalSheets,
              total_sheets: workbook.SheetNames.length
            },
            isPackingList: true
          });
          toast.success(`Packing list saved: ${totalRecords} parcels. Now upload Order Sheet to merge and save.`);
          
          // Record upload activity for history
          await recordUploadActivity(
            selectedFile.name,
            'packing_list',
            'shipment_costing',
            'success',
            { parcels: totalRecords, sheets_processed: totalSheets }
          );
          
        } catch (saveError) {
          console.error("Failed to save packing list:", saveError);
          setUploadProgress(100);
          setUploadResult({
            success: true,
            message: `Packing list parsed (${totalRecords} parcels) but failed to save to server. Please try again.`,
            details: { total: totalRecords },
            isPackingList: true
          });
          toast.warning("Packing list parsed but not saved to server");
        }
        
      } catch (error) {
        console.error("Packing list parse error:", error);
        setUploadResult({
          success: false,
          message: error.message || 'Failed to parse packing list',
        });
        toast.error("Failed to parse packing list: " + error.message);
      } finally {
        setUploading(false);
      }
      return; // Exit early - don't proceed to backend upload
    }
    */
    // ============ END OLD CLIENT-SIDE PACKING LIST PARSING ============
    
    try {
      // Use stored file data to create a fresh File object (avoids "Body is disturbed" error)
      // If the user applied a CSV-corrections patch (Combined Database Sheet flow),
      // prefer the patched workbook bytes so the upload carries the fixes.
      let fileToUpload;
      const bytesForUpload = patchedFileData || selectedFileData;
      if (bytesForUpload) {
        // Create a new File from the stored ArrayBuffer
        fileToUpload = new File([bytesForUpload], selectedFile.name, {
          type: selectedFile.type || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });
      } else {
        // Fallback to original file (might cause "Body is disturbed" if already read)
        fileToUpload = selectedFile;
      }
      
      // Build the endpoint URL - replace {client_code} if present
      let endpoint = selectedUpload.endpoint;
      let useAsyncEndpoint = false;
      
      // Determine if we should use async processing
      const fileSizeMB = selectedFile.size / (1024 * 1024);
      const asyncEndpoints = {
        '/api/shipment-costing/process': '/api/shipment-costing/process-async',
      };
      
      // Always use async for order sheets (prevents Cloudflare timeout + gives progress updates)
      // Other endpoints: use async only for large files (>6MB)
      if (asyncEndpoints[endpoint]) {
        useAsyncEndpoint = true;
        endpoint = asyncEndpoints[endpoint];
      }
      // Endpoints that are ALREADY async (return task_id natively)
      if (endpoint === '/api/shipment-costing/process-combined-async') {
        useAsyncEndpoint = true;
      }
      
      if (selectedUpload.requiresClient && selectedClient) {
        // Special handling for partner_ledger_structured - rename sheet to partner code
        if (selectedUpload.id === 'partner_ledger_structured') {
          // Show progress: Preparing file
          setProgressMessage(`Preparing file for ${selectedClient.client_code}...`);
          setUploadProgress(5);
          
          // For partner ledger, we need to rename the sheet to the partner code
          // The backend parses sheet names as partner codes
          // Use stored file data to avoid "Body is disturbed" error
          const dataToUse = selectedFileData || await fileToUpload.arrayBuffer();
          const workbook = XLSX.read(dataToUse, { type: 'array' });
          
          // Show progress: Reading Excel
          setProgressMessage(`Reading Excel file (${workbook.SheetNames.length} sheet(s))...`);
          setUploadProgress(10);
          
          // Rename the first sheet to the selected partner code
          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];
          
          // Count rows for progress display
          const range = XLSX.utils.decode_range(worksheet['!ref'] || 'A1:A1');
          const rowCount = range.e.r - range.s.r;
          setProgressMessage(`Processing ${rowCount} rows for ${selectedClient.client_code}...`);
          setUploadProgress(15);
          
          // Delete old sheet and add with new name
          delete workbook.Sheets[firstSheetName];
          workbook.SheetNames[0] = selectedClient.client_code;
          workbook.Sheets[selectedClient.client_code] = worksheet;
          
          // Convert back to file and update fileToUpload for createFormData
          const wbout = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
          fileToUpload = new File([wbout], selectedFile.name, { 
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
          });
          
          setProgressMessage("Uploading to server...");
          setUploadProgress(20);
        } else {
          endpoint = endpoint.replace('{client_code}', encodeURIComponent(selectedClient.client_code));
          // Add replace_existing parameter for client price lists
          endpoint += `?replace_existing=${replaceExisting}`;
        }
      }
      
      // Use XMLHttpRequest for progress tracking with retry logic
      const maxRetries = 2;
      let lastError = null;

      // Live server-side parse-progress polling (Partner Ledger only)
      const isPartnerLedgerUpload = selectedUpload.id === 'partner_ledger_structured';
      const progressId = isPartnerLedgerUpload
        ? `ul_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`
        : "";
      let progressPollTimer = null;
      const startProgressPolling = () => {
        if (!progressId) return;
        const poll = async () => {
          try {
            const r = await fetch(`${BACKEND_URL}/api/partner-ledger/upload-progress/${progressId}`, {
              headers: { Authorization: `Bearer ${token}` },
            });
            if (!r.ok) return;
            const d = await r.json();
            if (!d?.found) return;
            const phase = d.phase || "";
            const rowsDone = d.rows_done || 0;
            const rowsTotal = d.rows_total || 0;
            const rawMatched = d.raw_matched || 0;
            const partner = d.partner || "";
            // Smooth progress bar climb based on parse phase.
            // 85 = bytes uploaded, 99 = server done, 100 = client-side "finalizing…"
            // parsing:            85 → 92% (linear on rows_done/rows_total)
            // matching_raw_funds: 92 → 98%
            // done / finalising:  99%
            const clamp = (n, lo, hi) => Math.min(hi, Math.max(lo, n));
            const ratio = rowsTotal > 0 ? clamp(rowsDone / rowsTotal, 0, 1) : 0;
            if (phase === "parsing") {
              setUploadProgress(Math.round(85 + ratio * 7));
            } else if (phase === "matching_raw_funds") {
              setUploadProgress(Math.round(92 + ratio * 6));
            } else if (phase === "done") {
              setUploadProgress(99);
            }
            let msg;
            if (phase === "parsing") {
              msg = `Parsing ${partner} · ${rowsDone.toLocaleString()}/${rowsTotal.toLocaleString()} rows`;
            } else if (phase === "matching_raw_funds") {
              msg = `Matching raw funds · ${rowsDone}/${rowsTotal} entries · ${rawMatched} matched`;
            } else if (phase === "reading_file") {
              msg = "Reading file…";
            } else if (phase === "done") {
              msg = "Finalizing…";
            } else if (phase === "error") {
              msg = `Error: ${d.error || "unknown"}`;
            }
            if (msg) setProgressMessage(msg);
          } catch { /* silent — best-effort */ }
        };
        progressPollTimer = setInterval(poll, 700);
      };
      const stopProgressPolling = () => {
        if (progressPollTimer) { clearInterval(progressPollTimer); progressPollTimer = null; }
      };

      // Function to create fresh FormData for each attempt (prevents "Body is disturbed" error)
      const createFormData = () => {
        const fd = new FormData();
        fd.append('file', fileToUpload);
        if (progressId) fd.append('progress_id', progressId);
        if (selectedUpload.params) {
          Object.entries(selectedUpload.params).forEach(([key, value]) => {
            fd.append(key, value);
          });
        }
        return fd;
      };
      
      const attemptUpload = (retryCount) => {
        return new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          const isPartnerLedger = selectedUpload.id === 'partner_ledger_structured';
          
          xhr.upload.addEventListener('progress', (e) => {
            if (e.lengthComputable) {
              // For partner ledger, scale upload progress to 20-80% (leaving room for processing)
              const uploadPercent = Math.round((e.loaded / e.total) * 100);
              const scaledProgress = isPartnerLedger 
                ? Math.round(20 + (uploadPercent * 0.6)) // 20% to 80%
                : uploadPercent;
              setUploadProgress(scaledProgress);
              
              if (uploadPercent < 100) {
                const sizeKB = Math.round(e.loaded / 1024);
                const totalKB = Math.round(e.total / 1024);
                setProgressMessage(
                  isPartnerLedger
                    ? `Uploading ledger data: ${sizeKB}KB / ${totalKB}KB${retryCount > 0 ? ` (retry ${retryCount})` : ''}`
                    : `Uploading: ${uploadPercent}%${retryCount > 0 ? ` (retry ${retryCount})` : ''}`
                );
              } else {
                setProgressMessage(
                  isPartnerLedger
                    ? "Analyzing entries and detecting payments..."
                    : "Processing file..."
                );
                if (isPartnerLedger) {
                  setUploadProgress(85);
                  // File bytes fully uploaded — start polling backend for the
                  // live "parsed X/Y rows · matched Z raw funds" status.
                  startProgressPolling();
                }
              }
            }
          });
          
          xhr.onload = () => {
            stopProgressPolling();
            if (xhr.status >= 200 && xhr.status < 300) {
              try {
                const data = JSON.parse(xhr.responseText);
                if (isPartnerLedger) {
                  setUploadProgress(95);
                  setProgressMessage(`Found ${data.total_entries_detected || 0} entries, finalizing...`);
                }
                resolve(data);
              } catch (e) {
                reject(new Error('Invalid server response - please try again'));
              }
            } else {
              try {
                const error = JSON.parse(xhr.responseText);
                let detail = error.detail;
                let userMessage;
                // Backend may return `detail` as a structured object (e.g.
                // {code: "duplicate_file", message: "...", existing_upload_id})
                // for the byte-identical-file guard. Extract the message and
                // expose the structured object so the caller can show a
                // friendlier dialog (e.g. "Force re-upload?").
                if (detail && typeof detail === "object") {
                  userMessage = detail.message || `Upload failed (${xhr.status})`;
                } else {
                  userMessage = detail || `Upload failed (${xhr.status})`;
                }
                const e = new Error(userMessage);
                e.status = xhr.status;
                e.detail = detail;
                reject(e);
              } catch (e) {
                reject(new Error(`Upload failed with status ${xhr.status}`));
              }
            }
          };
          
          xhr.onerror = () => {
            stopProgressPolling();
            reject(new Error('Network error - please check your internet connection and try again'));
          };
          
          xhr.ontimeout = () => {
            stopProgressPolling();
            reject(new Error('Upload timeout - the server took too long to respond'));
          };
          
          xhr.timeout = 300000; // 5 minutes timeout
          
          xhr.open('POST', `${BACKEND_URL}${endpoint}`);
          xhr.setRequestHeader('Authorization', `Bearer ${token}`);
          // Create fresh FormData for each attempt to avoid "Body is disturbed" error
          xhr.send(createFormData());
        });
      };
      
      // Try upload with retries
      let result = null;
      for (let retry = 0; retry <= maxRetries; retry++) {
        try {
          if (retry > 0) {
            setProgressMessage(`Retrying upload (attempt ${retry + 1}/${maxRetries + 1})...`);
            await new Promise(r => setTimeout(r, 1000)); // Wait 1 second before retry
          }
          result = await attemptUpload(retry);
          break; // Success, exit retry loop
        } catch (error) {
          lastError = error;
          console.error(`Upload attempt ${retry + 1} failed:`, error.message);
          // Don't retry on 409 duplicate_file — it's not a transient error
          // and re-trying wraps the structured `detail` away, breaking the
          // friendly "Re-upload anyway?" confirm dialog downstream.
          if (error?.status === 409 || error?.detail?.code === "duplicate_file") {
            throw error;
          }
          if (retry === maxRetries) {
            // Preserve detail/status when wrapping so outer catch can still
            // route duplicate_file / 4xx errors correctly.
            const wrapped = new Error(`${error.message}. Please try again or contact support if the issue persists.`);
            wrapped.status = error.status;
            wrapped.detail = error.detail;
            throw wrapped;
          }
        }
      }
      
      if (!result) {
        throw lastError || new Error('Upload failed - please try again');
      }
      
      // Handle async response - start polling
      if (useAsyncEndpoint && result.task_id) {
        setBackgroundTaskId(result.task_id);
        setProgressMessage("Processing file in background...");
        toast.info("File uploaded. Processing in background...");
        setTimeout(() => pollUploadCenterTask(result.task_id), 2000);
        return; // Don't set uploading=false - polling will do that
      }
      
      // ============ SPECIAL HANDLING: Order Sheet - Save to Database (same as Admin) ============
      if (selectedUpload.id === 'order_sheet' && result.orders) {
        setProgressMessage("Checking for packing list data...");
        
        try {
          // First, check if there's a packing list to merge
          let packingData = {};
          let packingMerged = false;
          
          try {
            const packingResponse = await fetch(`${BACKEND_URL}/api/shipment-costing/get-packing-list`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            
            if (packingResponse.ok) {
              const packingResult = await packingResponse.json();
              if (packingResult.has_packing_list && packingResult.packing_data) {
                packingData = packingResult.packing_data;
                toast.info(`Merging with packing list: ${packingResult.total_parcels} parcels from ${packingResult.file_name}`);
              }
            }
          } catch (packingError) {
            console.log("No packing list to merge:", packingError);
          }
          
          // Merge packing list data with orders (add weights)
          let ordersToSave = result.orders;
          if (Object.keys(packingData).length > 0) {
            setProgressMessage("Merging packing list with orders...");
            
            ordersToSave = result.orders.map(order => {
              const tracking = order.tracking?.toUpperCase();
              if (tracking && packingData[tracking]) {
                const packingInfo = packingData[tracking];
                packingMerged = true;
                return {
                  ...order,
                  packing_weight: packingInfo.weight || order.packing_weight || 0,
                  packing_service: packingInfo.service || order.service,
                  // Don't override product info, just add weight
                };
              }
              return order;
            });
            
            if (packingMerged) {
              toast.success("Packing list weights merged with orders");
            }
          }
          
          setProgressMessage("Checking for duplicates...");
          
          // Check for duplicates
          const checkDupResponse = await fetch(`${BACKEND_URL}/api/shipment-costing/check-duplicates`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`
            },
            body: JSON.stringify({ orders: ordersToSave })
          });
          
          let saveMode = "append";
          if (checkDupResponse.ok) {
            const dupData = await checkDupResponse.json();
            if (dupData.has_duplicates && dupData.duplicate_count > 0) {
              // Show dialog asking user to Skip, Replace, or Update Problems Only
              const smartBreakdown = dupData.smart_breakdown || {};
              setDuplicateInfo({
                count: dupData.duplicate_count,
                trackings: dupData.duplicate_trackings || [],
                // Smart breakdown for intelligent options
                unassigned_count: smartBreakdown.unassigned_count || 0,
                error_count: smartBreakdown.error_count || 0,
                clean_count: smartBreakdown.clean_count || 0,
                problem_count: smartBreakdown.problem_count || 0,
                problem_trackings: smartBreakdown.problem_trackings || []
              });
              setPendingOrders(ordersToSave);
              setPendingStagingId(result?.staging_id || null);
              setShowDuplicateDialog(true);
              setUploading(false);
              return; // Wait for user to choose
            }
          }
          
          // No duplicates found, proceed with save
          await saveFinalOrders(ordersToSave, "append", packingMerged, result);
          
        } catch (error) {
          console.error("Upload error:", error);
          toast.error("Upload failed: " + error.message);
          setUploading(false);
        }
      }
      
      // ============ HANDLE ALL OTHER UPLOAD TYPES (Partner Ledger, Shipping Rates, etc.) ============
      // For non-order-sheet uploads, display the result directly
      if (selectedUpload.id !== 'order_sheet') {
        // Set progress to 100% before showing result
        setUploadProgress(100);
        setProgressMessage("Complete!");
        
        // Build a success message based on the response
        let message = "Upload successful";
        let details = result;
        
        if (result.total_entries_detected !== undefined) {
          // Partner Ledger format - check for duplicates AND/OR errors first
          const errorEntries = [];
          result.partners?.forEach(partner => {
            partner.entries?.forEach(entry => {
              const status = String(entry.status || "").toLowerCase();
              // Anything that isn't a clean "matched" entry is something the
              // user usually wants to review/fix in their source sheet.
              if (status && status !== "matched" && status !== "duplicate") {
                errorEntries.push({ ...entry, partner_code: partner.partner_code });
              }
            });
          });
          // Top-level upload errors (header parse fail, missing sheet, etc.)
          (result.errors || []).forEach(e => {
            errorEntries.push({
              tracking: "",
              partner_code: e.partner_code || "",
              amount: 0,
              date_display: "",
              status: "upload_error",
              narration: typeof e === "string" ? e : (e.message || JSON.stringify(e)),
            });
          });
          // Unmatched tracking numbers (typo'd / not in shipment_orders)
          (result.unmatched_tracking || []).forEach(u => {
            errorEntries.push({
              tracking: typeof u === "string" ? u : (u.tracking || ""),
              partner_code: typeof u === "string" ? "" : (u.partner_code || ""),
              amount: typeof u === "string" ? 0 : (u.amount || 0),
              date_display: typeof u === "string" ? "" : (u.date || ""),
              status: "unmatched_tracking",
              narration: "Tracking not found in shipment orders",
            });
          });

          if ((result.has_duplicates && result.duplicates_count > 0) || errorEntries.length > 0) {
            // Collect all duplicate entries for display
            const duplicates = [];
            result.partners?.forEach(partner => {
              partner.entries?.forEach(entry => {
                if (entry.is_potential_duplicate) {
                  duplicates.push({
                    ...entry,
                    partner_code: partner.partner_code
                  });
                }
              });
            });

            // Show duplicate / error review dialog
            setDuplicateEntries(duplicates);
            setLedgerErrorEntries(errorEntries);
            setPendingLedgerResult(result);
            setDuplicateDialog(true);
            setUploading(false);
            return; // Wait for user to choose action
          }
          
          // No duplicates - proceed normally
          const payments = result.total_payments_detected || 0;
          const matched = result.partners?.reduce((acc, p) => acc + (p.tracking_matches || 0), 0) || 0;
          const unmatched = result.partners?.reduce((acc, p) => acc + (p.unmatched?.length || 0), 0) || 0;
          
          message = `✓ Parsed ${result.total_entries_detected} entries from ${result.sheets_processed} sheet(s)`;
          if (payments > 0) {
            message += `\n💰 ${payments} payments detected`;
          }
          if (matched > 0) {
            message += ` (${matched} matched)`;
          }
          
          // Add extra details for display
          details = {
            ...result,
            // Computed fields for UI display
            _summary: {
              entries: result.total_entries_detected,
              payments: payments,
              matched: matched,
              unmatched: unmatched,
              sheets: result.sheets_processed
            }
          };
        } else if (result.inserted !== undefined) {
          message = `${result.inserted} records inserted`;
          if (result.updated > 0) message += `, ${result.updated} updated`;
          if (result.skipped > 0) message += `, ${result.skipped} skipped`;
        } else if (result.message) {
          message = result.message;
        }
        
        setUploadResult({
          success: true,
          message: message,
          details: details,
          isPartnerLedger: result.total_entries_detected !== undefined
        });
        toast.success(message.split('\n')[0]); // Show first line in toast
        setUploading(false);
        
        // Record upload activity
        await recordUploadActivity(
          selectedFile?.name || 'unknown',
          selectedUpload.id,
          selectedCategory?.id || 'unknown',
          'success',
          details,
          selectedFile
        );
        return;
      }
      
      // Note: order_sheet uploads are handled in the block above (lines 1495-1588)
    } catch (error) {
      console.error("Upload error:", error);
      // Pre-existing byte-identical guard from the backend returns a
      // structured `detail.code === "duplicate_file"`. Offer a one-click
      // force re-upload so the user isn't stuck with "[object Object]".
      if (error?.detail && typeof error.detail === "object" && error.detail.code === "duplicate_file") {
        const info = error.detail;
        const when = info.existing_uploaded_at ? new Date(info.existing_uploaded_at).toLocaleString() : "earlier";
        // eslint-disable-next-line no-alert
        const force = window.confirm(
          `⚠️ This exact file was already uploaded by '${info.existing_uploaded_by || "?"}' at ${when}.\n\n` +
          `(Filename: ${info.existing_filename || "?"})\n\n` +
          `Click OK to RE-UPLOAD anyway (forces a fresh parse — useful if you renamed or edited cells but not bytes).\n` +
          `Click Cancel to keep the existing upload.`
        );
        if (force) {
          try {
            // Build a FormData with force=true and retry once via fetch
            // (skips the retry-loop / xhr path which doesn't expose force).
            setUploading(true);
            setProgressMessage("Re-uploading (force=true)...");
            const fd = new FormData();
            fd.append("file", selectedFile);
            fd.append("force", "true");
            const token = localStorage.getItem("token");
            const r = await fetch(`${BACKEND_URL}/api/partner-ledger/upload`, {
              method: "POST",
              headers: { Authorization: `Bearer ${token}` },
              body: fd,
            });
            if (!r.ok) {
              const txt = await r.text();
              throw new Error(`Force re-upload failed: ${txt.slice(0, 200)}`);
            }
            const data = await r.json();
            toast.success(`Force re-uploaded · ${data.total_entries_detected || 0} entries parsed.`);
            // Resurface the duplicate-handling dialog if the new parse has dups.
            if (data.has_duplicates && data.duplicates_count > 0) {
              const allDups = [];
              for (const p of data.partners || []) {
                for (const e of p.entries || []) {
                  if (e.is_potential_duplicate) allDups.push({ ...e, partner_code: p.partner_code });
                }
              }
              // CRITICAL: pendingLedgerResult must be set, otherwise
              // handleDuplicateResolution aborts silently and the Skip /
              // Replace buttons appear unresponsive.
              setPendingLedgerResult(data);
              setDuplicateEntries(allDups);
              setLedgerErrorEntries([]);
              setDuplicateDialog(true);
            }
          } catch (e2) {
            toast.error(`Re-upload failed: ${e2.message}`);
          } finally {
            setUploading(false);
          }
          return;
        }
        toast.info("Kept existing upload — nothing changed.");
        setUploading(false);
        return;
      }
      toast.error("Failed to process file: " + (error.message || String(error)));
      setUploading(false);
    }
  };
  
  // Save orders with chosen mode (called after duplicate dialog)
  const saveFinalOrders = async (ordersToSave, saveMode, packingMerged = false, result = {}) => {
    try {
      setProgressMessage("Saving orders to database...");
      setUploading(true);
      
      // Use staging_id for large uploads (orders stored server-side).
      // Order of preference: staging_id stashed when the duplicate dialog was opened >
      // summary result > result arg. The first one is critical for combined-sheet
      // uploads where `pendingOrders` is only a 100-row preview slice.
      const stagingId = pendingStagingId || uploadSummaryResult?.staging_id || result?.staging_id;
      const saveBody = stagingId
        ? { staging_id: stagingId, save_mode: saveMode }
        : { orders: ordersToSave, save_mode: saveMode };
      
      // Save to database
      const saveResponse = await fetch(`${BACKEND_URL}/api/shipment-costing/save`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(saveBody)
      });
      
      if (saveResponse.ok) {
        const saveData = await saveResponse.json();
        let message = `${saveData.orders_saved} orders saved to Shipment Costing`;
        if (saveData.orders_skipped > 0) {
          message += ` (${saveData.orders_skipped} duplicates skipped)`;
        }
        if (saveData.orders_replaced > 0) {
          message += ` (${saveData.orders_replaced} duplicates replaced)`;
        }
        if (packingMerged) {
          message += ` [Packing list merged]`;
        }
        
        setUploadResult({
          success: true,
          message: message,
          details: { ...result, ...saveData },
          isOrderSheet: true
        });
        toast.success(message);
        
        // Record upload activity
        await recordUploadActivity(
          selectedFile?.name || 'order_sheet',
          'order_sheet',
          'shipment_costing',
          'success',
          { ...result, ...saveData, save_mode: saveMode },
          selectedFile
        );
      } else {
        const errData = await saveResponse.json();
        toast.error(errData.detail || "Failed to save orders");
        setUploadResult({
          success: false,
          message: errData.detail || "Failed to save orders",
          details: {}
        });
      }
    } catch (error) {
      console.error("Save error:", error);
      toast.error("Failed to save orders: " + error.message);
    } finally {
      setUploading(false);
      setShowDuplicateDialog(false);
      setPendingOrders(null);
      setPendingStagingId(null);
    }
  };
  
  // Handle duplicate dialog action
  const handleDuplicateAction = async (action) => {
    if (!pendingOrders) return;
    
    // Map action to save mode
    let saveMode;
    if (action === "replace") {
      saveMode = "replace";
    } else if (action === "update_problems") {
      saveMode = "update_problems_only";
    } else {
      saveMode = "skip_duplicates";
    }
    
    await saveFinalOrders(pendingOrders, saveMode, false, {});
  };

  // Open upload dialog
  const openUploadDialog = (category, upload) => {
    setSelectedCategory(category);
    setSelectedUpload(upload);
    setSelectedFile(null);
    setUploadResult(null);
    setUploadProgress(0);
    setValidationResult(null);
    setFilePreview(null);
    setSelectedClient(null);
    setClientSearch("");
    setReplaceExisting(false);
    setUploadDialog(true);
    
    // Fetch clients if this upload requires client selection
    if (upload.requiresClient) {
      fetchClients();
    }
  };

  // Close upload dialog
  const closeUploadDialog = () => {
    setUploadDialog(false);
    setSelectedFile(null);
    setSelectedFileData(null); // Clear stored file data
    setPatchedFileData(null);
    setCorrectionsSummary(null);
    setUploadResult(null);
    setValidationResult(null);
    setFilePreview(null);
    setUploadProgress(0);
    setSelectedClient(null);
    setClientSearch("");
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="p-3 lg:p-6 space-y-4 lg:space-y-6 bg-gradient-to-br from-slate-50 to-slate-100 min-h-screen">
      {/* Header */}
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-lg lg:text-2xl font-bold text-slate-800 flex items-center gap-2">
            <Upload className="w-5 h-5 lg:w-7 lg:h-7 text-indigo-600 flex-shrink-0" />
            <span className="truncate">Upload Center</span>
          </h1>
          <p className="text-slate-500 text-xs lg:text-sm mt-1 hidden sm:block">
            Upload sheets for various modules - Download templates and track your uploads
          </p>
        </div>
        <Badge variant="outline" className="text-xs lg:text-sm flex-shrink-0">
          {user?.display_name || user?.username}
        </Badge>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full max-w-3xl grid-cols-6 h-auto">
          <TabsTrigger value="upload" className="gap-1 lg:gap-2 text-xs lg:text-sm py-2 lg:py-2.5">
            <Upload className="w-3.5 h-3.5 lg:w-4 lg:h-4" />
            <span className="hidden sm:inline">Upload Files</span>
            <span className="sm:hidden">Upload</span>
          </TabsTrigger>
          <TabsTrigger value="convert" data-testid="structured-conversion-tab" className="gap-1 lg:gap-2 text-xs lg:text-sm py-2 lg:py-2.5">
            <FileSpreadsheet className="w-3.5 h-3.5 lg:w-4 lg:h-4" />
            <span className="hidden sm:inline">Structured Conversion</span>
            <span className="sm:hidden">Convert</span>
          </TabsTrigger>
          <TabsTrigger value="year-summary" className="gap-1 lg:gap-2 text-xs lg:text-sm py-2 lg:py-2.5">
            <CalendarDays className="w-3.5 h-3.5 lg:w-4 lg:h-4" />
            <span className="hidden sm:inline">Year Summary</span>
            <span className="sm:hidden">Year</span>
          </TabsTrigger>
          <TabsTrigger value="pending" className="gap-1 lg:gap-2 text-xs lg:text-sm py-2 lg:py-2.5">
            <AlertCircle className="w-3.5 h-3.5 lg:w-4 lg:h-4" />
            <span className="hidden sm:inline">Pending</span>
            <span className="sm:hidden">Pending</span>
          </TabsTrigger>
          <TabsTrigger value="history" className="gap-1 lg:gap-2 text-xs lg:text-sm py-2 lg:py-2.5">
            <History className="w-3.5 h-3.5 lg:w-4 lg:h-4" />
            <span className="hidden sm:inline">Upload History</span>
            <span className="sm:hidden">History</span>
          </TabsTrigger>
          <TabsTrigger value="audit" data-testid="upload-audit-tab" className="gap-1 lg:gap-2 text-xs lg:text-sm py-2 lg:py-2.5">
            <FileSearch className="w-3.5 h-3.5 lg:w-4 lg:h-4" />
            <span className="hidden sm:inline">Audit</span>
            <span className="sm:hidden">Audit</span>
          </TabsTrigger>
        </TabsList>

        {/* Upload Tab */}
        <TabsContent value="upload" className="mt-4 lg:mt-6">
          {loadingPermissions ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-8 h-8 animate-spin text-indigo-500" />
              <span className="ml-2 text-slate-500">Loading...</span>
            </div>
          ) : visibleCategories.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Upload className="w-12 h-12 text-slate-300 mb-4" />
              <p className="text-slate-500">No upload sections available for your role</p>
              <p className="text-sm text-slate-400">Contact admin for access</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 lg:gap-6">
              {visibleCategories.map((category) => {
                const IconComponent = category.icon;
                return (
                  <Card 
                    key={category.id} 
                    data-testid={`upload-category-${category.id}`}
                    className={`${category.bgColor} ${category.borderColor} border-2 hover:shadow-lg active:scale-[0.99] transition-all duration-200 touch-manipulation`}
                  >
                    <CardHeader className="pb-2 lg:pb-3 p-3 lg:p-6">
                      <div className="flex items-center gap-2 lg:gap-3">
                        <div className={`p-2 lg:p-3 rounded-xl bg-white shadow-sm flex-shrink-0`}>
                          <IconComponent className={`w-5 h-5 lg:w-6 lg:h-6 ${category.iconColor}`} />
                        </div>
                        <div className="min-w-0">
                          <CardTitle className="text-base lg:text-lg truncate">{category.title}</CardTitle>
                          <CardDescription className="text-[10px] lg:text-xs mt-0.5 line-clamp-1">
                            {category.description}
                          </CardDescription>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-2 p-3 lg:p-6 pt-0">
                      {category.uploads.map((upload) => {
                        const UploadIcon = upload.icon || FileSpreadsheet;
                        return (
                          <div
                            key={upload.id}
                            data-testid={`upload-item-${upload.id}`}
                            className="flex items-center justify-between p-2.5 lg:p-3 bg-white rounded-lg border hover:border-slate-300 active:bg-slate-50 transition-colors cursor-pointer group touch-manipulation"
                            onClick={() => openUploadDialog(category, upload)}
                          >
                            <div className="flex items-center gap-2 lg:gap-3 min-w-0">
                              <UploadIcon className="w-4 h-4 text-slate-500 flex-shrink-0" />
                              <div className="min-w-0">
                                <p className="text-xs lg:text-sm font-medium text-slate-700 truncate">{upload.name}</p>
                                <p className="text-[10px] lg:text-xs text-slate-500">{upload.acceptedFormats}</p>
                              </div>
                            </div>
                            <ChevronRight className="w-4 h-4 text-slate-400 group-hover:text-slate-600 transition-colors flex-shrink-0" />
                          </div>
                        );
                      })}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* Structured Data Conversion Tab */}
        <TabsContent value="convert" className="mt-4 lg:mt-6" data-testid="structured-conversion-tab-content">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <FileSpreadsheet className="w-5 h-5 text-emerald-600" />
                Structured Data Conversion
              </CardTitle>
              <CardDescription className="text-xs sm:text-sm">
                Upload a raw partner sales sheet (multiple tabs, mixed column headers).
                The tool will merge all tabs into <strong>one structured sheet</strong> in the
                upload-ready ledger format, sorted oldest&nbsp;→&nbsp;newest with dates in
                <code className="mx-1">DDMMYY</code> format, currency tokens stripped (e.g. <code>8730 INR</code> → <code>8730</code>),
                and empty tracking cells highlighted in red.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Step 0: download blank input template — partners build their
                  raw file FROM this so headers match the parser perfectly. */}
              <div className="border border-sky-300 bg-sky-50 rounded p-3 flex items-start gap-3" data-testid="conv-template-section">
                <FileDown className="w-5 h-5 text-sky-700 mt-0.5 shrink-0" />
                <div className="flex-1 text-xs sm:text-sm">
                  <div className="font-semibold text-sky-900">Step 0 · Download the input template</div>
                  <p className="text-sky-800 mt-0.5 leading-relaxed">
                    Send this blank XLSX to your partner. It contains the canonical
                    header row + sample rows + a Field Notes sheet listing every
                    accepted column alias. Anything filled in this template
                    converts cleanly without manual fix-ups.
                  </p>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  className="border-sky-400 text-sky-800 hover:bg-sky-100 shrink-0"
                  data-testid="conv-template-btn"
                  onClick={async () => {
                    try {
                      const tk = localStorage.getItem("token");
                      const pcRaw = (convPartner || "PARTNER_CODE").trim().toUpperCase();
                      const safe = pcRaw.replace(/[^A-Z0-9_-]+/g, "_") || "PARTNER_CODE";
                      const url = `${BACKEND_URL}/api/upload-center/raw-to-structured-template?partner_code=${encodeURIComponent(safe)}`;
                      const r = await fetch(url, { headers: { Authorization: `Bearer ${tk}` } });
                      if (!r.ok) {
                        const t = await r.text();
                        throw new Error(t.slice(0, 160) || `HTTP ${r.status}`);
                      }
                      const blob = await r.blob();
                      const a = document.createElement("a");
                      a.href = URL.createObjectURL(blob);
                      a.download = `raw_input_template_${safe}.xlsx`;
                      document.body.appendChild(a);
                      a.click();
                      a.remove();
                      URL.revokeObjectURL(a.href);
                      toast.success(`📥 Downloaded template for ${safe}`);
                    } catch (e) {
                      toast.error(`Template download failed: ${e.message}`);
                    }
                  }}
                >
                  <FileDown className="w-4 h-4 mr-1" />
                  Download template
                </Button>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium text-slate-700">Raw partner sales file (.xlsx)</label>
                  <input
                    ref={convFileInputRef}
                    type="file"
                    accept=".xlsx"
                    onChange={handleConvFileSelect}
                    className="mt-1 block w-full text-sm text-slate-600 file:mr-2 file:py-2 file:px-3 file:rounded file:border-0 file:text-sm file:bg-emerald-100 file:text-emerald-700 hover:file:bg-emerald-200"
                    data-testid="conv-file-input"
                  />
                  {convFile && (
                    <p className="text-[11px] text-emerald-700 mt-1" data-testid="conv-file-name">
                      Selected: {convFile.name} ({(convFile.size / 1024).toFixed(1)} KB)
                    </p>
                  )}
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">Partner code (output sheet name)</label>
                  <input
                    type="text"
                    value={convPartner}
                    onChange={(e) => setConvPartner(e.target.value.toUpperCase())}
                    placeholder="e.g. MED_INDIA"
                    className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded-md font-mono uppercase"
                    data-testid="conv-partner-input"
                  />
                  <p className="text-[11px] text-slate-500 mt-1">
                    Auto-filled from filename; you can override.
                  </p>
                </div>
              </div>

              <div className="bg-slate-50 border border-slate-200 rounded p-2 text-[11px] text-slate-600">
                <div className="font-semibold text-slate-700 mb-1">Column mapping (raw → structured):</div>
                <ul className="space-y-0.5 pl-3 list-disc">
                  <li><code>Tracking No.</code> → <code>Tracking Number</code></li>
                  <li><code>Name</code> → <code>Customer Name</code></li>
                  <li><code>Date</code> → <code>Order Date</code> (DDMMYY format)</li>
                  <li><code>TOTAL INR</code> / <code>INR COST</code> → <code>Total Sale INR</code></li>
                  <li><code>Payment ESR/Maxwell</code> → <code>Payment to Company (ESR)</code> (auto-calculated as Total − PRS when column missing)</li>
                  <li><code>Payment Persona</code> → <code>Payment to Personal (PRS)</code></li>
                  <li><code>Account</code> / <code>Account/Mode</code> → <code>Payment Mode</code></li>
                  <li><code>Comments</code> / <code>Remarks</code> → <code>Remarks</code></li>
                </ul>
              </div>

              <Button
                onClick={handleConvert}
                disabled={convRunning || !convFile || !convPartner.trim()}
                className="w-full bg-emerald-600 hover:bg-emerald-700"
                data-testid="conv-run-btn"
              >
                {convRunning ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Converting…</>
                ) : (
                  <><FileSpreadsheet className="w-4 h-4 mr-2" /> Convert &amp; download structured file</>
                )}
              </Button>

              {convResult && (
                <div className="p-3 border-2 border-emerald-400 bg-emerald-50 rounded" data-testid="conv-result">
                  <div className="font-semibold text-emerald-900 text-sm">
                    ✅ Conversion complete — {convResult.rows.toLocaleString()} transactions exported
                  </div>
                  <div className="text-[11px] text-emerald-700 mt-0.5">
                    Downloaded at {new Date(convResult.downloaded_at).toLocaleString()}.
                  </div>
                  {convResult.skipped.length > 0 && (
                    <div className="text-[11px] text-amber-700 mt-1">
                      ⚠️ Skipped sheets (no recognised header): {convResult.skipped.join(", ")}
                    </div>
                  )}
                  <div className="text-[11px] text-slate-600 mt-2">
                    Next: open the downloaded file, verify highlighted (red) tracking cells, fix where needed, then
                    upload it via <strong>Upload Files → Partner Structured Ledger</strong>.
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Year Summary Tab */}
        <TabsContent value="year-summary" className="mt-4 lg:mt-6">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <CardTitle className="text-lg flex items-center gap-2">
                  <CalendarDays className="w-5 h-5 text-indigo-600" />
                  Year Summary - Data Flow Overview
                </CardTitle>
                <div className="flex items-center gap-3">
                  <select
                    value={yearSummaryYear}
                    onChange={(e) => setYearSummaryYear(Number(e.target.value))}
                    className="px-3 py-1.5 border rounded-md text-sm"
                    data-testid="year-summary-year-select"
                  >
                    {(() => {
                      const currentYear = new Date().getFullYear();
                      const years = [];
                      for (let y = currentYear + 1; y >= 2015; y--) years.push(y);
                      return years;
                    })().map((y) => (
                      <option key={y} value={y}>{y}</option>
                    ))}
                  </select>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={fetchYearSummary}
                    disabled={loadingYearSummary}
                    data-testid="year-summary-refresh-btn"
                  >
                    <RefreshCw className={`w-4 h-4 mr-1 ${loadingYearSummary ? "animate-spin" : ""}`} />
                    Refresh
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={fetchReconcile}
                    disabled={loadingReconcile}
                    data-testid="year-summary-reconcile-btn"
                    title="Compare dashboard counts vs Excel download row counts"
                  >
                    <RefreshCw className={`w-4 h-4 mr-1 ${loadingReconcile ? "animate-spin" : ""}`} />
                    Reconcile counts
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {/* Summary Stats - Top Cards */}
              <div className="grid grid-cols-2 md:grid-cols-5 lg:grid-cols-10 gap-2 mb-6">
                <div className="p-2.5 bg-blue-50 rounded-lg border border-blue-200" data-testid="total-parcels-card">
                  <p className="text-[10px] text-blue-600 font-medium uppercase">Total Parcels</p>
                  <p className="text-lg font-bold text-blue-800">{yearSummaryData.totals?.parcels?.toLocaleString() || 0}</p>
                </div>
                <div className="p-2.5 bg-sky-50 rounded-lg border border-sky-200" data-testid="dp-parcels-card">
                  <p className="text-[10px] text-sky-600 font-medium uppercase">DP Clients</p>
                  <p className="text-lg font-bold text-sky-800">{yearSummaryData.totals?.dp_client_parcels?.toLocaleString() || 0}</p>
                </div>
                <div className="p-2.5 bg-violet-50 rounded-lg border border-violet-200" data-testid="partner-parcels-card">
                  <p className="text-[10px] text-violet-600 font-medium uppercase">Partners</p>
                  <p className="text-lg font-bold text-violet-800">{yearSummaryData.totals?.partner_parcels?.toLocaleString() || 0}</p>
                </div>
                <div className="p-2.5 bg-cyan-50 rounded-lg border border-cyan-200" data-testid="packing-list-card">
                  <p className="text-[10px] text-cyan-600 font-medium uppercase">Packing List</p>
                  <p className="text-lg font-bold text-cyan-800">{yearSummaryData.totals?.packing_list_count?.toLocaleString() || 0}</p>
                </div>
                <div className="p-2.5 bg-amber-50 rounded-lg border border-amber-200" data-testid="dp-costed-card">
                  <p className="text-[10px] text-amber-600 font-medium uppercase">Costing Sheet</p>
                  <p className="text-lg font-bold text-amber-800">{yearSummaryData.totals?.all_costed?.toLocaleString() || 0}</p>
                  <p className="text-[9px] text-amber-500">DP: {yearSummaryData.totals?.dp_costed?.toLocaleString() || 0} | Partner: {yearSummaryData.totals?.partner_costed?.toLocaleString() || 0}</p>
                </div>
                <div className="p-2.5 bg-teal-50 rounded-lg border border-teal-200" data-testid="partner-sales-card">
                  <p className="text-[10px] text-teal-600 font-medium uppercase">Partner Sales</p>
                  <p className="text-lg font-bold text-teal-800">{yearSummaryData.totals?.partner_with_sales?.toLocaleString() || 0}</p>
                </div>
                <div className="p-2.5 bg-red-50 rounded-lg border border-red-200" data-testid="errors-card">
                  <p className="text-[10px] text-red-600 font-medium uppercase">Errors</p>
                  <p className="text-lg font-bold text-red-800">{yearSummaryData.totals?.costing_errors?.toLocaleString() || 0}</p>
                  <p className="text-[9px] text-red-500">product / shipping / no tracking</p>
                </div>
                <div className="p-2.5 bg-rose-50 rounded-lg border border-rose-200" data-testid="sales-pending-card">
                  <p className="text-[10px] text-rose-600 font-medium uppercase">Sales Pending</p>
                  <p className="text-lg font-bold text-rose-800">{yearSummaryData.totals?.sales_errors?.toLocaleString() || 0}</p>
                  <p className="text-[9px] text-rose-500">not errors</p>
                </div>
                <div className="p-2.5 bg-emerald-50 rounded-lg border border-emerald-200" data-testid="resolved-card">
                  <p className="text-[10px] text-emerald-600 font-medium uppercase">Resolved</p>
                  <p className="text-lg font-bold text-emerald-800">{yearSummaryData.totals?.errors_resolved?.toLocaleString() || 0}</p>
                </div>
                <div className="p-2.5 bg-indigo-50 rounded-lg border border-indigo-200" data-testid="payment-verified-card">
                  <p className="text-[10px] text-indigo-600 font-medium uppercase">Pmt Verified</p>
                  <p className="text-lg font-bold text-indigo-800">{yearSummaryData.totals?.payment_verified?.toLocaleString() || 0}</p>
                </div>
                <div className="p-2.5 bg-green-50 rounded-lg border border-green-200" data-testid="verified-months-card">
                  <p className="text-[10px] text-green-600 font-medium uppercase">Verified</p>
                  <p className="text-lg font-bold text-green-800">{yearSummaryData.totals?.verified_months || 0}/12</p>
                </div>
              </div>

              {/* Category + name filter — view yearly summary by DP / Partner / Agent / All or one specific code */}
              <div className="flex flex-wrap items-end gap-2 mb-3 p-3 rounded-md border border-slate-200 bg-slate-50/50" data-testid="year-summary-filter-row">
                <div>
                  <label className="text-[10px] uppercase tracking-wide text-slate-500">Category</label>
                  <select
                    value={yearSummaryCategory}
                    onChange={(e) => { setYearSummaryCategory(e.target.value); setYearSummaryClientCode(""); }}
                    className="block px-3 py-1.5 border rounded-md text-sm bg-white"
                    data-testid="year-summary-category-select"
                  >
                    <option value="all">All</option>
                    <option value="dp_clients">DP Clients</option>
                    <option value="partners">Partners</option>
                    <option value="agents">Agents</option>
                  </select>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wide text-slate-500">Specific name</label>
                  <select
                    value={yearSummaryClientCode || "__category__"}
                    onChange={(e) => setYearSummaryClientCode(e.target.value === "__category__" ? "" : e.target.value)}
                    className="block px-3 py-1.5 border rounded-md text-sm bg-white min-w-[200px]"
                    data-testid="year-summary-name-select"
                  >
                    <option value="__category__">— Whole category —</option>
                    {(() => {
                      const opts = yearSummaryCategory === "dp_clients"
                        ? yearSummaryFilterOptions.dp_clients
                        : yearSummaryCategory === "partners"
                          ? yearSummaryFilterOptions.partners
                          : yearSummaryCategory === "agents"
                            ? yearSummaryFilterOptions.agents
                            : [
                                ...yearSummaryFilterOptions.dp_clients.map(x => ({ ...x, _grp: "DP" })),
                                ...yearSummaryFilterOptions.partners.map(x => ({ ...x, _grp: "Partner" })),
                                ...yearSummaryFilterOptions.agents.map(x => ({ ...x, _grp: "Agent" })),
                              ];
                      return opts.map((o) => (
                        <option key={o.code} value={o.code}>
                          {o.code}{o._grp ? ` · ${o._grp}` : ""}{o.name && o.name !== o.code ? ` — ${o.name}` : ""}
                        </option>
                      ));
                    })()}
                  </select>
                </div>
                {(yearSummaryCategory !== "all" || yearSummaryClientCode) && (
                  <Button variant="ghost" size="sm" onClick={() => { setYearSummaryCategory("all"); setYearSummaryClientCode(""); }} data-testid="year-summary-filter-clear">
                    Clear
                  </Button>
                )}
                {/* Raw payment / balance summary chip */}
                {yearSummaryData.totals?.raw_payment !== undefined && (
                  <div className="ml-auto flex items-center gap-3 text-xs">
                    <div className="px-3 py-1.5 rounded-md bg-emerald-50 border border-emerald-200" data-testid="year-summary-raw-payment-card">
                      <div className="text-[10px] uppercase text-emerald-700">Raw payment</div>
                      <div className="font-bold text-emerald-800">
                        ₹{(yearSummaryData.totals?.raw_payment || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}
                      </div>
                    </div>
                    <div className={`px-3 py-1.5 rounded-md border ${
                      (yearSummaryData.totals?.balance_raw || 0) > 0
                        ? "bg-rose-50 border-rose-200"
                        : "bg-emerald-50 border-emerald-200"
                    }`} data-testid="year-summary-balance-raw-card">
                      <div className={`text-[10px] uppercase ${(yearSummaryData.totals?.balance_raw || 0) > 0 ? "text-rose-700" : "text-emerald-700"}`}>Balance raw</div>
                      <div className={`font-bold ${(yearSummaryData.totals?.balance_raw || 0) > 0 ? "text-rose-800" : "text-emerald-800"}`}>
                        ₹{(yearSummaryData.totals?.balance_raw || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Year-level View / Download toolbar — same datasets as the
                  per-month dropdown but scoped to the entire calendar year.
                  Backend treats month=0 as the "whole year" sentinel. */}
              <div className="flex items-center justify-end gap-2 flex-wrap mb-2 px-1" data-testid="year-summary-year-toolbar">
                <span className="text-[11px] text-slate-500 mr-1">
                  Whole year ({yearSummaryData?.year || yearSummaryYear}):
                </span>
                <select
                  defaultValue=""
                  data-testid="year-summary-year-view"
                  onChange={(e) => {
                    const ds = e.target.value;
                    if (!ds) return;
                    openYearSummaryPreview(0, ds);
                    e.target.value = "";
                  }}
                  className="text-xs border border-emerald-300 rounded px-2 py-1 bg-emerald-50 hover:border-emerald-500 focus:outline-none focus:border-emerald-600 text-emerald-800"
                  title="View any FY-wide dataset on screen (new tab)"
                >
                  <option value="">👁 View (FY)</option>
                  <option value="parcels">Parcels</option>
                  <option value="costing">Costing DP Clients</option>
                  <option value="costing_partners">Costing Partners</option>
                  <option value="partners_ledger">Partners Ledger</option>
                  <option value="sales_pending_dp">Sales Pending DP Clients</option>
                  <option value="sales_pending_partners">Sales Pending Partners</option>
                  <option value="errors">Other Errors</option>
                  <option value="payment">Payment</option>
                </select>
                <select
                  defaultValue=""
                  data-testid="year-summary-year-download"
                  onChange={(e) => {
                    const ds = e.target.value;
                    if (!ds) return;
                    const tk = localStorage.getItem("token");
                    let url = `${process.env.REACT_APP_BACKEND_URL}/api/upload-center/year-summary/download?year=${yearSummaryData?.year || yearSummaryYear}&month=0&dataset=${ds === "all_filtered" ? "all" : ds}`;
                    if (ds === "all_filtered") {
                      // eslint-disable-next-line no-alert
                      const cc = (window.prompt("Filter FY Close Pack to which client code? (e.g. CARECRAFT)") || "").trim();
                      if (!cc) { e.target.value = ""; return; }
                      url += `&client_code=${encodeURIComponent(cc)}`;
                    }
                    toast.info("Generating FY workbook… large datasets can take ~15-30s");
                    fetch(url, { headers: { Authorization: `Bearer ${tk}` } })
                      .then(async (res) => {
                        if (!res.ok) {
                          const err = await res.json().catch(() => ({}));
                          throw new Error(err.detail || `HTTP ${res.status}`);
                        }
                        const blob = await res.blob();
                        const cd = res.headers.get("Content-Disposition") || "";
                        const m = cd.match(/filename="([^"]+)"/);
                        const filename = m ? m[1] : `${ds}_FY${yearSummaryData?.year || yearSummaryYear}.xlsx`;
                        const blobUrl = URL.createObjectURL(blob);
                        const a = document.createElement("a");
                        a.href = blobUrl;
                        a.download = filename;
                        document.body.appendChild(a);
                        a.click();
                        a.remove();
                        URL.revokeObjectURL(blobUrl);
                        toast.success(`Downloaded ${filename}`);
                      })
                      .catch((err) => toast.error(err.message || "Download failed"))
                      .finally(() => { e.target.value = ""; });
                  }}
                  className="text-xs border border-indigo-300 rounded px-2 py-1 bg-indigo-50 hover:border-indigo-500 focus:outline-none focus:border-indigo-600 text-indigo-800 font-medium"
                  title="Download an FY-wide workbook (all 12 months merged)"
                >
                  <option value="">↓ Download (FY)</option>
                  <option value="all">📦 Close Pack (All · FY)</option>
                  <option value="all_filtered">🎯 Close Pack (filter to client...)</option>
                  <option value="parcels">Parcels</option>
                  <option value="costing">Costing DP Clients</option>
                  <option value="costing_partners">Costing Partners</option>
                  <option value="partners_ledger">Partners Ledger</option>
                  <option value="sales_pending_dp">Sales Pending DP Clients</option>
                  <option value="sales_pending_partners">Sales Pending Partners</option>
                  <option value="errors">Other Errors</option>
                  <option value="payment">Payment</option>
                </select>
              </div>

              {/* Monthly Table */}
              <div className="overflow-x-auto border rounded-lg" data-testid="year-summary-table">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 border-b">
                    <tr>
                      <th className="px-3 py-3 text-left font-semibold text-xs whitespace-nowrap">Month</th>
                      <th className="px-3 py-3 text-right font-semibold text-xs whitespace-nowrap">Parcels</th>
                      <th className="px-3 py-3 text-center font-semibold text-xs whitespace-nowrap">Order Sheets</th>
                      <th className="px-3 py-3 text-center font-semibold text-xs whitespace-nowrap bg-amber-100 text-amber-900 border-l border-r border-amber-300" title="Manual entry by Upload Team — click ✏️ to edit, ↗ to open this month in a new tab">
                        <div className="flex items-center justify-center gap-1">
                          <Edit3 className="w-3 h-3" />
                          Manual Parcels
                        </div>
                      </th>
                      <th className="px-3 py-3 text-center font-semibold text-xs whitespace-nowrap">
                        <span className="block">Costing Sheet</span>
                        <span className="text-[10px] text-amber-500 font-normal">(All Clients)</span>
                      </th>
                      <th className="px-3 py-3 text-center font-semibold text-xs whitespace-nowrap">
                        <span className="block">Partners Ledger</span>
                        <span className="text-[10px] text-teal-500 font-normal">(Sales View)</span>
                      </th>
                      <th className="px-3 py-3 text-center font-semibold text-xs whitespace-nowrap">Error View</th>
                      <th className="px-3 py-3 text-center font-semibold text-xs whitespace-nowrap">
                        <span className="block">Sales Pending</span>
                        <span className="text-[10px] text-rose-500 font-normal">(not errors)</span>
                      </th>
                      <th className="px-3 py-3 text-center font-semibold text-xs whitespace-nowrap">Resolved</th>
                      <th className="px-3 py-3 text-center font-semibold text-xs whitespace-nowrap">
                        <span className="block">Payment</span>
                        <span className="text-[10px] text-indigo-500 font-normal">Verification</span>
                      </th>
                      <th className="px-3 py-3 text-right font-semibold text-xs whitespace-nowrap" data-testid="th-raw-payment">
                        <span className="block">Raw Payment</span>
                        <span className="text-[10px] text-emerald-500 font-normal">(money received)</span>
                      </th>
                      <th className="px-3 py-3 text-right font-semibold text-xs whitespace-nowrap" data-testid="th-balance-raw">
                        <span className="block">Balance Raw</span>
                        <span className="text-[10px] text-rose-500 font-normal">(cost − raw)</span>
                      </th>
                      <th className="px-3 py-3 text-center font-semibold text-xs whitespace-nowrap">Verified</th>
                      <th className="px-3 py-3 text-center font-semibold text-xs whitespace-nowrap">Narration</th>
                      <th className="px-3 py-3 text-center font-semibold text-xs whitespace-nowrap">View / Download</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loadingYearSummary ? (
                      <tr>
                        <td colSpan={15} className="text-center py-8">
                          <RefreshCw className="w-6 h-6 animate-spin mx-auto text-slate-400" />
                        </td>
                      </tr>
                    ) : (
                      yearSummaryData.months?.map((monthData) => {
                        const isExpanded = expandedMonth === monthData.month;
                        const dayCacheKey = `${yearSummaryYear}-${monthData.month}`;
                        const dayRows = monthDaysData[dayCacheKey] || [];
                        return (
                        <React.Fragment key={monthData.month}>
                        <tr
                          className={`border-b hover:bg-slate-50 cursor-pointer ${
                            monthData.verified ? "bg-green-50/50" : ""
                          } ${monthData.parcels === 0 ? "opacity-50" : ""} ${isExpanded ? "bg-indigo-50/40" : ""}`}
                          onClick={() => monthData.parcels > 0 && toggleMonthExpansion(monthData.month)}
                          onDoubleClick={(e) => { e.stopPropagation(); handleYearSummaryDoubleClick(monthData); }}
                          title="Click to expand date-wise breakdown · Double-click to edit verification"
                          data-testid={`year-summary-row-${monthData.month_name?.toLowerCase()}`}
                        >
                          <td className="px-3 py-3 font-medium">
                            <span className="inline-flex items-center gap-2">
                              {monthData.parcels > 0 && (
                                isExpanded
                                  ? <ChevronDown className="w-4 h-4 text-indigo-600" />
                                  : <ChevronRight className="w-4 h-4 text-slate-400" />
                              )}
                              {monthData.month_name}
                            </span>
                          </td>
                          {/* Parcels */}
                          <td className="px-3 py-3 text-right">
                            <div>
                              <span className="font-semibold">{monthData.parcels?.toLocaleString()}</span>
                              {monthData.parcels > 0 && (
                                <div className="text-[10px] text-muted-foreground mt-0.5">
                                  <span className="text-sky-600">{monthData.dp_client_parcels} DP</span>
                                  {" / "}
                                  <span className="text-violet-600">{monthData.partner_parcels} P</span>
                                </div>
                              )}
                            </div>
                          </td>
                          {/* Order Sheets */}
                          <td className="px-3 py-3 text-center">
                            {monthData.order_sheets_status === "yes" ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                                <CheckCircle className="w-3 h-3" /> Yes
                              </span>
                            ) : monthData.order_sheets_status === "partial" ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full text-xs font-medium">
                                <AlertTriangle className="w-3 h-3" /> Partial
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-slate-400 text-xs">
                                <XCircle className="w-3 h-3" /> No
                              </span>
                            )}
                          </td>
                          {/* Manual Parcels — editable.
                              Discoverability fix:
                                • ✏️ pencil = click to edit value (only handler that triggers edit mode)
                                • ↗ arrow = click to open the month's parcels in a new tab
                              Removed the cell-level onDoubleClick because it
                              accidentally fired the "open preview" action when
                              the user wanted to edit the number (Jan/Feb screenshot).
                          */}
                          <td
                            className="px-3 py-3 text-center bg-amber-50 transition-colors border-l border-r border-amber-200"
                            title="Manual entry by Upload Team"
                            data-testid={`manual-parcels-cell-${monthData.month}`}
                          >
                            {(() => {
                              const key = `${yearSummaryYear}-${monthData.month}`;
                              const isEditing = editingManualKey === key;
                              const mp = manualParcels[key]?.parcels;
                              const startEdit = () => {
                                setEditingManualKey(key);
                                setEditingManualValue(String(manualParcels[key]?.parcels ?? ""));
                              };
                              if (isEditing) {
                                const saveManual = async () => {
                                  const n = parseInt(editingManualValue, 10);
                                  if (Number.isNaN(n) || n < 0) {
                                    toast.error("Enter a non-negative integer");
                                    return;
                                  }
                                  try {
                                    const r = await fetch(`${BACKEND_URL}/api/payment-verification/manual-parcels`, {
                                      method: "PUT",
                                      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                                      body: JSON.stringify({ year: yearSummaryYear, month: monthData.month, parcels: n }),
                                    });
                                    const d = await r.json();
                                    if (!r.ok) throw new Error(d.detail || `HTTP ${r.status}`);
                                    setManualParcels((p) => ({ ...p, [key]: d }));
                                    toast.success(`Saved · ${n} parcels`);
                                  } catch (e) {
                                    toast.error(`Save failed: ${e.message}`);
                                  } finally {
                                    setEditingManualKey(null);
                                  }
                                };
                                return (
                                  <input
                                    type="number"
                                    min="0"
                                    autoFocus
                                    value={editingManualValue}
                                    onChange={(e) => setEditingManualValue(e.target.value)}
                                    onBlur={saveManual}
                                    onKeyDown={(e) => {
                                      if (e.key === "Enter") saveManual();
                                      if (e.key === "Escape") setEditingManualKey(null);
                                    }}
                                    className="w-20 px-1.5 py-0.5 text-xs text-center border-2 border-indigo-400 rounded focus:outline-none"
                                    data-testid={`manual-parcels-input-${monthData.month}`}
                                  />
                                );
                              }
                              if (mp == null) {
                                // Empty state — single pencil button to start editing
                                return (
                                  <button
                                    type="button"
                                    onClick={startEdit}
                                    className="inline-flex items-center gap-1 text-amber-700 hover:text-amber-900 hover:bg-amber-200 px-2 py-1 rounded text-xs italic font-medium border border-dashed border-amber-400"
                                    title="Click ✏️ to enter manual parcel count"
                                    data-testid={`manual-parcels-add-${monthData.month}`}
                                  >
                                    <Edit3 className="w-3 h-3" />
                                    Click to add
                                  </button>
                                );
                              }
                              const parcels = monthData.parcels || 0;
                              const costing = monthData.all_costed || 0;
                              const allMatch = mp === parcels && mp === costing && mp > 0;
                              return (
                                <div className="flex flex-col items-center gap-0.5">
                                  {/* Value row: number + ✏️ edit + ↗ open-in-tab buttons */}
                                  <div className="inline-flex items-center gap-1.5">
                                    <span className={`font-semibold ${allMatch ? "text-emerald-700" : "text-cyan-700"}`}>
                                      {mp.toLocaleString()}
                                    </span>
                                    <button
                                      type="button"
                                      onClick={startEdit}
                                      className="p-0.5 rounded hover:bg-amber-200 text-amber-700 hover:text-amber-900"
                                      title="Edit manual parcel count"
                                      data-testid={`manual-parcels-edit-${monthData.month}`}
                                    >
                                      <Edit3 className="w-3.5 h-3.5" />
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => openYearSummaryPreview(monthData.month, "parcels")}
                                      className="p-0.5 rounded hover:bg-sky-200 text-sky-700 hover:text-sky-900"
                                      title="Open this month's parcels in a new tab"
                                      data-testid={`manual-parcels-open-${monthData.month}`}
                                    >
                                      <ExternalLink className="w-3.5 h-3.5" />
                                    </button>
                                  </div>
                                  {/* Tally / discrepancy subtext (informational only — not clickable) */}
                                  {allMatch ? (
                                    <span
                                      className="text-[10px] text-emerald-600"
                                      data-testid={`manual-parcels-tally-${monthData.month}`}
                                    >
                                      ✅ Tally
                                    </span>
                                  ) : (
                                    <span
                                      className="text-[10px] text-amber-600"
                                      data-testid={`manual-parcels-mismatch-${monthData.month}`}
                                    >
                                      Δ P:{(mp - parcels).toLocaleString()} / C:{(mp - costing).toLocaleString()}
                                    </span>
                                  )}
                                </div>
                              );
                            })()}
                          </td>
                          {/* Costing Sheet (All Clients - DP + Partner) */}
                          <td className="px-3 py-3 text-center">
                            {(monthData.all_costed || 0) > 0 ? (
                              <div>
                                <span className="text-amber-700 font-semibold">{(monthData.all_costed || 0).toLocaleString()}</span>
                                {monthData.parcels > 0 && (
                                  <div className="text-[10px] text-muted-foreground">
                                    {monthData.all_costed || 0}/{monthData.parcels}
                                  </div>
                                )}
                                <div className="text-[9px] text-slate-400">
                                  DP:{monthData.dp_costed || 0} P:{monthData.partner_costed || 0}
                                </div>
                              </div>
                            ) : monthData.parcels > 0 ? (
                              <span className="text-red-500 text-xs font-medium">0/{monthData.parcels}</span>
                            ) : (
                              <span className="text-slate-400 text-xs">-</span>
                            )}
                          </td>
                          {/* Partners Ledger (Sales View) */}
                          <td className="px-3 py-3 text-center">
                            {monthData.partner_with_sales > 0 ? (
                              <div>
                                <span className="text-teal-700 font-semibold">{monthData.partner_with_sales?.toLocaleString()}</span>
                                {monthData.partner_parcels > 0 && (
                                  <div className="text-[10px] text-muted-foreground">
                                    {monthData.partner_with_sales}/{monthData.partner_parcels}
                                  </div>
                                )}
                              </div>
                            ) : monthData.partner_parcels > 0 ? (
                              <span className="text-red-500 text-xs font-medium">0/{monthData.partner_parcels}</span>
                            ) : (
                              <span className="text-slate-400 text-xs">-</span>
                            )}
                          </td>
                          {/* Error View — REAL errors only (product / shipping / no-tracking) */}
                          <td className="px-3 py-3 text-center">
                            {monthData.costing_errors > 0 ? (
                              <span className="text-red-600 font-semibold">{monthData.costing_errors?.toLocaleString()}</span>
                            ) : monthData.parcels > 0 ? (
                              <span className="text-green-600 text-xs font-medium flex items-center justify-center gap-1">
                                <CheckCircle className="w-3 h-3" /> 0
                              </span>
                            ) : (
                              <span className="text-slate-400 text-xs">-</span>
                            )}
                          </td>
                          {/* Sales Pending — count only, not an error */}
                          <td className="px-3 py-3 text-center">
                            {monthData.sales_errors > 0 ? (
                              <span className="text-rose-500 font-semibold">{monthData.sales_errors?.toLocaleString()}</span>
                            ) : monthData.parcels > 0 ? (
                              <span className="text-slate-400 text-xs">0</span>
                            ) : (
                              <span className="text-slate-400 text-xs">-</span>
                            )}
                          </td>
                          {/* Resolved */}
                          <td className="px-3 py-3 text-center">
                            {monthData.errors_resolved > 0 ? (
                              <span className="text-green-600 font-semibold">{monthData.errors_resolved}</span>
                            ) : (
                              <span className="text-slate-400 text-xs">0</span>
                            )}
                          </td>
                          {/* Payment Verification */}
                          <td className="px-3 py-3 text-center">
                            {monthData.payment_verified > 0 ? (
                              <div>
                                <span className="text-indigo-700 font-semibold">{monthData.payment_verified?.toLocaleString()}</span>
                                {monthData.partner_parcels > 0 && (
                                  <div className="text-[10px] text-muted-foreground">
                                    {monthData.payment_verified}/{monthData.partner_parcels}
                                  </div>
                                )}
                              </div>
                            ) : monthData.partner_parcels > 0 ? (
                              <span className="text-slate-400 text-xs">0/{monthData.partner_parcels}</span>
                            ) : (
                              <span className="text-slate-400 text-xs">-</span>
                            )}
                          </td>
                          {/* Raw Payment */}
                          <td className="px-3 py-3 text-right font-mono text-xs" data-testid={`td-raw-payment-${monthData.month}`}>
                            {monthData.raw_payment > 0 ? (
                              <span className="text-emerald-700 font-semibold">
                                ₹{monthData.raw_payment.toLocaleString("en-IN", { maximumFractionDigits: 2 })}
                              </span>
                            ) : (
                              <span className="text-slate-300">-</span>
                            )}
                          </td>
                          {/* Balance Raw — clickable drill-down */}
                          <td className="px-3 py-3 text-right font-mono text-xs" data-testid={`td-balance-raw-${monthData.month}`}>
                            {monthData.total_cost_sum > 0 ? (
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); openBalanceRawDrilldown(monthData.year, monthData.month); }}
                                className={`${monthData.balance_raw > 0 ? "text-rose-700" : "text-emerald-700"} font-semibold underline-offset-2 hover:underline`}
                                title="Click to see parcels still awaiting payment"
                                data-testid={`btn-balance-raw-${monthData.month}`}
                              >
                                ₹{monthData.balance_raw.toLocaleString("en-IN", { maximumFractionDigits: 2 })}
                              </button>
                            ) : (
                              <span className="text-slate-300">-</span>
                            )}
                          </td>
                          {/* Verified */}
                          <td className="px-3 py-3 text-center">
                            {monthData.verified ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                                <CheckCircle className="w-3 h-3" /> Yes
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-slate-100 text-slate-500 rounded-full text-xs">
                                <Clock className="w-3 h-3" /> No
                              </span>
                            )}
                          </td>
                          {/* Narration */}
                          <td className="px-3 py-3 text-center max-w-[120px]">
                            {monthData.narration ? (
                              <span className="text-xs text-slate-600 truncate block" title={monthData.narration}>
                                {monthData.narration}
                              </span>
                            ) : (
                              <span className="text-xs text-slate-300 flex items-center justify-center gap-1">
                                <Edit3 className="w-3 h-3" /> Add
                              </span>
                            )}
                          </td>
                          {/* View + Download dropdowns */}
                          <td
                            className="px-3 py-3 text-center"
                            onClick={(e) => e.stopPropagation()}
                            onDoubleClick={(e) => e.stopPropagation()}
                          >
                            <div className="flex items-center justify-center gap-1.5 flex-wrap">
                            <select
                              defaultValue=""
                              data-testid={`year-summary-view-${monthData.month}`}
                              onChange={(e) => {
                                const ds = e.target.value;
                                if (!ds) return;
                                openYearSummaryPreview(monthData.month, ds);
                                e.target.value = "";
                              }}
                              className="text-xs border border-emerald-300 rounded px-2 py-0.5 bg-emerald-50 hover:border-emerald-500 focus:outline-none focus:border-emerald-600 text-emerald-800"
                              title="View dataset on screen (no Excel download)"
                            >
                              <option value="">👁 View</option>
                              <option value="parcels">Parcels</option>
                              <option value="costing">Costing DP Clients</option>
                              <option value="costing_partners">Costing Partners</option>
                              <option value="partners_ledger">Partners Ledger</option>
                              <option value="sales_pending_dp">Sales Pending DP Clients</option>
                              <option value="sales_pending_partners">Sales Pending Partners</option>
                              <option value="errors">Other Errors</option>
                              <option value="payment">Payment</option>
                            </select>
                            {(() => {
                              const key = `${yearSummaryData?.year || yearSummaryYear}-${monthData.month}`;
                              const last = lastViewedDataset[key];
                              if (!last || !DATASET_LABELS[last]) return null;
                              return (
                                <button
                                  type="button"
                                  onClick={() => openYearSummaryPreview(monthData.month, last)}
                                  className="text-[10.5px] px-1.5 py-0.5 rounded border border-indigo-300 bg-indigo-50 text-indigo-700 hover:bg-indigo-100 hover:border-indigo-500 whitespace-nowrap"
                                  title={`Reopen last view: ${DATASET_LABELS[last]}`}
                                  data-testid={`year-summary-reopen-${monthData.month}`}
                                >
                                  🕒 {DATASET_LABELS[last]}
                                </button>
                              );
                            })()}
                            <select
                              defaultValue=""
                              data-testid={`year-summary-download-${monthData.month}`}
                              onChange={(e) => {
                                const ds = e.target.value;
                                if (!ds) return;
                                const token = localStorage.getItem("token");
                                let url = `${process.env.REACT_APP_BACKEND_URL}/api/upload-center/year-summary/download?year=${yearSummaryData?.year}&month=${monthData.month}&dataset=${ds === "all_filtered" ? "all" : ds}`;
                                if (ds === "all_filtered") {
                                  // eslint-disable-next-line no-alert
                                  const cc = (window.prompt("Filter Close Pack to which client code? (e.g. CARECRAFT)") || "").trim();
                                  if (!cc) {
                                    e.target.value = "";
                                    return;
                                  }
                                  url += `&client_code=${encodeURIComponent(cc)}`;
                                }
                                fetch(url, { headers: { Authorization: `Bearer ${token}` } })
                                  .then(async (res) => {
                                    if (!res.ok) {
                                      const err = await res.json().catch(() => ({}));
                                      throw new Error(err.detail || `HTTP ${res.status}`);
                                    }
                                    const blob = await res.blob();
                                    const cd = res.headers.get("Content-Disposition") || "";
                                    const m = cd.match(/filename="([^"]+)"/);
                                    const filename = m ? m[1] : `${ds}_${monthData.month_name}${yearSummaryData?.year}.xlsx`;
                                    const blobUrl = URL.createObjectURL(blob);
                                    const a = document.createElement("a");
                                    a.href = blobUrl;
                                    a.download = filename;
                                    document.body.appendChild(a);
                                    a.click();
                                    a.remove();
                                    URL.revokeObjectURL(blobUrl);
                                    toast.success(`Downloaded ${filename}`);
                                  })
                                  .catch((err) => toast.error(err.message || "Download failed"))
                                  .finally(() => {
                                    e.target.value = "";
                                  });
                              }}
                              className="text-xs border border-slate-300 rounded px-2 py-0.5 bg-white hover:border-indigo-400 focus:outline-none focus:border-indigo-500"
                            >
                              <option value="">↓ Download</option>
                              <option value="all">📦 Close Pack (All)</option>
                              <option value="all_filtered">🎯 Close Pack (filter to client...)</option>
                              <option value="parcels">Parcels</option>
                              <option value="costing">Costing DP Clients</option>
                              <option value="costing_partners">Costing Partners</option>
                              <option value="partners_ledger">Partners Ledger</option>
                              <option value="sales_pending_dp">Sales Pending DP Clients</option>
                              <option value="sales_pending_partners">Sales Pending Partners</option>
                              <option value="errors">Other Errors</option>
                              <option value="payment">Payment</option>
                            </select>
                            </div>
                          </td>
                        </tr>
                        {/* Expanded per-day rows */}
                        {isExpanded && (
                          monthDaysLoading && dayRows.length === 0 ? (
                            <tr>
                              <td colSpan={15} className="text-center py-4 bg-indigo-50/30">
                                <RefreshCw className="w-4 h-4 animate-spin mx-auto text-indigo-400 inline-block" />
                                <span className="ml-2 text-xs text-indigo-600">Loading date-wise breakdown...</span>
                              </td>
                            </tr>
                          ) : dayRows.length === 0 ? (
                            <tr>
                              <td colSpan={15} className="text-center py-4 text-xs text-slate-400 bg-indigo-50/20">
                                No daily data for this month
                              </td>
                            </tr>
                          ) : (() => {
                            const selKey = (day) => `${yearSummaryYear}-${monthData.month}-${day}`;
                            const allSelected = dayRows.length > 0 && dayRows.every((d) => selectedDays[selKey(d.day)]);
                            const anySelected = dayRows.some((d) => selectedDays[selKey(d.day)]);
                            const selectedCount = dayRows.filter((d) => selectedDays[selKey(d.day)]).length;
                            return (
                              <>
                                <tr className="bg-indigo-50/60 border-b border-indigo-200" onClick={(e) => e.stopPropagation()}>
                                  <td colSpan={15} className="px-3 py-2">
                                    <div className="flex items-center justify-between gap-3 flex-wrap">
                                      <label className="flex items-center gap-2 text-xs text-slate-700 cursor-pointer">
                                        <input
                                          type="checkbox"
                                          checked={allSelected}
                                          ref={(el) => { if (el) el.indeterminate = !allSelected && anySelected; }}
                                          onChange={(e) => {
                                            const next = { ...selectedDays };
                                            dayRows.forEach((d) => {
                                              if (e.target.checked) next[selKey(d.day)] = true;
                                              else delete next[selKey(d.day)];
                                            });
                                            setSelectedDays(next);
                                          }}
                                          className="w-4 h-4 accent-indigo-600"
                                          data-testid={`bulk-select-all-${monthData.month}`}
                                        />
                                        <span className="font-semibold">Select all days</span>
                                        <span className="text-slate-500">({selectedCount}/{dayRows.length} selected)</span>
                                      </label>
                                      <div className="flex items-center gap-2">
                                        <select
                                          value={bulkDataset}
                                          onChange={(e) => setBulkDataset(e.target.value)}
                                          className="text-xs border border-slate-300 rounded px-2 py-1 bg-white"
                                          data-testid={`bulk-dataset-${monthData.month}`}
                                        >
                                          <option value="parcels">Parcels</option>
                                          <option value="costing">Costing DP</option>
                                          <option value="costing_partners">Costing Partners</option>
                                          <option value="partners_ledger">Partners Ledger</option>
                                          <option value="sales_pending_dp">Sales Pending DP</option>
                                          <option value="sales_pending_partners">Sales Pending Partners</option>
                                          <option value="errors">Errors</option>
                                          <option value="payment">Payment</option>
                                        </select>
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          disabled={selectedCount === 0 || bulkDownloading}
                                          className="h-7 text-xs border-indigo-300 text-indigo-700 hover:bg-indigo-100 disabled:opacity-50"
                                          data-testid={`bulk-download-${monthData.month}`}
                                          onClick={async () => {
                                            const days = dayRows.filter((d) => selectedDays[selKey(d.day)]).map((d) => d.day);
                                            if (days.length === 0) return;
                                            setBulkDownloading(true);
                                            try {
                                              const tk = localStorage.getItem("token");
                                              const url = `${process.env.REACT_APP_BACKEND_URL}/api/upload-center/year-summary/download-days?year=${yearSummaryYear}&month=${monthData.month}&days=${days.join(",")}&dataset=${bulkDataset}`;
                                              const res = await fetch(url, { headers: { Authorization: `Bearer ${tk}` } });
                                              if (!res.ok) {
                                                const err = await res.json().catch(() => ({}));
                                                throw new Error(err.detail || `HTTP ${res.status}`);
                                              }
                                              const blob = await res.blob();
                                              const cd = res.headers.get("Content-Disposition") || "";
                                              const m = cd.match(/filename="([^"]+)"/);
                                              const filename = m ? m[1] : `${bulkDataset}_bulk.xlsx`;
                                              const blobUrl = URL.createObjectURL(blob);
                                              const a = document.createElement("a");
                                              a.href = blobUrl;
                                              a.download = filename;
                                              document.body.appendChild(a);
                                              a.click();
                                              a.remove();
                                              URL.revokeObjectURL(blobUrl);
                                              toast.success(`Downloaded ${filename} (${days.length} day${days.length > 1 ? 's' : ''})`);
                                            } catch (err) {
                                              toast.error(err.message || "Bulk download failed");
                                            } finally {
                                              setBulkDownloading(false);
                                            }
                                          }}
                                        >
                                          {bulkDownloading ? <RefreshCw className="w-3 h-3 mr-1 animate-spin" /> : <Download className="w-3 h-3 mr-1" />}
                                          Download Selected ({selectedCount})
                                        </Button>
                                      </div>
                                    </div>
                                  </td>
                                </tr>
                                {dayRows.map((d) => {
                              // Compose a DD/MM/YY display
                              const [yr, mo, dd] = d.date.split("-");
                              const ddmmyy = `${dd}/${mo}/${String(yr).slice(-2)}`;
                              const sk = selKey(d.day);
                              const isSel = !!selectedDays[sk];
                              return (
                                <tr key={`${monthData.month}-${d.day}`} className={`border-b border-indigo-100 ${isSel ? 'bg-indigo-100/60' : 'bg-indigo-50/10 hover:bg-indigo-50/40'} text-[12px]`} data-testid={`year-summary-day-${monthData.month}-${d.day}`} onClick={(e) => e.stopPropagation()}>
                                  <td className="px-3 py-2 pl-6 font-mono text-slate-600">
                                    <label className="inline-flex items-center gap-2 cursor-pointer">
                                      <input
                                        type="checkbox"
                                        checked={isSel}
                                        onChange={(e) => {
                                          const next = { ...selectedDays };
                                          if (e.target.checked) next[sk] = true;
                                          else delete next[sk];
                                          setSelectedDays(next);
                                        }}
                                        className="w-3.5 h-3.5 accent-indigo-600"
                                        data-testid={`bulk-day-${monthData.month}-${d.day}`}
                                      />
                                      <span>{ddmmyy}</span>
                                    </label>
                                  </td>
                                  <td className="px-3 py-2 text-right">
                                    <div>
                                      <span className="font-semibold">{d.parcels.toLocaleString()}</span>
                                      {d.parcels > 0 && (
                                        <div className="text-[10px] text-muted-foreground mt-0.5">
                                          <span className="text-sky-600">{d.dp_client_parcels} DP</span>
                                          {" / "}
                                          <span className="text-violet-600">{d.partner_parcels} P</span>
                                        </div>
                                      )}
                                    </div>
                                  </td>
                                  <td className="px-3 py-2 text-center">
                                    {d.order_sheets_status === "yes" ? (
                                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-green-100 text-green-700 rounded text-[10px] font-medium">
                                        <CheckCircle className="w-2.5 h-2.5" /> Yes
                                      </span>
                                    ) : (
                                      <span className="text-slate-400 text-[10px]">No</span>
                                    )}
                                  </td>
                                  <td className="px-3 py-2 text-center">
                                    {d.packing_list_count > 0 ? (
                                      <div>
                                        <span className="text-cyan-700 font-semibold">{d.packing_list_count.toLocaleString()}</span>
                                        {d.parcels > 0 && (
                                          <div className="text-[10px] text-muted-foreground">
                                            {Math.round((d.packing_list_count / d.parcels) * 100)}%
                                          </div>
                                        )}
                                      </div>
                                    ) : <span className="text-slate-400">-</span>}
                                  </td>
                                  <td className="px-3 py-2 text-center">
                                    {d.all_costed > 0 ? (
                                      <div>
                                        <span className="text-amber-700 font-semibold">{d.all_costed.toLocaleString()}</span>
                                        <div className="text-[10px] text-muted-foreground">{d.all_costed}/{d.parcels}</div>
                                      </div>
                                    ) : d.parcels > 0 ? <span className="text-red-500 text-[10px]">0/{d.parcels}</span> : <span className="text-slate-400">-</span>}
                                  </td>
                                  <td className="px-3 py-2 text-center">
                                    {d.partner_with_sales > 0 ? (
                                      <div>
                                        <span className="text-teal-700 font-semibold">{d.partner_with_sales.toLocaleString()}</span>
                                        {d.partner_parcels > 0 && (
                                          <div className="text-[10px] text-muted-foreground">{d.partner_with_sales}/{d.partner_parcels}</div>
                                        )}
                                      </div>
                                    ) : d.partner_parcels > 0 ? <span className="text-red-500 text-[10px]">0/{d.partner_parcels}</span> : <span className="text-slate-400">-</span>}
                                  </td>
                                  <td className="px-3 py-2 text-center">
                                    {d.costing_errors > 0 ? (
                                      <span className="text-red-600 font-semibold">{d.costing_errors.toLocaleString()}</span>
                                    ) : d.parcels > 0 ? <CheckCircle className="w-3 h-3 text-green-600 mx-auto" /> : <span className="text-slate-400">-</span>}
                                  </td>
                                  <td className="px-3 py-2 text-center">
                                    {d.sales_errors > 0 ? (
                                      <span className="text-rose-500 font-semibold">{d.sales_errors.toLocaleString()}</span>
                                    ) : d.parcels > 0 ? <span className="text-slate-400 text-[10px]">0</span> : <span className="text-slate-400">-</span>}
                                  </td>
                                  <td className="px-3 py-2 text-center text-slate-400 text-[10px]">-</td>
                                  <td className="px-3 py-2 text-center">
                                    {d.payment_verified > 0 ? (
                                      <span className="text-indigo-700 font-semibold">{d.payment_verified.toLocaleString()}</span>
                                    ) : <span className="text-slate-400 text-[10px]">-</span>}
                                  </td>
                                  <td className="px-3 py-2 text-center text-slate-400 text-[10px]">-</td>
                                  <td className="px-3 py-2 text-center text-slate-400 text-[10px]">-</td>
                                  <td className="px-3 py-2 text-center" onClick={(e) => e.stopPropagation()}>
                                    <select
                                      defaultValue=""
                                      data-testid={`year-summary-day-download-${monthData.month}-${d.day}`}
                                      onChange={(e) => {
                                        const ds = e.target.value;
                                        if (!ds) return;
                                        const tk = localStorage.getItem("token");
                                        const url = `${process.env.REACT_APP_BACKEND_URL}/api/upload-center/year-summary/download?year=${yearSummaryData?.year}&month=${monthData.month}&day=${d.day}&dataset=${ds}`;
                                        fetch(url, { headers: { Authorization: `Bearer ${tk}` } })
                                          .then(async (res) => {
                                            if (!res.ok) {
                                              const err = await res.json().catch(() => ({}));
                                              throw new Error(err.detail || `HTTP ${res.status}`);
                                            }
                                            const blob = await res.blob();
                                            const cd = res.headers.get("Content-Disposition") || "";
                                            const m = cd.match(/filename="([^"]+)"/);
                                            const filename = m ? m[1] : `${ds}_${ddmmyy.replaceAll("/","")}.xlsx`;
                                            const blobUrl = URL.createObjectURL(blob);
                                            const a = document.createElement("a");
                                            a.href = blobUrl;
                                            a.download = filename;
                                            document.body.appendChild(a);
                                            a.click();
                                            a.remove();
                                            URL.revokeObjectURL(blobUrl);
                                            toast.success(`Downloaded ${filename}`);
                                          })
                                          .catch((err) => toast.error(err.message || "Download failed"))
                                          .finally(() => { e.target.value = ""; });
                                      }}
                                      className="text-[10px] border border-slate-300 rounded px-1.5 py-0.5 bg-white hover:border-indigo-400"
                                    >
                                      <option value="">↓</option>
                                      <option value="all">Close Pack</option>
                                      <option value="parcels">Parcels</option>
                                      <option value="costing">Costing DP</option>
                                      <option value="costing_partners">Costing Partners</option>
                                      <option value="partners_ledger">Partners Ledger</option>
                                      <option value="sales_pending_dp">Sales Pending DP</option>
                                      <option value="sales_pending_partners">Sales Pending Partners</option>
                                      <option value="errors">Errors</option>
                                      <option value="payment">Payment</option>
                                    </select>
                                  </td>
                                </tr>
                              );
                            })}
                              </>
                            );
                          })()
                        )}
                        </React.Fragment>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>

              <p className="text-xs text-slate-500 mt-3 text-center">
                Double-click on any row to edit verification status and add narration. 
                <span className="text-sky-600 ml-1">DP = DP Clients</span> / <span className="text-violet-600">P = Partners/Agents</span> / 
                <span className="text-amber-600 ml-1">c = costing errors</span> / <span className="text-rose-600">s = sales errors</span>
              </p>
            </CardContent>
          </Card>

          {/* Edit Year Summary Dialog */}
          <Dialog open={editYearSummaryDialog} onOpenChange={setEditYearSummaryDialog}>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <CalendarDays className="w-5 h-5 text-indigo-600" />
                  Edit {editingYearMonth?.month_name} {yearSummaryYear}
                </DialogTitle>
              </DialogHeader>

              <div className="space-y-4 py-4">
                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <span className="font-medium">Verified</span>
                  <button
                    onClick={() => setEditYearVerified(!editYearVerified)}
                    className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                      editYearVerified
                        ? "bg-green-100 text-green-700 border border-green-300"
                        : "bg-slate-100 text-slate-600 border border-slate-300"
                    }`}
                  >
                    {editYearVerified ? (
                      <span className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4" /> Yes
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        <XCircle className="w-4 h-4" /> No
                      </span>
                    )}
                  </button>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">
                    Narration <span className="text-slate-400 font-normal">(max 40 chars)</span>
                  </label>
                  <textarea
                    value={editYearNarration}
                    onChange={(e) => setEditYearNarration(e.target.value.slice(0, 40))}
                    placeholder="Add a note..."
                    maxLength={40}
                    rows={2}
                    className="w-full px-3 py-2 border rounded-md text-sm"
                  />
                  <p className="text-xs text-slate-400 text-right">{editYearNarration.length}/40</p>
                </div>

                {editingYearMonth?.last_updated && (
                  <p className="text-xs text-slate-500">
                    Last updated: {new Date(editingYearMonth.last_updated).toLocaleString()} by {editingYearMonth.updated_by}
                  </p>
                )}
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setEditYearSummaryDialog(false)}>
                  Cancel
                </Button>
                <Button onClick={handleUpdateYearVerification} className="bg-indigo-600 hover:bg-indigo-700">
                  Save Changes
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Reconcile Counts Dialog */}
          <Dialog open={reconcileDialog} onOpenChange={setReconcileDialog}>
            <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="reconcile-dialog">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <RefreshCw className="w-5 h-5 text-indigo-600" />
                  Reconcile counts — {yearSummaryYear}
                </DialogTitle>
                <DialogDescription>
                  Compares the dashboard badge counts against the row counts inside the per-month Excel downloads. Highlights any drift.
                </DialogDescription>
              </DialogHeader>

              {loadingReconcile ? (
                <div className="py-12 text-center text-slate-500">
                  <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-3" />
                  Crunching numbers...
                </div>
              ) : reconcileData ? (
                <div className="space-y-4">
                  <div className={`p-3 rounded-lg border text-sm ${
                    reconcileData.total_mismatches === 0
                      ? "bg-emerald-50 border-emerald-200 text-emerald-800"
                      : "bg-amber-50 border-amber-200 text-amber-800"
                  }`}>
                    {reconcileData.total_mismatches === 0 ? (
                      <span className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4" />
                        All months reconcile perfectly across all 5 datasets.
                      </span>
                    ) : (
                      <span>
                        <strong>{reconcileData.total_mismatches}</strong> dataset(s) have a count mismatch.{" "}
                        {reconcileData.note}
                      </span>
                    )}
                  </div>

                  <div className="flex items-center gap-2 text-xs">
                    <button
                      onClick={() => setReconcileShowAll((v) => !v)}
                      className="px-3 py-1 border rounded text-slate-600 hover:bg-slate-50"
                      data-testid="reconcile-toggle-all"
                    >
                      {reconcileShowAll ? "Show only mismatches" : "Show all months"}
                    </button>
                    <span className="text-slate-400">
                      Cells show <code className="px-1 bg-slate-100 rounded">dashboard / download</code>; deltas in red mean drift.
                    </span>
                  </div>

                  <div className="overflow-x-auto rounded-lg border">
                    <table className="w-full text-xs">
                      <thead className="bg-slate-100 text-slate-700">
                        <tr>
                          <th className="px-3 py-2 text-left">Month</th>
                          <th className="px-3 py-2 text-center">Parcels</th>
                          <th className="px-3 py-2 text-center">Costing</th>
                          <th className="px-3 py-2 text-center">Partners Ledger</th>
                          <th className="px-3 py-2 text-center">Errors</th>
                          <th className="px-3 py-2 text-center">Payment</th>
                        </tr>
                      </thead>
                      <tbody>
                        {reconcileData.months
                          .filter((m) => reconcileShowAll || m.mismatches > 0)
                          .map((m) => (
                            <tr key={m.month} className="border-t hover:bg-slate-50">
                              <td className="px-3 py-2 font-medium">{m.month_name}</td>
                              {["parcels", "costing", "partners_ledger", "errors", "payment"].map((ds) => {
                                const cell = m.datasets[ds];
                                const drift = cell.delta !== 0;
                                const baseClass = `px-3 py-2 text-center tabular-nums ${
                                  drift ? "bg-rose-50 text-rose-700 font-medium" : "text-slate-700"
                                }`;
                                const content = (
                                  <>
                                    {cell.dashboard.toLocaleString()} / {cell.download.toLocaleString()}
                                    {drift && (
                                      <div className="text-[10px] text-rose-500">
                                        Δ {cell.delta > 0 ? "+" : ""}
                                        {cell.delta} <span className="underline">view</span>
                                      </div>
                                    )}
                                  </>
                                );
                                return drift ? (
                                  <td
                                    key={ds}
                                    className={`${baseClass} cursor-pointer hover:bg-rose-100`}
                                    data-testid={`reconcile-${m.month}-${ds}`}
                                    onClick={() => fetchReconcileDetail(m.month, m.month_name, ds, cell)}
                                    title="Click to see the mismatched parcels"
                                  >
                                    {content}
                                  </td>
                                ) : (
                                  <td
                                    key={ds}
                                    className={baseClass}
                                    data-testid={`reconcile-${m.month}-${ds}`}
                                  >
                                    {content}
                                  </td>
                                );
                              })}
                            </tr>
                          ))}
                        {!reconcileShowAll &&
                          reconcileData.months.filter((m) => m.mismatches > 0).length === 0 && (
                            <tr>
                              <td colSpan={6} className="px-3 py-6 text-center text-slate-400 italic">
                                No mismatches. Toggle "Show all months" to view the full grid.
                              </td>
                            </tr>
                          )}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : null}

              <DialogFooter>
                <Button variant="outline" onClick={() => setReconcileDialog(false)}>
                  Close
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Reconcile Detail Dialog */}
          <Dialog open={reconcileDetailDialog} onOpenChange={setReconcileDetailDialog}>
            <DialogContent className="max-w-5xl max-h-[85vh] overflow-y-auto" data-testid="reconcile-detail-dialog">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-rose-600" />
                  {reconcileDetailMeta?.month_name} {yearSummaryYear} —{" "}
                  {reconcileDetailMeta?.dataset === "errors" ? "Both-error parcels" : "Mismatched parcels"}
                </DialogTitle>
                <DialogDescription>
                  {reconcileDetailMeta && (
                    <span>
                      Dashboard: <strong>{reconcileDetailMeta.dashboard?.toLocaleString()}</strong> &nbsp;|&nbsp;
                      Download: <strong>{reconcileDetailMeta.download?.toLocaleString()}</strong> &nbsp;|&nbsp;
                      Δ <span className="text-rose-600 font-semibold">{reconcileDetailMeta.delta > 0 ? "+" : ""}{reconcileDetailMeta.delta}</span>
                    </span>
                  )}
                </DialogDescription>
              </DialogHeader>

              {loadingReconcileDetail ? (
                <div className="py-12 text-center text-slate-500">
                  <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-3" />
                  Loading parcels...
                </div>
              ) : reconcileDetailData ? (
                <div className="space-y-3">
                  <div className="p-3 rounded-lg border bg-slate-50 text-xs text-slate-700">
                    {reconcileDetailData.note}
                  </div>

                  {reconcileDetailData.rows.length === 0 ? (
                    <div className="py-8 text-center text-slate-400 italic">
                      No mismatched parcels for this cell.
                    </div>
                  ) : (
                    <div className="overflow-x-auto rounded-lg border">
                      <table className="w-full text-xs">
                        <thead className="bg-slate-100 text-slate-700 sticky top-0">
                          <tr>
                            <th className="px-3 py-2 text-left">Tracking</th>
                            <th className="px-3 py-2 text-left">Client</th>
                            <th className="px-3 py-2 text-left">Customer</th>
                            <th className="px-3 py-2 text-center">Qty</th>
                            <th className="px-3 py-2 text-center">Gross Wt</th>
                            <th className="px-3 py-2 text-center">Total Cost</th>
                            <th className="px-3 py-2 text-center">Order Date</th>
                            <th className="px-3 py-2 text-center">Issues</th>
                            <th className="px-3 py-2 text-center">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {reconcileDetailData.rows.map((r, idx) => (
                            <tr key={`${r.tracking}-${idx}`} className="border-t hover:bg-rose-50/40">
                              <td className="px-3 py-2 font-mono">{r.tracking}</td>
                              <td className="px-3 py-2">{r.client_code}</td>
                              <td className="px-3 py-2 truncate max-w-[180px]" title={r.customer_name}>{r.customer_name}</td>
                              <td className="px-3 py-2 text-center tabular-nums">{r.qty ?? ""}</td>
                              <td className="px-3 py-2 text-center tabular-nums">{r.gross_weight ?? ""}</td>
                              <td className="px-3 py-2 text-center tabular-nums">{r.total_cost ?? 0}</td>
                              <td className="px-3 py-2 text-center font-mono">{formatDMY(r.order_date)}</td>
                              <td className="px-3 py-2 text-center">
                                {r.both_errors ? (
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-rose-100 text-rose-700 rounded-full text-[10px] font-medium">
                                    Costing + Sales
                                  </span>
                                ) : (
                                  <span className="text-slate-400">—</span>
                                )}
                              </td>
                              <td className="px-3 py-2 text-center whitespace-nowrap">
                                <div className="inline-flex gap-1">
                                  <button
                                    onClick={() => openInCosting(r.tracking)}
                                    data-testid={`reconcile-open-costing-${r.tracking}`}
                                    title="Open this parcel in Shipment Costing"
                                    className="px-2 py-1 text-[11px] border border-indigo-200 text-indigo-700 bg-white hover:bg-indigo-50 rounded"
                                  >
                                    Open
                                  </button>
                                  <button
                                    onClick={() => sendToPartner(r)}
                                    data-testid={`reconcile-send-partner-${r.tracking}`}
                                    title="Send a reminder via WhatsApp / email"
                                    className="px-2 py-1 text-[11px] border border-emerald-200 text-emerald-700 bg-white hover:bg-emerald-50 rounded"
                                  >
                                    Send
                                  </button>
                                  <button
                                    onClick={() => downloadClientClosePack(r.client_code)}
                                    data-testid={`reconcile-close-pack-${r.client_code}`}
                                    title="Download Close Pack filtered to this client (Excel)"
                                    className="px-2 py-1 text-[11px] border border-violet-200 text-violet-700 bg-white hover:bg-violet-50 rounded"
                                  >
                                    📦 Pack
                                  </button>
                                  <button
                                    onClick={() => markReconcileResolved(r.tracking)}
                                    data-testid={`reconcile-resolve-${r.tracking}`}
                                    title="Mark as acknowledged & hide from this view"
                                    className="px-2 py-1 text-[11px] border border-slate-200 text-slate-700 bg-white hover:bg-slate-50 rounded"
                                  >
                                    Resolve
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              ) : null}

              <DialogFooter>
                <Button variant="outline" onClick={() => setReconcileDetailDialog(false)}>
                  Close
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </TabsContent>

        {/* Pending Tab */}
        <TabsContent value="pending" className="mt-4 lg:mt-6">
          <div className="space-y-4">
            {/* Pending Shipment Costing */}
            <Card className="border-orange-200 bg-orange-50/50">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Package className="w-5 h-5 text-orange-600" />
                    Pending Shipment Costing
                  </CardTitle>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => window.open('/reports/pending-shipment-costing', '_blank')}
                  >
                    View Full Report
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
                <p className="text-sm text-slate-600 mt-1">
                  Check which months need Order Sheet and Packing List uploads from Jan 2018 onwards
                </p>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-4 text-sm">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-green-500" />
                    <span>Complete</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-yellow-500" />
                    <span>Partial (Orders only)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-red-500" />
                    <span>Pending</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Pending Sales by Client */}
            <Card className="border-purple-200 bg-purple-50/50">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Users className="w-5 h-5 text-purple-600" />
                    Pending Sales by Client
                  </CardTitle>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => window.open('/reports/pending-sales-by-client', '_blank')}
                  >
                    View Full Report
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
                <p className="text-sm text-slate-600 mt-1">
                  Auto-generated report showing clients with shipment data but missing sales/ledger uploads
                </p>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-slate-500">
                  Click "View Full Report" to see year-wise and month-wise breakdown per client with pending value amounts.
                </p>
              </CardContent>
            </Card>

            {/* Quick Links */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg">Quick Links to Pending Reports</CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Button 
                  variant="outline" 
                  className="justify-start h-auto py-3"
                  onClick={() => window.open('/reports/pending-sales', '_blank')}
                >
                  <Building2 className="w-5 h-5 mr-3 text-blue-600" />
                  <div className="text-left">
                    <div className="font-medium">Pending Partner Sales</div>
                    <div className="text-xs text-slate-500">Partners with shipments but no sales uploaded</div>
                  </div>
                </Button>
                <Button 
                  variant="outline" 
                  className="justify-start h-auto py-3"
                  onClick={() => window.open('/reports/pending-auto-generate', '_blank')}
                >
                  <AlertCircle className="w-5 h-5 mr-3 text-red-600" />
                  <div className="text-left">
                    <div className="font-medium">Pending Auto-Generate</div>
                    <div className="text-xs text-slate-500">Orders that failed auto-generation</div>
                  </div>
                </Button>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history" className="mt-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between gap-2">
              <CardTitle className="text-lg">{user?.role === 'super_admin' || user?.role === 'admin' ? 'All Upload History' : 'My Upload History'}</CardTitle>
              <div className="flex items-center gap-2">
                {(user?.role === 'super_admin' || user?.role === 'admin') && (
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleBackfillLedgers}
                      disabled={backfillingLedgers}
                      className="gap-1.5 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                      data-testid="backfill-ledgers-btn"
                      title="Scan all existing orders and auto-create any missing ledgers"
                    >
                      {backfillingLedgers ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Users className="w-4 h-4" />}
                      <span className="hidden sm:inline">Backfill ledgers</span>
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setBulkReprocessOpen(true)}
                      className="gap-1.5 border-indigo-300 text-indigo-700 hover:bg-indigo-50"
                      data-testid="bulk-reprocess-btn"
                    >
                      <RotateCw className="w-4 h-4" />
                      <span className="hidden sm:inline">Bulk re-process</span>
                    </Button>
                  </>
                )}
                <Button variant="outline" size="sm" onClick={fetchUploadHistory} disabled={loadingHistory}>
                  <RefreshCw className={`w-4 h-4 mr-2 ${loadingHistory ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {loadingHistory ? (
                <div className="text-center py-8 text-slate-500">Loading...</div>
              ) : uploadHistory.length === 0 ? (
                <div className="text-center py-12">
                  <History className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">No uploads yet</p>
                  <p className="text-sm text-slate-400">Your upload history will appear here</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {uploadHistory.map((upload, idx) => (
                    <div
                      key={idx}
                      className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border"
                    >
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div className={`p-2 rounded-lg shrink-0 ${upload.status === 'success' ? 'bg-green-100' : 'bg-red-100'}`}>
                          {upload.status === 'success' ? (
                            <CheckCircle className="w-4 h-4 text-green-600" />
                          ) : (
                            <XCircle className="w-4 h-4 text-red-600" />
                          )}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="font-medium text-slate-700 text-sm truncate">
                            {upload.file_name || 'Unknown file'}
                            {upload.total_uploads > 1 && (
                              <span className="ml-1.5 text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full font-normal">
                                ({upload.upload_number}/{upload.total_uploads})
                              </span>
                            )}
                          </p>
                          <p className="text-xs text-slate-500">
                            {(() => {
                              const typeMap = {
                                upload_order_sheet: 'Order Sheet',
                                upload_shipping_rates: 'Shipping Rates',
                                upload_partner_ledger: 'Partner Ledger',
                                upload_partner_ledger_structured: 'Partner Ledger',
                                upload_packing_list: 'Packing List',
                                upload_raw_partner_sales: 'Partner Sales',
                                upload_client_price_history: 'Price History',
                                upload_product_price_list: 'Product Prices',
                                file_upload: 'File Upload',
                              };
                              return typeMap[upload.upload_type || upload.action] || (upload.upload_type || upload.action || '').replace(/^upload_/i, '').replace(/_/g, ' ');
                            })()}
                            {upload.uploaded_by && (
                              <span className="ml-2 text-slate-400">by <span className="font-medium text-slate-600">{upload.uploaded_by}</span></span>
                            )}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 shrink-0 ml-2">
                        {upload.file_store?.available && (
                          <>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0"
                              title="View parsed contents"
                              data-testid={`history-view-btn-${idx}`}
                              onClick={() => openHistoryView(upload.file_store.id)}
                            >
                              <Eye className="w-4 h-4 text-slate-500" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0"
                              title="Download original file"
                              data-testid={`history-download-btn-${idx}`}
                              disabled={downloadingStoreId === upload.file_store.id}
                              onClick={() => downloadHistoryFile(upload.file_store.id, upload.file_name)}
                            >
                              {downloadingStoreId === upload.file_store.id ? (
                                <RefreshCw className="w-4 h-4 text-slate-500 animate-spin" />
                              ) : (
                                <Download className="w-4 h-4 text-slate-500" />
                              )}
                            </Button>
                            {/* Re-process — only enable for partner-ledger uploads.
                                Backend rejects anything else with a friendly message. */}
                            {(upload.module || "").toLowerCase().includes("ledger") && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0"
                                title="Re-run this upload through the latest parser (creates a NEW upload)"
                                data-testid={`history-reprocess-btn-${idx}`}
                                disabled={reprocessingId === upload.file_store.id}
                                onClick={() => reprocessHistoryUpload(upload.file_store.id, upload.file_name)}
                              >
                                {reprocessingId === upload.file_store.id ? (
                                  <RefreshCw className="w-4 h-4 text-indigo-500 animate-spin" />
                                ) : (
                                  <RotateCw className="w-4 h-4 text-indigo-500" />
                                )}
                              </Button>
                            )}
                          </>
                        )}
                        {upload.file_store?.purged && (
                          <Badge variant="outline" className="text-[10px] text-amber-600 border-amber-300 bg-amber-50" data-testid={`history-expired-badge-${idx}`}>
                            Expired
                          </Badge>
                        )}
                        {upload.file_store?.too_large && !upload.file_store?.purged && (
                          <Badge variant="outline" className="text-[10px] text-slate-400 border-slate-200">
                            Too large
                          </Badge>
                        )}
                      </div>
                      <div className="text-right shrink-0 ml-2">
                        <p className="text-xs text-slate-600">
                          {formatDMY(upload.timestamp)}
                        </p>
                        <p className="text-[10px] text-slate-400">
                          {new Date(upload.timestamp).toLocaleTimeString()}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Audit Tab */}
        <TabsContent value="audit" className="mt-4 lg:mt-6" data-testid="audit-tab-content">
          <UploadAuditPanel />
        </TabsContent>
      </Tabs>

      {/* Bulk re-process dialog — admins only */}
      <Dialog open={bulkReprocessOpen} onOpenChange={(o) => { setBulkReprocessOpen(o); if (!o) setBulkResult(null); }}>
        <DialogContent className="max-w-md" data-testid="bulk-reprocess-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RotateCw className="w-5 h-5 text-indigo-500" />
              Bulk Re-process — Ledger Uploads
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-600">
              Re-run every partner-ledger upload from the selected month through the latest parser. Creates new uploads; originals stay intact.
            </p>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs font-medium text-slate-600 mb-1 block">Month</label>
                <select
                  value={bulkMonth}
                  onChange={(e) => setBulkMonth(parseInt(e.target.value, 10))}
                  className="w-full h-9 px-2 rounded-md border bg-background text-sm"
                  data-testid="bulk-reprocess-month"
                >
                  {["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"].map((m, i) => (
                    <option key={i} value={i + 1}>{m}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600 mb-1 block">Year</label>
                <select
                  value={bulkYear}
                  onChange={(e) => setBulkYear(parseInt(e.target.value, 10))}
                  className="w-full h-9 px-2 rounded-md border bg-background text-sm"
                  data-testid="bulk-reprocess-year"
                >
                  {[2024, 2025, 2026, 2027].map((y) => <option key={y} value={y}>{y}</option>)}
                </select>
              </div>
            </div>

            {bulkResult && (
              <div className="rounded-lg border p-3 bg-slate-50 max-h-56 overflow-y-auto" data-testid="bulk-reprocess-results">
                <p className="text-sm font-semibold mb-1">
                  {bulkResult.processed} / {bulkResult.total_matched || bulkResult.results?.length || 0} succeeded
                </p>
                <ul className="space-y-1 text-xs">
                  {(bulkResult.results || []).slice(0, 40).map((r, i) => (
                    <li key={i} className="flex items-center gap-2">
                      <span className={`inline-block w-2 h-2 rounded-full flex-shrink-0 ${r.status === 'ok' ? 'bg-emerald-500' : r.status === 'error' ? 'bg-rose-500' : 'bg-slate-400'}`} />
                      <span className="truncate flex-1" title={r.file_name}>{r.file_name}</span>
                      {r.status === 'error' && <span className="text-rose-600 text-[10px]" title={r.error}>{r.error?.slice(0, 40)}</span>}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setBulkReprocessOpen(false)} disabled={bulkReprocessing}>
              Close
            </Button>
            <Button
              onClick={runBulkReprocess}
              disabled={bulkReprocessing}
              className="bg-indigo-600 hover:bg-indigo-700 text-white gap-1.5"
              data-testid="bulk-reprocess-run"
            >
              {bulkReprocessing ? <RefreshCw className="w-4 h-4 animate-spin" /> : <RotateCw className="w-4 h-4" />}
              {bulkReprocessing ? "Running…" : "Run"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upload History — original-file preview drawer */}
      <Sheet open={historyViewOpen} onOpenChange={setHistoryViewOpen}>
        <SheetContent side="right" className="w-full sm:max-w-3xl flex flex-col" data-testid="history-view-drawer">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2 text-base">
              <FileSpreadsheet className="w-4 h-4 text-emerald-600" />
              <span className="truncate">{historyView?.file_name || 'File preview'}</span>
            </SheetTitle>
            <SheetDescription>
              {historyView
                ? `${(historyView.size / 1024).toFixed(1)} KB · uploaded ${formatDMY(historyView.uploaded_at)} by ${historyView.uploaded_by || '—'}`
                : 'Parsed contents of the original upload'}
            </SheetDescription>
          </SheetHeader>
          {historyViewLoading ? (
            <div className="flex items-center justify-center flex-1 text-slate-500">
              <RefreshCw className="w-5 h-5 animate-spin mr-2" /> Parsing file…
            </div>
          ) : historyViewError ? (
            <div className="flex-1 flex items-center justify-center text-center p-6">
              <div>
                <AlertTriangle className="w-8 h-8 text-amber-500 mx-auto mb-2" />
                <p className="text-sm text-slate-600" data-testid="history-view-error">{historyViewError}</p>
              </div>
            </div>
          ) : historyView ? (
            <div className="flex-1 flex flex-col min-h-0 mt-2">
              {historyView.sheets.length > 1 && (
                <div className="flex gap-1 flex-wrap mb-2">
                  {historyView.sheets.map((s, i) => (
                    <button
                      key={i}
                      onClick={() => setHistoryViewSheetIdx(i)}
                      data-testid={`history-view-sheet-tab-${i}`}
                      className={`px-2 py-1 text-xs rounded border transition-colors ${
                        i === historyViewSheetIdx
                          ? 'bg-slate-800 text-white border-slate-800'
                          : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                      }`}
                    >
                      {s.name} <span className="opacity-60">({s.total_rows})</span>
                    </button>
                  ))}
                </div>
              )}
              {(() => {
                const sheet = historyView.sheets[historyViewSheetIdx];
                if (!sheet) return <p className="text-sm text-slate-500 p-4">No sheets found.</p>;
                return (
                  <div className="flex-1 overflow-auto border rounded-md min-h-0">
                    <table className="text-xs w-full border-collapse">
                      <thead className="sticky top-0 bg-slate-100 z-10">
                        <tr>
                          <th className="px-2 py-1.5 border-b text-left text-slate-400 font-normal">#</th>
                          {sheet.headers.map((h, i) => (
                            <th key={i} className="px-2 py-1.5 border-b text-left font-semibold text-slate-700 whitespace-nowrap">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {sheet.rows.map((row, ri) => (
                          <tr key={ri} className="odd:bg-white even:bg-slate-50/60">
                            <td className="px-2 py-1 border-b text-slate-400">{ri + 1}</td>
                            {sheet.headers.map((_, ci) => (
                              <td key={ci} className="px-2 py-1 border-b whitespace-nowrap text-slate-600">{row[ci] ?? ''}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {sheet.total_rows > sheet.rows.length && (
                      <p className="text-[11px] text-slate-400 p-2 text-center">
                        Showing first {sheet.rows.length} of {sheet.total_rows.toLocaleString()} rows — download the file for full data.
                      </p>
                    )}
                  </div>
                );
              })()}
            </div>
          ) : null}
        </SheetContent>
      </Sheet>

      {/* Upload Dialog */}
      <Dialog open={uploadDialog} onOpenChange={closeUploadDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="flex items-center gap-2">
              {selectedCategory && (
                <>
                  {React.createElement(selectedCategory.icon, { 
                    className: `w-5 h-5 ${selectedCategory.iconColor}` 
                  })}
                  {selectedUpload?.name}
                </>
              )}
            </DialogTitle>
            <DialogDescription>
              {selectedUpload?.description}
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto space-y-4 py-4 min-h-0">
            {/* Client Selector - Only for client-based uploads */}
            {selectedUpload?.requiresClient && (
              <div className="space-y-3 p-4 bg-indigo-50 rounded-lg border border-indigo-200">
                <div className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-indigo-600" />
                  <span className="text-sm font-medium text-indigo-700">Select Client</span>
                  {selectedClient && (
                    <Badge variant="secondary" className="ml-auto bg-indigo-100 text-indigo-700">
                      {selectedClient.client_code}
                    </Badge>
                  )}
                </div>
                
                {/* Search input */}
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search by client code or name..."
                    value={clientSearch}
                    onChange={(e) => setClientSearch(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-indigo-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                
                {/* Client list */}
                {loadingClients ? (
                  <div className="flex items-center justify-center py-4">
                    <RefreshCw className="w-5 h-5 animate-spin text-indigo-500" />
                    <span className="ml-2 text-sm text-indigo-600">Loading clients...</span>
                  </div>
                ) : (
                  <div className="max-h-40 overflow-y-auto space-y-1 border border-indigo-100 rounded-md bg-white">
                    {filteredClients.length === 0 ? (
                      <p className="text-sm text-slate-500 text-center py-3">No clients found</p>
                    ) : (
                      filteredClients.slice(0, 50).map((client) => (
                        <div
                          key={client.client_code}
                          data-testid={`client-option-${client.client_code}`}
                          className={`flex items-center justify-between px-3 py-2 cursor-pointer hover:bg-indigo-50 transition-colors ${
                            selectedClient?.client_code === client.client_code
                              ? 'bg-indigo-100 border-l-2 border-indigo-500'
                              : ''
                          }`}
                          onClick={() => setSelectedClient(client)}
                        >
                          <div>
                            <p className="text-sm font-medium text-slate-700">{client.client_code}</p>
                            {client.client_name && (
                              <p className="text-xs text-slate-500">{client.client_name}</p>
                            )}
                          </div>
                          {selectedClient?.client_code === client.client_code && (
                            <CheckCircle className="w-4 h-4 text-indigo-600" />
                          )}
                        </div>
                      ))
                    )}
                    {filteredClients.length > 50 && (
                      <p className="text-xs text-slate-400 text-center py-2">
                        Showing 50 of {filteredClients.length} - refine your search
                      </p>
                    )}
                  </div>
                )}
                
                {/* Replace existing option */}
                {selectedClient && (
                  <div className="flex items-center gap-2 pt-2">
                    <input
                      type="checkbox"
                      id="replaceExisting"
                      checked={replaceExisting}
                      onChange={(e) => setReplaceExisting(e.target.checked)}
                      className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                    />
                    <label htmlFor="replaceExisting" className="text-sm text-slate-600">
                      Replace existing price list (removes all current items)
                    </label>
                  </div>
                )}
              </div>
            )}

            {/* Download Template */}
            <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-blue-600" />
                <span className="text-sm font-medium text-blue-700">Need a template?</span>
              </div>
              {selectedUpload?.templateEndpoints ? (
                <div className="flex gap-2">
                  {selectedUpload.templateEndpoints.map((t, idx) => (
                    <Button
                      key={idx}
                      variant="outline"
                      size="sm"
                      onClick={() => handleDownloadTemplate(selectedUpload, t.endpoint)}
                      className="gap-1.5 border-blue-300 text-blue-600 hover:bg-blue-100 text-xs"
                    >
                      <Download className="w-3.5 h-3.5" />
                      {t.label}
                    </Button>
                  ))}
                </div>
              ) : (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleDownloadTemplate(selectedUpload)}
                  className="gap-2 border-blue-300 text-blue-600 hover:bg-blue-100"
                >
                  <Download className="w-4 h-4" />
                  Download
                </Button>
              )}
            </div>

            {/* Drag & Drop Zone */}
            <div
              className={`relative border-2 border-dashed rounded-xl p-8 text-center transition-all
                ${dragActive ? 'border-indigo-500 bg-indigo-50' : 'border-slate-300 hover:border-slate-400'}
                ${selectedFile ? 'bg-green-50 border-green-300' : ''}
              `}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileSelect}
                className="hidden"
              />
              
              {selectedFile ? (
                <div className="space-y-2">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                    <FileIcon className="w-6 h-6 text-green-600" />
                  </div>
                  <p className="font-medium text-green-700">{selectedFile.name}</p>
                  <p className="text-sm text-green-600">
                    {(selectedFile.size / 1024).toFixed(1)} KB
                  </p>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedFile(null);
                      setValidationResult(null);
                      setFilePreview(null);
                      if (fileInputRef.current) fileInputRef.current.value = '';
                    }}
                    className="text-red-500 hover:text-red-700"
                  >
                    <X className="w-4 h-4 mr-1" />
                    Remove
                  </Button>
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center mx-auto">
                    <Upload className="w-6 h-6 text-slate-400" />
                  </div>
                  <p className="text-slate-600">
                    <span className="font-medium text-indigo-600 cursor-pointer">Click to upload</span>
                    {" "}or drag and drop
                  </p>
                  <p className="text-sm text-slate-400">{selectedUpload?.acceptedFormats}</p>
                </div>
              )}
            </div>

            {/* Validating indicator */}
            {validating && (
              <div className="flex items-center gap-2 p-3 bg-blue-50 rounded-lg border border-blue-200">
                <RefreshCw className="w-4 h-4 text-blue-600 animate-spin" />
                <span className="text-sm text-blue-700">Validating file columns...</span>
              </div>
            )}

            {/* Required Columns Info */}
            {selectedUpload && REQUIRED_COLUMNS[selectedUpload.id] && !selectedFile && (
              <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                <div className="flex items-center gap-2 mb-2">
                  <Table className="w-4 h-4 text-slate-600" />
                  <span className="text-sm font-medium text-slate-700">Required Columns</span>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {REQUIRED_COLUMNS[selectedUpload.id].required.map((col) => (
                    <Badge key={col} variant="outline" className="text-xs bg-white border-slate-300">
                      {col}
                    </Badge>
                  ))}
                </div>
                {REQUIRED_COLUMNS[selectedUpload.id].optional?.length > 0 && (
                  <div className="mt-2">
                    <span className="text-xs text-slate-500">Optional: </span>
                    <span className="text-xs text-slate-400">
                      {REQUIRED_COLUMNS[selectedUpload.id].optional.join(", ")}
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* Frontend error strip — surfaces uncaught JS errors during validation/upload */}
            {frontendErrors.length > 0 && (
              <div
                className="p-3 rounded-lg border border-rose-300 bg-rose-50 space-y-1.5"
                data-testid="frontend-error-strip"
              >
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold text-rose-800 flex items-center gap-1.5">
                    <AlertTriangle className="w-4 h-4" />
                    Front-end error{frontendErrors.length === 1 ? "" : "s"} detected ({frontendErrors.length})
                  </p>
                  <button
                    type="button"
                    onClick={() => setFrontendErrors([])}
                    className="text-[11px] text-rose-700 hover:text-rose-900 underline"
                    data-testid="frontend-error-clear"
                  >
                    dismiss
                  </button>
                </div>
                <ul className="text-xs text-rose-700 space-y-1 max-h-28 overflow-y-auto font-mono leading-tight">
                  {frontendErrors.slice(-5).map((e, i) => (
                    <li key={i} className="break-all">
                      <span className="text-rose-500">[{e.at.slice(11, 19)}]</span> {e.msg}
                    </li>
                  ))}
                </ul>
                <p className="text-[11px] text-rose-600">
                  Some panels (parcel count, Data Quality, flagged-rows CSV) may be missing because of these errors. Send a screenshot to your dev so the bug can be patched.
                </p>
              </div>
            )}

            {/* Validation Result */}
            {validationResult && !uploadResult && (
              <div className={`p-3 rounded-lg border ${
                validationResult.valid 
                  ? 'bg-green-50 border-green-200' 
                  : 'bg-red-50 border-red-200'
              }`}>
                <div className="flex items-start gap-2">
                  {validationResult.valid ? (
                    <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                  ) : (
                    <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                  )}
                  <div className="flex-1">
                    <p className={`text-sm font-medium ${validationResult.valid ? 'text-green-700' : 'text-red-700'}`}>
                      {validationResult.valid ? 'File validated successfully!' : validationResult.error}
                    </p>
                    
                    {validationResult.valid && filePreview && (
                      <div className="mt-2 text-xs text-green-600 space-y-1">
                        <p>
                          <strong>{filePreview.rowCount?.toLocaleString?.() ?? filePreview.rowCount}</strong> rows found
                          {filePreview.orderSheetStats && (
                            <span className="ml-3">
                              · <strong className="text-indigo-700">{filePreview.orderSheetStats.uniqueParcels.toLocaleString()}</strong> unique tracking numbers / parcels
                            </span>
                          )}
                        </p>
                        {filePreview.orderSheetStats && filePreview.orderSheetStats.invalidTrackingRows > 0 && (
                          <p className="text-amber-700">
                            · {filePreview.orderSheetStats.invalidTrackingRows.toLocaleString()} row{filePreview.orderSheetStats.invalidTrackingRows === 1 ? '' : 's'} with invalid / placeholder tracking (routed to No-Tracking tab after upload)
                          </p>
                        )}
                        {filePreview.orderSheetStatsError && (
                          <p
                            className="text-rose-600 font-medium"
                            data-testid="order-sheet-stats-error"
                          >
                            · Preview scan failed: {filePreview.orderSheetStatsError}. Flagged-rows panel unavailable — upload will still work.
                          </p>
                        )}
                        {validationResult.foundColumns?.length > 0 && (
                          <p>Required columns: {validationResult.foundColumns.join(", ")}</p>
                        )}
                        {validationResult.optionalFound?.length > 0 && (
                          <p className="text-green-500">Optional found: {validationResult.optionalFound.join(", ")}</p>
                        )}
                        {filePreview.orderSheetStats && (
                          <div
                            className="mt-3 p-2.5 rounded-md bg-white border border-green-200 text-slate-700 space-y-2"
                            data-testid="order-sheet-preview-stats"
                          >
                            <div className="flex flex-wrap gap-x-4 gap-y-1 text-[13px]">
                              <span>
                                <strong className="text-indigo-700">
                                  {filePreview.orderSheetStats.uniqueParcels.toLocaleString()}
                                </strong> unique tracking numbers / parcels
                              </span>
                              <span>
                                <strong className="text-slate-800">
                                  {filePreview.orderSheetStats.totalRows.toLocaleString()}
                                </strong> tracking rows across {filePreview.orderSheetStats.perSheet.length} sheet{filePreview.orderSheetStats.perSheet.length === 1 ? '' : 's'}
                              </span>
                            </div>
                            {filePreview.orderSheetStats.comparison && (() => {
                              const cmp = filePreview.orderSheetStats.comparison;
                              const MONTH_NAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
                              const prevLabel = `${MONTH_NAMES[cmp.prevMonth - 1]} ${cmp.prevYear}`;
                              const targetLabel = `${MONTH_NAMES[cmp.targetMonth - 1]} ${cmp.targetYear}`;
                              const hasPrev = cmp.prevParcels > 0;
                              const pct = cmp.pct;
                              const up = pct == null ? null : pct >= 0;
                              const warn = pct != null && pct <= -15;
                              const tone = warn
                                ? 'bg-rose-50 border-rose-200 text-rose-700'
                                : up
                                  ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
                                  : up === false
                                    ? 'bg-amber-50 border-amber-200 text-amber-700'
                                    : 'bg-slate-50 border-slate-200 text-slate-600';
                              const arrow = up == null ? '' : up ? '▲' : '▼';
                              const sign = up ? '+' : '';
                              const errorsTotal = (filePreview.orderSheetStats.invalidTrackingRows || 0)
                                + (filePreview.orderSheetStats.missingClientRows || 0);
                              const deltaParcels = cmp.currentParcels - cmp.prevParcels;
                              const deltaErrors = errorsTotal - (cmp.prevErrors || 0);
                              return (
                                <div className="space-y-2" data-testid="preview-mom-comparison">
                                  {hasPrev ? (
                                    <div
                                      className={`text-[12px] px-2 py-1 rounded border inline-block ${tone}`}
                                      title={`${targetLabel}: ${cmp.currentParcels.toLocaleString()} parcels vs ${prevLabel}: ${cmp.prevParcels.toLocaleString()} parcels`}
                                    >
                                      {arrow} <strong>{sign}{pct.toFixed(1)}%</strong> vs {prevLabel} ({cmp.prevParcels.toLocaleString()} parcels)
                                      {warn && <span className="ml-1">— review before uploading</span>}
                                    </div>
                                  ) : (
                                    <div className="text-[12px] px-2 py-1 rounded bg-slate-50 border border-slate-200 text-slate-600 inline-block">
                                      No {prevLabel} data on file — nothing to compare.
                                    </div>
                                  )}

                                  {cmp.existingInTargetMonth > 0 && (
                                    <div
                                      data-testid="preview-already-uploaded"
                                      className="text-[12px] px-2 py-1 rounded bg-amber-50 border border-amber-200 text-amber-800 flex items-start gap-1.5"
                                    >
                                      <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                                      <span>
                                        <strong>{cmp.existingInTargetMonth.toLocaleString()}</strong> parcels for {targetLabel} already exist in the database.
                                        Duplicates will be skipped or flagged on upload.
                                      </span>
                                    </div>
                                  )}

                                  <div
                                    className="overflow-hidden rounded-md border border-slate-200 text-[12px]"
                                    data-testid="preview-comparison-table"
                                  >
                                    <table className="w-full bg-white text-slate-700">
                                      <thead className="bg-slate-50 text-slate-500">
                                        <tr>
                                          <th className="text-left font-medium px-2 py-1.5">Metric</th>
                                          <th className="text-right font-medium px-2 py-1.5">{prevLabel}</th>
                                          <th className="text-right font-medium px-2 py-1.5">This file ({targetLabel})</th>
                                          <th className="text-right font-medium px-2 py-1.5">Δ</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        <tr className="border-t border-slate-100">
                                          <td className="px-2 py-1.5">Unique parcels</td>
                                          <td className="text-right tabular-nums px-2 py-1.5">{cmp.prevParcels.toLocaleString()}</td>
                                          <td className="text-right tabular-nums px-2 py-1.5">{cmp.currentParcels.toLocaleString()}</td>
                                          <td className={`text-right tabular-nums px-2 py-1.5 ${deltaParcels >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
                                            {deltaParcels >= 0 ? '+' : ''}{deltaParcels.toLocaleString()}
                                          </td>
                                        </tr>
                                        <tr className="border-t border-slate-100">
                                          <td className="px-2 py-1.5">Tracking rows</td>
                                          <td className="text-right tabular-nums px-2 py-1.5 text-slate-400">—</td>
                                          <td className="text-right tabular-nums px-2 py-1.5">{filePreview.orderSheetStats.totalRows.toLocaleString()}</td>
                                          <td className="text-right tabular-nums px-2 py-1.5 text-slate-400">—</td>
                                        </tr>
                                        <tr className="border-t border-slate-100">
                                          <td className="px-2 py-1.5">Potential errors</td>
                                          <td className="text-right tabular-nums px-2 py-1.5">{(cmp.prevErrors || 0).toLocaleString()}</td>
                                          <td className="text-right tabular-nums px-2 py-1.5">{errorsTotal.toLocaleString()}</td>
                                          <td className={`text-right tabular-nums px-2 py-1.5 ${deltaErrors <= 0 ? 'text-emerald-700' : 'text-amber-700'}`}>
                                            {deltaErrors >= 0 ? '+' : ''}{deltaErrors.toLocaleString()}
                                          </td>
                                        </tr>
                                        {cmp.existingInTargetMonth > 0 && (
                                          <tr className="border-t border-slate-100 bg-amber-50/50">
                                            <td className="px-2 py-1.5">Already in DB for {targetLabel}</td>
                                            <td className="text-right tabular-nums px-2 py-1.5 text-slate-400">—</td>
                                            <td className="text-right tabular-nums px-2 py-1.5 text-amber-800">
                                              {cmp.existingInTargetMonth.toLocaleString()}
                                              {cmp.existingErrorsInTargetMonth > 0 && (
                                                <span className="text-amber-700"> ({cmp.existingErrorsInTargetMonth} errors)</span>
                                              )}
                                            </td>
                                            <td className="text-right px-2 py-1.5 text-slate-400">—</td>
                                          </tr>
                                        )}
                                      </tbody>
                                    </table>
                                  </div>
                                </div>
                              );
                            })()}
                            {(filePreview.orderSheetStats.invalidTrackingRows > 0 ||
                              filePreview.orderSheetStats.missingClientRows > 0) && (
                              <div className="flex flex-wrap gap-2 text-[12px]">
                                {filePreview.orderSheetStats.invalidTrackingRows > 0 && (
                                  <span
                                    data-testid="preview-invalid-tracking"
                                    className="px-2 py-0.5 rounded bg-amber-50 border border-amber-200 text-amber-700"
                                  >
                                    {filePreview.orderSheetStats.invalidTrackingRows} invalid / placeholder tracking
                                  </span>
                                )}
                                {filePreview.orderSheetStats.missingClientRows > 0 && (
                                  <span
                                    data-testid="preview-missing-client"
                                    className="px-2 py-0.5 rounded bg-amber-50 border border-amber-200 text-amber-700"
                                  >
                                    {filePreview.orderSheetStats.missingClientRows} rows without client code
                                  </span>
                                )}
                              </div>
                            )}
                            {filePreview.orderSheetStats.perSheet.length > 0 && (
                              <details className="text-[12px] text-slate-600">
                                <summary className="cursor-pointer select-none text-slate-500 hover:text-slate-700">
                                  Per-sheet breakdown
                                </summary>
                                <div className="mt-1.5 grid grid-cols-2 sm:grid-cols-3 gap-1">
                                  {filePreview.orderSheetStats.perSheet.map((s) => (
                                    <div
                                      key={s.name}
                                      className="flex items-center justify-between border border-slate-200 rounded px-2 py-1 bg-slate-50"
                                    >
                                      <span className="truncate mr-2" title={s.name}>{s.name}</span>
                                      <span className="tabular-nums text-slate-700">
                                        {s.parcels}
                                        {s.invalid > 0 && (
                                          <span className="ml-1 text-amber-600">({s.invalid} bad)</span>
                                        )}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              </details>
                            )}

                            {/* Data Quality Check (combined sheet only) */}
                            {filePreview.orderSheetStats.dataQuality && (() => {
                              const dq = filePreview.orderSheetStats.dataQuality;
                              const total = dq.totalFlaggedRows || 0;
                              if (total === 0) {
                                return (
                                  <div
                                    data-testid="data-quality-clean"
                                    className="mt-2 text-[12px] px-2 py-1.5 rounded bg-emerald-50 border border-emerald-200 text-emerald-700 inline-block"
                                  >
                                    Data quality looks clean — no suspicious rows detected.
                                  </div>
                                );
                              }
                              const items = [
                                { k: 'missing_tracking', label: 'Missing tracking', tone: 'rose' },
                                { k: 'missing_client_code', label: 'Missing client code', tone: 'rose' },
                                { k: 'missing_product', label: 'Missing product', tone: 'rose' },
                                { k: 'missing_parcel_dt', label: 'Missing parcel date', tone: 'amber' },
                                { k: 'missing_country', label: 'Missing country', tone: 'rose' },
                                { k: 'bad_country', label: 'Suspicious country', tone: 'amber' },
                                { k: 'unknown_service', label: 'Unknown / missing service', tone: 'amber' },
                                { k: 'missing_qty', label: 'Missing / zero qty', tone: 'amber' },
                                { k: 'missing_weight', label: 'Missing / zero weight', tone: 'amber' },
                                { k: 'duplicate_tracking', label: 'Duplicate tracking (collision)', tone: 'rose' },
                              ].filter((i) => (dq.counts?.[i.k] || 0) > 0);
                              const toneMap = {
                                rose: 'bg-rose-50 border-rose-200 text-rose-700',
                                amber: 'bg-amber-50 border-amber-200 text-amber-700',
                              };
                              return (
                                <div
                                  data-testid="data-quality-panel"
                                  className="mt-2 rounded-md border border-rose-200 bg-rose-50/30 p-2 text-[12px] space-y-1.5"
                                >
                                  <div className="flex items-center justify-between gap-2 font-medium text-rose-700">
                                    <div className="flex items-center gap-1.5">
                                      <AlertTriangle className="w-3.5 h-3.5" />
                                      Data quality — {total.toLocaleString()} row{total === 1 ? '' : 's'} flagged
                                      {dq.flaggedRows && dq.flaggedRows.length < total && (
                                        <span className="text-[11px] font-normal text-slate-500">
                                          (first {dq.flaggedRows.length.toLocaleString()} saved for CSV)
                                        </span>
                                      )}
                                    </div>
                                    <div className="flex items-center gap-1.5">
                                      <input
                                        type="file"
                                        accept=".csv,text/csv"
                                        id="corrections-csv-input"
                                        className="hidden"
                                        onChange={(e) => {
                                          const f = e.target.files?.[0];
                                          if (f) applyCsvCorrections(f);
                                          e.target.value = '';
                                        }}
                                        data-testid="corrections-csv-input"
                                      />
                                      <label
                                        htmlFor="corrections-csv-input"
                                        className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-1 rounded border border-emerald-300 text-emerald-700 hover:bg-white transition-colors cursor-pointer"
                                        data-testid="reimport-corrections-btn"
                                        title="Re-import a fixed CSV to patch only flagged rows"
                                      >
                                        <Upload className="w-3 h-3" /> Re-import fixed CSV
                                      </label>
                                      <button
                                        type="button"
                                        onClick={downloadFlaggedRowsCsv}
                                        className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-1 rounded border border-rose-300 text-rose-700 hover:bg-white transition-colors"
                                        data-testid="download-flagged-csv-btn"
                                        title="Download flagged rows as CSV to fix offline"
                                      >
                                        <Download className="w-3 h-3" /> Download CSV
                                      </button>
                                    </div>
                                  </div>
                                  {correctionsSummary && (
                                    <div
                                      data-testid="corrections-applied-summary"
                                      className="text-[11px] px-2 py-1 rounded bg-emerald-50 border border-emerald-200 text-emerald-800"
                                    >
                                      Corrections applied:
                                      <strong className="mx-1">{correctionsSummary.patched.toLocaleString()}</strong>rows patched
                                      {correctionsSummary.unmatched > 0 && (
                                        <span className="ml-1 text-amber-700">
                                          · <strong>{correctionsSummary.unmatched.toLocaleString()}</strong> unmatched (not found in current sheet)
                                        </span>
                                      )}
                                      <span className="ml-1 text-slate-500">— patched workbook will be used on Upload.</span>
                                    </div>
                                  )}
                                  <div className="flex flex-wrap gap-1.5">
                                    {items.map((i) => (
                                      <span
                                        key={i.k}
                                        data-testid={`dq-${i.k.replace(/_/g, '-')}`}
                                        className={`px-2 py-0.5 rounded border ${toneMap[i.tone]}`}
                                        title={(dq.samples?.[i.k] || []).slice(0, 5).join(' · ')}
                                      >
                                        {i.label}: <strong>{(dq.counts[i.k] || 0).toLocaleString()}</strong>
                                      </span>
                                    ))}
                                  </div>
                                  <details className="text-slate-600">
                                    <summary className="cursor-pointer select-none text-slate-500 hover:text-slate-700">
                                      Show sample values
                                    </summary>
                                    <div className="mt-1 grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                                      {items.map((i) => (
                                        <div key={i.k} className="bg-white border border-slate-200 rounded px-2 py-1">
                                          <div className="font-medium text-slate-700">{i.label}</div>
                                          <div className="text-[11px] text-slate-500 truncate" title={(dq.samples?.[i.k] || []).join(' · ')}>
                                            {(dq.samples?.[i.k] || []).slice(0, 5).join(' · ') || '—'}
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  </details>
                                  {dq.duplicateGroups?.length > 0 && (
                                    <details
                                      className="text-slate-600"
                                      data-testid="duplicate-tracking-details"
                                    >
                                      <summary className="cursor-pointer select-none text-rose-600 hover:text-rose-800 font-medium">
                                        Show all rows with shared tracking ({dq.duplicateGroups.length} collision{dq.duplicateGroups.length === 1 ? '' : 's'})
                                      </summary>
                                      <div className="mt-2 max-h-72 overflow-y-auto rounded border border-rose-200 bg-white">
                                        {dq.duplicateGroups.map((g) => (
                                          <div
                                            key={g.tracking}
                                            className="border-b border-rose-100 last:border-b-0 px-2 py-1.5"
                                            data-testid={`duplicate-group-${g.tracking}`}
                                          >
                                            <div className="font-mono text-[11px] font-semibold text-rose-700">
                                              {g.tracking}
                                            </div>
                                            <div className="mt-1 grid gap-0.5">
                                              {g.occurrences.map((occ, idx) => (
                                                <div
                                                  key={idx}
                                                  className="text-[11px] text-slate-700 flex flex-wrap gap-x-2"
                                                >
                                                  <span className="text-slate-400">{occ.sheet}:r{occ.row}</span>
                                                  <span className="text-indigo-700">{occ.client_code || '—'}</span>
                                                  <span className="truncate">{occ.customer || '(no name)'}</span>
                                                  <span className="text-slate-500">{occ.country || '—'}</span>
                                                </div>
                                              ))}
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    </details>
                                  )}
                                  <div className="text-[11px] text-slate-500">
                                    Tip: fix these in the source sheet before uploading to avoid zero-cost parcels. Upload will still proceed.
                                  </div>
                                </div>
                              );
                            })()}
                          </div>
                        )}
                      </div>
                    )}
                    
                    {!validationResult.valid && (
                      <div className="mt-2 text-xs text-red-600 space-y-1">
                        <p>{validationResult.details}</p>
                        {validationResult.missingColumns?.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-1">
                            <span className="text-red-700">Missing:</span>
                            {validationResult.missingColumns.map((col) => (
                              <Badge key={col} variant="destructive" className="text-xs">
                                {col}
                              </Badge>
                            ))}
                          </div>
                        )}
                        {validationResult.foundColumns?.length > 0 && (
                          <p className="text-slate-500 mt-1">
                            Found: {validationResult.foundColumns.join(", ")}
                          </p>
                        )}
                      </div>
                    )}
                    
                    {validationResult.warning && (
                      <p className="mt-1 text-xs text-amber-600">{validationResult.warning}</p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* File Preview - Column Headers */}
            {filePreview && validationResult?.valid && !uploadResult && (
              <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-slate-600">Detected Columns</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 text-xs"
                    onClick={() => {
                      navigator.clipboard.writeText(filePreview.headers.join(", "));
                      toast.success("Columns copied to clipboard");
                    }}
                  >
                    <Copy className="w-3 h-3 mr-1" />
                    Copy
                  </Button>
                </div>
                <div className="flex flex-wrap gap-1 max-h-20 overflow-y-auto">
                  {filePreview.headers.map((header, idx) => (
                    <Badge 
                      key={idx} 
                      variant="secondary" 
                      className="text-xs bg-slate-200 text-slate-800"
                    >
                      {header || "(empty)"}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Upload Progress */}
            {uploading && (
              <div className="space-y-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-600 flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
                    {progressMessage || "Uploading..."}
                  </span>
                  <span className="font-medium text-orange-600">{uploadProgress}%</span>
                </div>
                <Progress value={uploadProgress} className="h-2" />
                
                {/* Progress stages for Partner Ledger uploads */}
                {selectedUpload?.id === 'partner_ledger_structured' && (
                  <div className="grid grid-cols-4 gap-1 text-xs">
                    <div className={`flex items-center gap-1 ${uploadProgress >= 10 ? 'text-green-600' : 'text-slate-400'}`}>
                      <div className={`w-2 h-2 rounded-full ${uploadProgress >= 10 ? 'bg-green-500' : 'bg-slate-300'}`} />
                      Prepare
                    </div>
                    <div className={`flex items-center gap-1 ${uploadProgress >= 40 ? 'text-green-600' : 'text-slate-400'}`}>
                      <div className={`w-2 h-2 rounded-full ${uploadProgress >= 40 ? 'bg-green-500' : 'bg-slate-300'}`} />
                      Upload
                    </div>
                    <div className={`flex items-center gap-1 ${uploadProgress >= 80 ? 'text-green-600' : 'text-slate-400'}`}>
                      <div className={`w-2 h-2 rounded-full ${uploadProgress >= 80 ? 'bg-green-500' : 'bg-slate-300'}`} />
                      Parse
                    </div>
                    <div className={`flex items-center gap-1 ${uploadProgress >= 95 ? 'text-green-600' : 'text-slate-400'}`}>
                      <div className={`w-2 h-2 rounded-full ${uploadProgress >= 95 ? 'bg-green-500' : 'bg-slate-300'}`} />
                      Complete
                    </div>
                  </div>
                )}
                
                {backgroundTaskId && (
                  <p className="text-xs text-blue-500 text-center">
                    Large file - processing in background. Please wait...
                  </p>
                )}
              </div>
            )}

            {/* Upload Result */}
            {uploadResult && (
              <div className={`p-4 rounded-lg border ${
                uploadResult.success 
                  ? 'bg-green-50 border-green-200' 
                  : 'bg-red-50 border-red-200'
              }`}>
                <div className="flex items-start gap-3">
                  {uploadResult.success ? (
                    <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                  ) : (
                    <XCircle className="w-5 h-5 text-red-600 mt-0.5" />
                  )}
                  <div className="flex-1">
                    <p className={`font-medium ${uploadResult.success ? 'text-green-700' : 'text-red-700'}`}>
                      {uploadResult.success ? 'Upload Successful!' : 'Upload Failed'}
                    </p>
                    <p className={`text-sm mt-1 ${uploadResult.success ? 'text-green-600' : 'text-red-600'}`}>
                      {uploadResult.message}
                    </p>
                    
                    {/* Show details for successful uploads */}
                    {uploadResult.success && uploadResult.details && (
                      <div className="mt-3 p-3 bg-white rounded-lg border text-sm space-y-2">
                        {/* Partner Ledger Summary Stats */}
                        {uploadResult.isPartnerLedger && uploadResult.details._summary && (
                          <div className="grid grid-cols-4 gap-2 text-center">
                            <div className="p-2 bg-blue-50 rounded">
                              <p className="text-lg font-bold text-blue-700">{uploadResult.details._summary.entries}</p>
                              <p className="text-xs text-blue-600">Entries</p>
                            </div>
                            <div className="p-2 bg-green-50 rounded">
                              <p className="text-lg font-bold text-green-700">{uploadResult.details._summary.payments}</p>
                              <p className="text-xs text-green-600">Payments</p>
                            </div>
                            <div className="p-2 bg-emerald-50 rounded">
                              <p className="text-lg font-bold text-emerald-700">{uploadResult.details._summary.matched}</p>
                              <p className="text-xs text-emerald-600">Matched</p>
                            </div>
                            <div className="p-2 bg-amber-50 rounded">
                              <p className="text-lg font-bold text-amber-700">{uploadResult.details._summary.unmatched}</p>
                              <p className="text-xs text-amber-600">Unmatched</p>
                            </div>
                          </div>
                        )}
                        
                        {/* Partner Ledger Partner Details */}
                        {uploadResult.isPartnerLedger && uploadResult.details.partners?.length > 0 && (
                          <div className="mt-2 p-2 bg-slate-50 rounded border">
                            <p className="text-xs font-medium text-slate-700 mb-2">Partner Breakdown:</p>
                            {uploadResult.details.partners.map((partner, idx) => (
                              <div key={idx} className="flex items-center justify-between py-1 border-b border-slate-200 last:border-0">
                                <span className="font-medium text-slate-800">{partner.partner_code}</span>
                                <div className="flex items-center gap-3 text-xs">
                                  <span className="text-blue-600">{partner.entries?.length || 0} entries</span>
                                  <span className="text-green-600">{partner.payments_detected || 0} payments</span>
                                  {!partner.ledger_found && (
                                    <span className="text-amber-600 font-medium">New partner</span>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                        
                        {/* Generic Summary Stats (for non-partner-ledger uploads) */}
                        {!uploadResult.isPartnerLedger && (
                          <div className="grid grid-cols-3 gap-2 text-center">
                          {uploadResult.details.added !== undefined && (
                            <div className="p-2 bg-green-50 rounded">
                              <p className="text-lg font-bold text-green-700">{uploadResult.details.added}</p>
                              <p className="text-xs text-green-600">Added</p>
                            </div>
                          )}
                          {uploadResult.details.updated !== undefined && (
                            <div className="p-2 bg-blue-50 rounded">
                              <p className="text-lg font-bold text-blue-700">{uploadResult.details.updated}</p>
                              <p className="text-xs text-blue-600">Updated</p>
                            </div>
                          )}
                          {uploadResult.details.skipped !== undefined && (
                            <div className="p-2 bg-slate-50 rounded">
                              <p className="text-lg font-bold text-slate-700">{uploadResult.details.skipped}</p>
                              <p className="text-xs text-slate-600">Skipped</p>
                            </div>
                          )}
                        </div>
                        )}
                        
                        {/* Additional details */}
                        {!uploadResult.isPartnerLedger && uploadResult.details.matched !== undefined && (
                          <p className="text-xs text-slate-600">
                            Matched: <strong>{uploadResult.details.matched}</strong>
                          </p>
                        )}
                        {!uploadResult.isPartnerLedger && uploadResult.details.total !== undefined && (
                          <p className="text-xs text-slate-600">
                            Total processed: <strong>{uploadResult.details.total}</strong>
                          </p>
                        )}
                        
                        {/* Errors section - enhanced */}
                        {uploadResult.details.errors?.length > 0 && (
                          <div className="mt-2 p-2 bg-amber-50 rounded border border-amber-200">
                            <div className="flex items-center justify-between mb-2">
                              <p className="text-amber-700 font-medium text-sm">
                                <AlertTriangle className="w-4 h-4 inline mr-1" />
                                {uploadResult.details.errors.length} rows with issues
                              </p>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 text-xs text-amber-600"
                                onClick={() => {
                                  navigator.clipboard.writeText(uploadResult.details.errors.join("\n"));
                                  toast.success("Errors copied to clipboard");
                                }}
                              >
                                <Copy className="w-3 h-3 mr-1" />
                                Copy All
                              </Button>
                            </div>
                            <div className="max-h-32 overflow-y-auto space-y-1">
                              {uploadResult.details.errors.map((err, i) => (
                                <div key={i} className="text-xs text-slate-700 p-1 bg-white rounded flex items-start gap-1">
                                  <span className="text-amber-500 flex-shrink-0">Row {i + 1}:</span>
                                  <span className="break-words">{typeof err === 'string' ? err : JSON.stringify(err)}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {/* Partner Ledger specific results */}
                        {uploadResult.isPartnerLedger && (
                          <div className="mt-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
                            <div className="flex items-start gap-2">
                              <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
                              <div>
                                <p className="font-medium text-amber-800">Review Required</p>
                                <p className="text-sm text-amber-700 mt-1">
                                  The uploaded data needs to be reviewed and processed in the Ledger Reconciliation page.
                                </p>
                                <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
                                  <div className="p-2 bg-white rounded text-center">
                                    <p className="text-lg font-bold text-blue-600">{uploadResult.details.total_entries_detected || 0}</p>
                                    <p className="text-xs text-slate-600">Entries Detected</p>
                                  </div>
                                  <div className="p-2 bg-white rounded text-center">
                                    <p className="text-lg font-bold text-green-600">{uploadResult.details.total_payments_detected || 0}</p>
                                    <p className="text-xs text-slate-600">Payments Detected</p>
                                  </div>
                                </div>
                                <Button
                                  className="mt-3 w-full bg-amber-600 hover:bg-amber-700"
                                  onClick={() => {
                                    window.open('/ledger-reconciliation', '_blank');
                                  }}
                                >
                                  <ChevronRight className="w-4 h-4 mr-1" />
                                  Go to Ledger Reconciliation
                                </Button>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                    
                    {/* Show details for failed uploads */}
                    {!uploadResult.success && uploadResult.details && (
                      <div className="mt-2 text-xs text-red-600">
                        {uploadResult.details.detail && <p>{uploadResult.details.detail}</p>}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          <DialogFooter className="flex-shrink-0 gap-2 border-t pt-4 mt-2">
            <Button variant="outline" onClick={closeUploadDialog}>
              {uploadResult ? 'Close' : 'Cancel'}
            </Button>
            {!uploadResult && (
              <Button
                onClick={handleUpload}
                disabled={
                  !selectedFile || 
                  uploading || 
                  validating || 
                  (validationResult && !validationResult.valid) ||
                  (selectedUpload?.requiresClient && !selectedClient)
                }
                className="gap-2 bg-indigo-600 hover:bg-indigo-700"
              >
                {uploading ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4" />
                    {selectedUpload?.requiresClient && selectedClient 
                      ? `Upload for ${selectedClient.client_code}` 
                      : 'Upload File'}
                  </>
                )}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Weight Source Dialog - shown after packing list upload */}
      <Dialog open={showWeightDialog} onOpenChange={setShowWeightDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="w-5 h-5 text-cyan-600" />
              Weight Source Selection
            </DialogTitle>
            <DialogDescription>
              Packing list uploaded successfully. Choose weight source to apply to existing orders.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-3">
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-xs text-emerald-700" data-testid="packing-reupload-note">
              <strong>Re-uploading?</strong> Totally safe — matching parcels are <strong>updated in place</strong> (weights, products &amp; shipping recalculated). Nothing gets duplicated, no orders are deleted, and parcels not in this sheet stay untouched.
            </div>
            <label
              className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${weightSource === 'price_list' ? 'border-blue-300 bg-blue-50' : 'border-slate-200 hover:bg-slate-50'}`}
              onClick={() => setWeightSource("price_list")}
            >
              <input type="radio" name="ws" checked={weightSource === "price_list"} onChange={() => setWeightSource("price_list")} className="w-4 h-4 text-blue-600" />
              <div>
                <div className="font-medium text-sm">Price List (Default)</div>
                <div className="text-xs text-slate-500">Calculate weight from product weight x quantity</div>
              </div>
            </label>
            <label
              className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${weightSource === 'packing_list' ? 'border-cyan-300 bg-cyan-50' : 'border-slate-200 hover:bg-slate-50'}`}
              onClick={() => setWeightSource("packing_list")}
            >
              <input type="radio" name="ws" checked={weightSource === "packing_list"} onChange={() => setWeightSource("packing_list")} className="w-4 h-4 text-cyan-600" />
              <div>
                <div className="font-medium text-sm text-cyan-700">Actual Weight (Packing List)</div>
                <div className="text-xs text-cyan-600">Use actual parcel weight from packing sheet</div>
              </div>
            </label>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-700">
              <strong>Note:</strong> This will update existing order weights and recalculate shipping costs based on selected weight source.
              Missing products from packing list will also be synced.
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" size="sm" onClick={() => setShowWeightDialog(false)}>Skip</Button>
            <Button size="sm" onClick={handleApplyPackingData} disabled={applyingPacking} className="bg-cyan-600 hover:bg-cyan-700" data-testid="apply-packing-btn">
              {applyingPacking ? "Applying..." : "Apply & Recalculate"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Post-Upload Summary Dialog */}
      <UploadSummaryDialog
        open={!!uploadSummaryResult}
        onClose={() => setUploadSummaryResult(null)}
        result={uploadSummaryResult}
        token={token}
        onRefresh={() => {}}
      />

      {/* Smart Duplicate Resolution Dialog */}
      <Dialog open={showDuplicateDialog} onOpenChange={setShowDuplicateDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-orange-600">
              <AlertTriangle className="w-5 h-5" />
              Duplicate Tracking Numbers Found
            </DialogTitle>
            <DialogDescription>
              {duplicateInfo.count} tracking number(s) already exist in the system.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Smart Breakdown Panel */}
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
              <p className="text-sm font-semibold text-slate-700 mb-3">Analysis of {duplicateInfo.count} duplicates:</p>
              <div className="grid grid-cols-3 gap-2 text-center">
                {duplicateInfo.unassigned_count > 0 && (
                  <div className="bg-amber-100 border border-amber-300 rounded-lg p-2">
                    <p className="text-2xl font-bold text-amber-700">{duplicateInfo.unassigned_count}</p>
                    <p className="text-xs text-amber-600">Unassigned</p>
                    <p className="text-xs text-amber-500">(no client code)</p>
                  </div>
                )}
                {duplicateInfo.error_count > 0 && (
                  <div className="bg-red-100 border border-red-300 rounded-lg p-2">
                    <p className="text-2xl font-bold text-red-700">{duplicateInfo.error_count}</p>
                    <p className="text-xs text-red-600">With Errors</p>
                    <p className="text-xs text-red-500">(product/cost issues)</p>
                  </div>
                )}
                {duplicateInfo.clean_count > 0 && (
                  <div className="bg-green-100 border border-green-300 rounded-lg p-2">
                    <p className="text-2xl font-bold text-green-700">{duplicateInfo.clean_count}</p>
                    <p className="text-xs text-green-600">Clean</p>
                    <p className="text-xs text-green-500">(properly processed)</p>
                  </div>
                )}
              </div>
              {duplicateInfo.problem_count > 0 && (
                <p className="text-sm text-slate-600 mt-3 text-center">
                  <span className="font-semibold text-orange-600">{duplicateInfo.problem_count}</span> records need fixing (unassigned + errors)
                </p>
              )}
            </div>
            
            {/* Options Panel */}
            <div className="space-y-2">
              <p className="text-sm font-semibold text-slate-700">Choose an action:</p>
              
              {/* Option 1: Skip All */}
              <div 
                className={`border rounded-lg p-3 cursor-pointer transition-all ${
                  duplicateAction === "skip" 
                    ? "border-blue-500 bg-blue-50" 
                    : "border-slate-200 hover:border-slate-300"
                }`}
                onClick={() => setDuplicateAction("skip")}
              >
                <div className="flex items-center gap-2">
                  <div className={`w-4 h-4 rounded-full border-2 ${duplicateAction === "skip" ? "border-blue-500 bg-blue-500" : "border-slate-300"}`}>
                    {duplicateAction === "skip" && <div className="w-2 h-2 bg-white rounded-full m-0.5" />}
                  </div>
                  <span className="font-medium text-slate-700">Skip All Duplicates</span>
                  <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">Safe</span>
                </div>
                <p className="text-xs text-slate-500 ml-6 mt-1">Keep all existing records unchanged, only upload new tracking numbers</p>
              </div>
              
              {/* Option 2: Update Problems Only (SMART) - only show if there are problems */}
              {duplicateInfo.problem_count > 0 && (
                <div 
                  className={`border rounded-lg p-3 cursor-pointer transition-all ${
                    duplicateAction === "update_problems" 
                      ? "border-green-500 bg-green-50" 
                      : "border-slate-200 hover:border-slate-300"
                  }`}
                  onClick={() => setDuplicateAction("update_problems")}
                >
                  <div className="flex items-center gap-2">
                    <div className={`w-4 h-4 rounded-full border-2 ${duplicateAction === "update_problems" ? "border-green-500 bg-green-500" : "border-slate-300"}`}>
                      {duplicateAction === "update_problems" && <div className="w-2 h-2 bg-white rounded-full m-0.5" />}
                    </div>
                    <span className="font-medium text-green-700">Fix Problem Records Only</span>
                    <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">Recommended</span>
                  </div>
                  <p className="text-xs text-slate-500 ml-6 mt-1">
                    Update <span className="font-semibold text-orange-600">{duplicateInfo.problem_count}</span> problem records 
                    ({duplicateInfo.unassigned_count > 0 && `${duplicateInfo.unassigned_count} unassigned`}
                    {duplicateInfo.unassigned_count > 0 && duplicateInfo.error_count > 0 && " + "}
                    {duplicateInfo.error_count > 0 && `${duplicateInfo.error_count} with errors`})
                    {duplicateInfo.clean_count > 0 && <span className="text-green-600">, keep {duplicateInfo.clean_count} clean records</span>}
                  </p>
                </div>
              )}
              
              {/* Option 3: Replace All */}
              <div 
                className={`border rounded-lg p-3 cursor-pointer transition-all ${
                  duplicateAction === "replace" 
                    ? "border-red-500 bg-red-50" 
                    : "border-slate-200 hover:border-slate-300"
                }`}
                onClick={() => setDuplicateAction("replace")}
              >
                <div className="flex items-center gap-2">
                  <div className={`w-4 h-4 rounded-full border-2 ${duplicateAction === "replace" ? "border-red-500 bg-red-500" : "border-slate-300"}`}>
                    {duplicateAction === "replace" && <div className="w-2 h-2 bg-white rounded-full m-0.5" />}
                  </div>
                  <span className="font-medium text-red-700">Replace All Duplicates</span>
                  <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded">Overwrite</span>
                </div>
                <p className="text-xs text-slate-500 ml-6 mt-1">Delete ALL {duplicateInfo.count} existing records and replace with new data</p>
              </div>
            </div>
            
            {/* Sample Trackings */}
            {duplicateInfo.trackings?.length > 0 && (
              <div className="max-h-24 overflow-y-auto text-xs bg-slate-50 p-2 rounded border">
                <p className="font-medium mb-1 text-slate-600">Sample duplicate trackings:</p>
                {duplicateInfo.trackings.slice(0, 8).map((t, i) => (
                  <span key={i} className="inline-block bg-slate-200 px-1.5 py-0.5 rounded mr-1 mb-1 text-slate-700">{t}</span>
                ))}
                {duplicateInfo.trackings.length > 8 && (
                  <span className="text-slate-500">...and {duplicateInfo.trackings.length - 8} more</span>
                )}
              </div>
            )}
          </div>
          
          <DialogFooter className="gap-2 mt-4">
            <Button
              variant="outline"
              onClick={() => {
                setShowDuplicateDialog(false);
                setPendingOrders(null);
                setPendingStagingId(null);
                setDuplicateAction("skip");
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={() => handleDuplicateAction(duplicateAction)}
              className={
                duplicateAction === "skip" ? "bg-blue-600 hover:bg-blue-700 text-white" :
                duplicateAction === "update_problems" ? "bg-green-600 hover:bg-green-700 text-white" :
                "bg-red-600 hover:bg-red-700 text-white"
              }
            >
              {duplicateAction === "skip" && "Skip & Continue"}
              {duplicateAction === "update_problems" && `Fix ${duplicateInfo.problem_count} Problems`}
              {duplicateAction === "replace" && "Replace All"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Partner Ledger Duplicate Resolution Dialog */}
      <Dialog open={duplicateDialog} onOpenChange={setDuplicateDialog}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <AlertTriangle className="w-5 h-5" />
              Upload Review — {duplicateEntries.length} duplicate(s){ledgerErrorEntries.length > 0 ? `, ${ledgerErrorEntries.length} error(s)` : ""}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Toolbar — download / re-import (mirrors the Combined Sheet flow) */}
            <div className="flex flex-wrap gap-2 items-center justify-end border-b border-slate-200 pb-3">
              <Button
                variant="outline"
                size="sm"
                disabled={duplicateEntries.length === 0 && ledgerErrorEntries.length === 0}
                data-testid="ledger-upload-download-issues"
                onClick={() => {
                  const rows = [
                    ...duplicateEntries.map(e => ({ ...e, _kind: "duplicate" })),
                    ...ledgerErrorEntries.map(e => ({ ...e, _kind: e.status || "error" })),
                  ];
                  if (rows.length === 0) {
                    toast.info("Nothing to download");
                    return;
                  }
                  const headers = ["Kind", "Partner", "Tracking", "Customer", "Amount", "Date", "Status", "Notes"];
                  const lines = [headers.join(",")];
                  for (const r of rows) {
                    const cells = [
                      r._kind || "",
                      r.partner_code || "",
                      r.tracking || "",
                      (r.customer || r.customer_name || "").replace(/[",\n]/g, " "),
                      r.amount || 0,
                      r.date_display || r.date || "",
                      r.status || "",
                      String(r.narration || r.message || "").replace(/[",\n]/g, " "),
                    ];
                    lines.push(cells.map(v => `"${v}"`).join(","));
                  }
                  const blob = new Blob(["\ufeff" + lines.join("\n")], { type: "text/csv;charset=utf-8" });
                  const a = document.createElement("a");
                  a.href = URL.createObjectURL(blob);
                  a.download = `partner_ledger_issues_${Date.now()}.csv`;
                  a.click();
                  URL.revokeObjectURL(a.href);
                }}
              >
                <Download className="w-3.5 h-3.5 mr-1" />
                Download issues CSV
              </Button>
              <Button
                variant="outline"
                size="sm"
                data-testid="ledger-upload-reimport"
                onClick={() => {
                  // Close this dialog and re-trigger the file picker so the
                  // user can pick a corrected sheet without losing context.
                  setDuplicateDialog(false);
                  setPendingLedgerResult(null);
                  setDuplicateEntries([]);
                  setLedgerErrorEntries([]);
                  setLedgerDuplicateAction("skip");
                  setTimeout(() => fileInputRef.current?.click(), 100);
                }}
              >
                <Upload className="w-3.5 h-3.5 mr-1" />
                Re-import (pick fixed file)
              </Button>
            </div>

            {duplicateEntries.length > 0 && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-sm text-amber-800">
                  <strong>{duplicateEntries.length}</strong> potential duplicate entries found.
                  These entries match existing records by <b>tracking number</b>. Rows where the
                  amount has changed are highlighted — pick <i>Replace</i> to overwrite or <i>Skip</i> to keep the existing record.
                </p>
              </div>
            )}

            {ledgerErrorEntries.length > 0 && (
              <div className="border rounded-lg overflow-hidden">
                <div className="bg-rose-50 border-b border-rose-200 px-3 py-2 flex items-center justify-between">
                  <p className="text-sm font-medium text-rose-800">
                    Errors / Unmatched ({ledgerErrorEntries.length})
                  </p>
                </div>
                <div className="max-h-48 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-rose-50 sticky top-0">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-medium text-slate-600">Tracking</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-slate-600">Partner</th>
                        <th className="px-3 py-2 text-right text-xs font-medium text-slate-600">Amount</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-slate-600">Status</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-slate-600">Note</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {ledgerErrorEntries.slice(0, 20).map((e, idx) => (
                        <tr key={`err-${idx}`} className="hover:bg-rose-50/40">
                          <td className="px-3 py-2 font-mono text-xs">{e.tracking || "—"}</td>
                          <td className="px-3 py-2 text-xs">{e.partner_code || "—"}</td>
                          <td className="px-3 py-2 text-right text-xs">₹{parseFloat(e.amount || 0).toLocaleString()}</td>
                          <td className="px-3 py-2 text-xs"><span className="px-1.5 py-0.5 bg-rose-100 text-rose-700 rounded text-[10px]">{e.status || "error"}</span></td>
                          <td className="px-3 py-2 text-xs text-slate-600 truncate max-w-[260px]" title={e.narration}>{e.narration || ""}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {ledgerErrorEntries.length > 20 && (
                    <p className="px-3 py-2 text-xs text-slate-500 bg-rose-50">
                      ...and {ledgerErrorEntries.length - 20} more — download CSV to see them all
                    </p>
                  )}
                </div>
              </div>
            )}

            {duplicateEntries.length > 0 && (
            /* Duplicate entries preview */
            <div className="border rounded-lg overflow-hidden">
              <div className="bg-slate-50 px-3 py-2 border-b">
                <p className="text-sm font-medium text-slate-700">Duplicate Entries Preview</p>
              </div>
              <div className="max-h-48 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-100 sticky top-0">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-slate-600">Tracking</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-slate-600">Partner</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-slate-600">Existing → New</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-slate-600">Date</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-slate-600">Previous Upload</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {duplicateEntries.slice(0, 10).map((entry, idx) => {
                      const oldAmt = entry.existing_amount;
                      const newAmt = parseFloat(entry.amount || 0);
                      const changed = entry.amount_changed && oldAmt != null;
                      return (
                      <tr key={idx} className={changed ? "bg-amber-50/60" : "hover:bg-slate-50"}>
                        <td className="px-3 py-2 font-mono text-xs">{entry.tracking}</td>
                        <td className="px-3 py-2 text-xs">{entry.partner_code}</td>
                        <td className="px-3 py-2 text-right text-xs whitespace-nowrap">
                          {changed ? (
                            <>
                              <span className="text-slate-400 line-through">₹{parseFloat(oldAmt || 0).toLocaleString()}</span>
                              <span className="mx-1 text-amber-700">→</span>
                              <span className="font-semibold text-amber-700">₹{newAmt.toLocaleString()}</span>
                            </>
                          ) : (
                            <span>₹{newAmt.toLocaleString()}</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-xs">{entry.date_display || entry.date}</td>
                        <td className="px-3 py-2 text-xs text-slate-500 truncate max-w-[180px]" title={entry.duplicate_info?.[0]?.filename}>
                          {entry.duplicate_info?.[0]?.filename?.substring(0, 24) || "Previous upload"}
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
                {duplicateEntries.length > 10 && (
                  <p className="px-3 py-2 text-xs text-slate-500 bg-slate-50">
                    ...and {duplicateEntries.length - 10} more duplicates
                  </p>
                )}
              </div>
            </div>
            )}

            {duplicateEntries.length > 0 && (
            /* Action selection */
            <div className="space-y-3">
              <p className="text-sm font-medium text-slate-700">Choose how to handle duplicates:</p>
              
              <div 
                onClick={() => setLedgerDuplicateAction("skip")}
                className={`p-3 border rounded-lg cursor-pointer transition-all ${
                  ledgerDuplicateAction === "skip" 
                    ? "border-blue-500 bg-blue-50 ring-2 ring-blue-200" 
                    : "border-slate-200 hover:border-slate-300"
                }`}
              >
                <div className="flex items-center gap-2">
                  <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                    ledgerDuplicateAction === "skip" ? "border-blue-500" : "border-slate-300"
                  }`}>
                    {ledgerDuplicateAction === "skip" && <div className="w-2 h-2 rounded-full bg-blue-500" />}
                  </div>
                  <span className="font-medium text-slate-800">Skip Duplicates</span>
                </div>
                <p className="text-xs text-slate-600 mt-1 ml-6">
                  Only add new entries. Existing records will not be affected.
                </p>
              </div>
              
              <div 
                onClick={() => setLedgerDuplicateAction("keep_both")}
                className={`p-3 border rounded-lg cursor-pointer transition-all ${
                  ledgerDuplicateAction === "keep_both" 
                    ? "border-green-500 bg-green-50 ring-2 ring-green-200" 
                    : "border-slate-200 hover:border-slate-300"
                }`}
              >
                <div className="flex items-center gap-2">
                  <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                    ledgerDuplicateAction === "keep_both" ? "border-green-500" : "border-slate-300"
                  }`}>
                    {ledgerDuplicateAction === "keep_both" && <div className="w-2 h-2 rounded-full bg-green-500" />}
                  </div>
                  <span className="font-medium text-slate-800">Keep Both</span>
                </div>
                <p className="text-xs text-slate-600 mt-1 ml-6">
                  Add all entries including duplicates. May result in duplicate records.
                </p>
              </div>
              
              <div 
                onClick={() => setLedgerDuplicateAction("replace")}
                className={`p-3 border rounded-lg cursor-pointer transition-all ${
                  ledgerDuplicateAction === "replace" 
                    ? "border-red-500 bg-red-50 ring-2 ring-red-200" 
                    : "border-slate-200 hover:border-slate-300"
                }`}
              >
                <div className="flex items-center gap-2">
                  <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                    ledgerDuplicateAction === "replace" ? "border-red-500" : "border-slate-300"
                  }`}>
                    {ledgerDuplicateAction === "replace" && <div className="w-2 h-2 rounded-full bg-red-500" />}
                  </div>
                  <span className="font-medium text-slate-800">Replace Existing</span>
                </div>
                <p className="text-xs text-slate-600 mt-1 ml-6">
                  Replace existing records with new data. Old records will be updated.
                </p>
              </div>
            </div>
            )}

            {duplicateEntries.length === 0 && ledgerErrorEntries.length > 0 && (
              <div className="text-xs text-slate-500 italic">
                No duplicates — but there are {ledgerErrorEntries.length} unmatched/error row(s) above. Fix them in your sheet and re-import, or proceed and only the matched entries will be saved.
              </div>
            )}
          </div>
          
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setDuplicateDialog(false);
                setPendingLedgerResult(null);
                setDuplicateEntries([]);
                setLedgerErrorEntries([]);
                setLedgerDuplicateAction("skip");
              }}
            >
              Cancel Upload
            </Button>
            <Button
              onClick={() => handleDuplicateResolution(ledgerDuplicateAction)}
              className={
                duplicateEntries.length === 0 ? "bg-emerald-600 hover:bg-emerald-700" :
                ledgerDuplicateAction === "skip" ? "bg-blue-600 hover:bg-blue-700" :
                ledgerDuplicateAction === "keep_both" ? "bg-green-600 hover:bg-green-700" :
                "bg-red-600 hover:bg-red-700"
              }
            >
              {duplicateEntries.length === 0 && "Proceed with valid entries"}
              {duplicateEntries.length > 0 && ledgerDuplicateAction === "skip" && "Skip Duplicates & Continue"}
              {duplicateEntries.length > 0 && ledgerDuplicateAction === "keep_both" && "Keep All Entries"}
              {duplicateEntries.length > 0 && ledgerDuplicateAction === "replace" && "Replace Existing"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Balance Raw drill-down — parcels still awaiting payment */}
      <Dialog open={balanceRawOpen} onOpenChange={setBalanceRawOpen}>
        <DialogContent className="max-w-5xl max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-rose-700">
              <CalendarDays className="w-5 h-5" />
              Balance Raw — parcels awaiting payment
              {balanceRawContext.month && (
                <span className="text-sm font-normal text-slate-500">
                  · {["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"][balanceRawContext.month - 1]} {balanceRawContext.year}
                </span>
              )}
            </DialogTitle>
            <DialogDescription>
              Every parcel in this period scoped to the current category/name filter. Rows are sorted with{" "}
              <span className="font-semibold text-rose-700">uncovered</span> clients first (raw payment hasn't covered their costing yet) and largest-cost on top.
            </DialogDescription>
          </DialogHeader>

          {balanceRawLoading ? (
            <div className="flex-1 flex items-center justify-center py-12">
              <RefreshCw className="w-6 h-6 animate-spin text-slate-400" />
            </div>
          ) : balanceRawData ? (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="balance-raw-totals">
                <div className="rounded-md border border-slate-200 bg-white px-3 py-2">
                  <div className="text-[10px] uppercase text-slate-500">Parcels</div>
                  <div className="text-lg font-bold text-slate-800">{balanceRawData.totals?.parcels_count?.toLocaleString()}</div>
                </div>
                <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2">
                  <div className="text-[10px] uppercase text-rose-700">Uncovered parcels</div>
                  <div className="text-lg font-bold text-rose-800">{balanceRawData.totals?.uncovered_parcels?.toLocaleString()}</div>
                </div>
                <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2">
                  <div className="text-[10px] uppercase text-emerald-700">Raw payment</div>
                  <div className="text-lg font-bold text-emerald-800">₹{(balanceRawData.totals?.raw_payment || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}</div>
                </div>
                <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2">
                  <div className="text-[10px] uppercase text-rose-700">Balance raw</div>
                  <div className="text-lg font-bold text-rose-800">₹{(balanceRawData.totals?.balance_raw || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}</div>
                </div>
              </div>
              <div className="flex-1 overflow-y-auto border border-slate-200 rounded">
                <table className="w-full text-xs">
                  <thead className="bg-slate-100 sticky top-0">
                    <tr>
                      <th className="text-left p-2">Status</th>
                      <th className="text-left p-2">Date</th>
                      <th className="text-left p-2">Tracking</th>
                      <th className="text-left p-2">Client</th>
                      <th className="text-left p-2">Customer</th>
                      <th className="text-left p-2">Country</th>
                      <th className="text-right p-2">Cost</th>
                      <th className="text-right p-2">Client raw</th>
                      <th className="text-right p-2">Client balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {balanceRawData.parcels?.length === 0 ? (
                      <tr><td colSpan={9} className="text-center text-slate-500 italic py-6">No parcels in this period for the current filter.</td></tr>
                    ) : balanceRawData.parcels?.slice(0, 500).map((p, i) => (
                      <tr key={`${p.tracking}-${i}`} className={`border-b border-slate-100 ${p.client_covered ? "bg-emerald-50/30" : ""}`} data-testid={`balance-raw-row-${i}`}>
                        <td className="p-2">
                          {p.client_covered ? (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-emerald-100 text-emerald-700 border border-emerald-300">Covered</span>
                          ) : (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-rose-100 text-rose-700 border border-rose-300">Uncovered</span>
                          )}
                        </td>
                        <td className="p-2 font-mono">{p.order_date || "-"}</td>
                        <td className="p-2 font-mono truncate max-w-[140px]" title={p.tracking}>{p.tracking}</td>
                        <td className="p-2"><Badge variant="outline" className="text-[10px]">{p.client_code}</Badge></td>
                        <td className="p-2 truncate max-w-[140px]" title={p.customer_name}>{p.customer_name || "-"}</td>
                        <td className="p-2">{p.country || "-"}</td>
                        <td className="p-2 text-right font-semibold">₹{p.total_cost.toLocaleString("en-IN", { maximumFractionDigits: 2 })}</td>
                        <td className="p-2 text-right text-emerald-700">₹{p.client_raw_payment.toLocaleString("en-IN", { maximumFractionDigits: 2 })}</td>
                        <td className={`p-2 text-right font-semibold ${p.client_balance_raw > 0 ? "text-rose-700" : "text-emerald-700"}`}>
                          ₹{p.client_balance_raw.toLocaleString("en-IN", { maximumFractionDigits: 2 })}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {balanceRawData.parcels && balanceRawData.parcels.length > 500 && (
                  <div className="text-center text-[11px] text-slate-500 py-2 italic bg-slate-50 border-t">
                    Showing first 500 of {balanceRawData.parcels.length.toLocaleString()} — refine the year-summary filter to narrow the list.
                  </div>
                )}
              </div>
            </>
          ) : null}

          <DialogFooter>
            <Button
              size="sm"
              variant="outline"
              onClick={async () => {
                if (!balanceRawContext.year || !balanceRawContext.month) return;
                const params = new URLSearchParams({
                  year: String(balanceRawContext.year),
                  month: String(balanceRawContext.month),
                });
                if (yearSummaryCategory && yearSummaryCategory !== "all") params.append("category", yearSummaryCategory);
                if (yearSummaryClientCode) params.append("client_code", yearSummaryClientCode);
                try {
                  const r = await fetch(`${BACKEND_URL}/api/upload-center/year-summary/balance-raw-drilldown/export?${params}`, {
                    headers: { Authorization: `Bearer ${token}` },
                  });
                  if (!r.ok) { toast.error("Export failed"); return; }
                  const blob = await r.blob();
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = `balance_raw_${balanceRawContext.year}_${String(balanceRawContext.month).padStart(2, "0")}.xlsx`;
                  a.click();
                  URL.revokeObjectURL(url);
                  toast.success("Exported");
                } catch (e) {
                  toast.error("Export failed: " + e.message);
                }
              }}
              disabled={balanceRawLoading || !balanceRawData}
              data-testid="balance-raw-export"
            >
              <Download className="w-3.5 h-3.5 mr-1.5" />
              Export to Excel
            </Button>
            <Button variant="ghost" onClick={() => setBalanceRawOpen(false)} data-testid="balance-raw-close">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UploadCenterPage;
;
